/**
 * Empire Skirmish — original colonial RTS sim (classic Dark→Castle skirmish feel).
 * Server-authoritative. Original names/content only — no copyrighted AoE assets.
 */

import {
  EMPIRE_MAP_POOL,
  generateMapTerrain,
  getMapById,
  pickRandomMap,
  type EmpireMapDef,
} from "./empire-maps";
import { inStealthThicket, MAP_HALF, terrainTrees, TOWN_PADS, WATERLINE } from "./empire-terrain";
import { civById, type CivId, type Difficulty, type DipStance, type MissionId, type WinRule } from "./empire-rules";
import {
  findGridPath,
  fortCellsAlong,
  fortCellsAlongCorners,
  fortNeighborMask,
  isCliffFace,
  isFortKind,
  isFortWallKind,
  terrainHeight,
  FORT_LINE_MAX,
  FORT_NX,
  FORT_NZ,
  FORT_PX,
  FORT_PZ,
  type FortPoint,
} from "./empire-fort";

export {
  MAP_HALF,
  EMPIRE_MAP_POOL,
  fortCellsAlong,
  fortCellsAlongCorners,
  fortNeighborMask,
  isFortKind,
  pickRandomMap,
};
export type { EmpireMapDef };

export const EMPIRE_TEAMS = [
  "amber",
  "teal",
  "rose",
  "violet",
  "lime",
  "sky",
  "coral",
  "gold",
] as const;
export type Team = (typeof EMPIRE_TEAMS)[number];
export const MAX_EMPIRE_SEATS = 8;

export const TEAM_LABELS: Record<Team, string> = {
  amber: "Blue",
  teal: "Green",
  rose: "Red",
  violet: "Purple",
  lime: "Lime",
  sky: "Sky",
  coral: "Coral",
  gold: "Gold",
};

export function isTeam(v: string | null | undefined): v is Team {
  return !!v && (EMPIRE_TEAMS as readonly string[]).includes(v);
}

export function teamsInPlay(state: { activeTeams?: Team[] }): Team[] {
  const list = state.activeTeams?.filter(isTeam);
  return list && list.length ? list : ["amber", "teal"];
}
export type ChronoTier = "A" | "B" | "C" | "D";
export type Age = "dark" | "feudal" | "castle" | "imperial";
export type ResType = "wood" | "food" | "gold" | "stone";

export type EntityKind =
  | "town_hall"
  | "house"
  | "farm"
  | "mill"
  | "lumber_camp"
  | "mining_camp"
  | "barracks"
  | "archery_range"
  | "stable"
  | "blacksmith"
  | "university"
  | "market"
  | "dock"
  | "watch_tower"
  | "palisade"
  | "wall"
  | "fortified_wall"
  | "gate"
  | "siege_workshop"
  | "keep"
  | "church"
  | "worker"
  | "militia"
  | "spearman"
  | "man_at_arms"
  | "archer"
  | "skirmisher"
  | "crossbowman"
  | "hand_cannon"
  | "scout_cav"
  | "knight"
  | "camel_rider"
  | "war_elephant"
  | "ram"
  | "mangonel"
  | "scorpion"
  | "trebuchet"
  | "fishing_boat"
  | "transport"
  | "war_galley"
  | "wood"
  | "food"
  | "gold"
  | "stone"
  | "fish"
  | "chrono"
  | "sheep"
  | "chicken"
  | "cow"
  | "goat"
  | "duck"
  | "deer"
  | "coyote"
  | "gorilla"
  | "lion"
  | "oil_seep"
  | "gas_seep"
  | "oil_well"
  | "geologist"
  | "lighthouse"
  | "beach_kid"
  | "trade_cart"
  | "explorer"
  | "ruin"
  | "artifact";

export type BuildKind =
  | "house"
  | "farm"
  | "mill"
  | "lumber_camp"
  | "mining_camp"
  | "barracks"
  | "archery_range"
  | "stable"
  | "blacksmith"
  | "university"
  | "market"
  | "dock"
  | "watch_tower"
  | "palisade"
  | "wall"
  | "fortified_wall"
  | "gate"
  | "siege_workshop"
  | "keep"
  | "church"
  | "granary"
  | "storage_pit"
  | "wonder";

export type GateState = "closed" | "open" | "locked";

export type TrainUnit =
  | "worker"
  | "militia"
  | "spearman"
  | "man_at_arms"
  | "archer"
  | "skirmisher"
  | "crossbowman"
  | "hand_cannon"
  | "scout_cav"
  | "knight"
  | "camel_rider"
  | "war_elephant"
  | "ram"
  | "mangonel"
  | "scorpion"
  | "trebuchet"
  | "fishing_boat"
  | "transport"
  | "war_galley"
  | "geologist"
  | "healer";

export type TechId =
  | "age_feudal"
  | "age_castle"
  | "age_imperial"
  | "bs_attack"
  | "bs_armor"
  | "uni_masonry"
  | "uni_ballistics";

export type Entity = {
  id: string;
  kind: EntityKind;
  team: Team | null;
  x: number;
  z: number;
  hp: number;
  maxHp: number;
  carry?: { type: ResType; amount: number };
  order?:
    | { type: "idle" }
    | { type: "move"; tx: number; tz: number; manual?: boolean }
    | { type: "browse"; tx: number; tz: number }
    | { type: "gather"; resourceId: string }
    | { type: "return"; resourceId: string }
    | { type: "attack"; targetId: string }
    | { type: "heal"; targetId: string }
    | { type: "build"; building: BuildKind; tx: number; tz: number }
    | { type: "capture"; relicId: string }
    | { type: "attack_move"; tx: number; tz: number }
    | { type: "repair"; targetId: string }
    | { type: "convert"; targetId: string }
    | { type: "patrol"; ax: number; az: number; bx: number; bz: number; leg: 0 | 1 }
    | { type: "attack_ground"; tx: number; tz: number };
  train?: { unit: TrainUnit; left: number } | null;
  research?: { tech: TechId; left: number } | null;
  stock?: number;
  chronoTier?: ChronoTier;
  facing?: number;
  constructing?: boolean;
  attackCd?: number;
  /** Gate only: closed (default), open (passable), locked (never auto-open). */
  gateState?: GateState;
  /** Seconds until auto-close when open. */
  gateCloseIn?: number;
  /** Cached A* waypoints for current move (grid centers). */
  path?: Array<{ x: number; z: number }>;
  /** Ticks spent not getting closer to the current job. Cleared when they move again. */
  stuck?: number;
  /** Which way to slide when a step hits water or a wall. Stays put so they follow the shore. */
  slide?: 1 | -1;
  /** The click is on land this unit cannot reach (another island). Stop at the closest shore. */
  routeBlocked?: boolean;
  /** Ticks spent failing to reach the current waypoint. */
  wpStall?: number;
  /** Shift-right-click path. Walk these after the current move. */
  waypoints?: Array<{ x: number; z: number }>;
  /** Military: stand stays put and only strikes in range. Missing means they chase. */
  stance?: "aggressive" | "stand";
  /** Production building: newly trained units walk here. */
  rallyX?: number;
  rallyZ?: number;
  /** Priest rest after a conversion, in seconds. */
  convertCd?: number;
  convertProg?: number;
  /** How many times they have shuffled in place on this order. */
  loiter?: number;
  anchorX?: number;
  anchorZ?: number;
  anchorTick?: number;
  /** Cavalry charge meter. Spent on the first hit after a run. */
  charge?: number;
  stuckKey?: string;
  browseCd?: number;
  /** Land unit riding a Cannon Ferry. */
  aboardShipId?: string;
  /** Ship cargo unit ids (transport). */
  cargo?: string[];
  /** Trade cart destination market id. */
  tradeTargetId?: string;
  /** Unit is inside a town hall or tower. */
  insideId?: string;
};

export type PlayerEco = {
  wood: number;
  food: number;
  gold: number;
  stone: number;
};

export type PlayerMeta = {
  age: Age;
  pop: number;
  popCap: number;
  atkBonus: number;
  armorBonus: number;
  wallHpBonus: number;
  rangeBonus: number;
  masonry: boolean;
  ballistics: boolean;
  civ?: CivId;
};

export type ChronoCharge = {
  tier: ChronoTier;
  name: string;
  rewindSec: number;
  relicId: string;
  capturedAt: number;
};

export type GameEvent = {
  id: number;
  at: number;
  kind: "chrono_found" | "chrono_used" | "chrono_victim" | "info" | "age_up";
  team?: Team | null;
  text: string;
  rewindSec?: number;
  vaultName?: string;
};

/** Commodities traded at the Market (gold is the currency). */
export type MarketGood = "food" | "wood" | "stone";

export type MarketQuote = { buy: number; sell: number };
export type MarketPrices = Record<MarketGood, MarketQuote>;

/** Lot size for each Market trade (classic skirmish commodity block). */
export const MARKET_LOT = 100;

export const MARKET_GOODS: MarketGood[] = ["food", "wood", "stone"];

export const MARKET_GOOD_LABELS: Record<MarketGood, string> = {
  food: "Food",
  wood: "Wood",
  stone: "Stone",
};

/** Starting gold cost/proceeds per MARKET_LOT — buy is steep, sell is lean. */
export function defaultMarketPrices(): MarketPrices {
  return {
    food: { buy: 90, sell: 28 },
    wood: { buy: 90, sell: 28 },
    stone: { buy: 120, sell: 38 },
  };
}

export type GameState = {
  tick: number;
  entities: Entity[];
  /** Trees and mines. Kept off the per-tick list so the match can move. */
  props?: Entity[];
  eco: Record<Team, PlayerEco>;
  meta: Record<Team, PlayerMeta>;
  winner: Team | "draw" | "";
  started: boolean;
  practice?: boolean;
  startedAt?: number;
  /** Set once the opening peace has been announced as over. */
  peaceEnded?: boolean;
  chronoCharges: Record<Team, ChronoCharge | null>;
  events: GameEvent[];
  eventSeq: number;
  explored: Record<Team, Record<string, 1>>;
  /** Team-shared resource memory — entity ids discovered while exploring */
  knownResources?: Record<Team, Record<string, 1>>;
  /** Water cell keys "ix,iz" at WATER_CELL resolution */
  water: Record<string, 1>;
  /** Bump when lake/river generator changes so live rooms refresh water */
  waterVersion?: number;
  /** Shared commodity board — buy/sell gold for MARKET_LOT of each good */
  marketPrices?: MarketPrices;
  /** Seats present in this match (2–8). Defaults to amber+teal. */
  activeTeams?: Team[];
  /** Computer-controlled seats (friends can mid-join and claim these). */
  computerTeams?: Partial<Record<Team, true>>;
  /** Beach sand cells — scenic + kids play here */
  beaches?: Record<string, 1>;
  /** Skirmish map from the 100-map pool */
  mapId?: string;
  mapName?: string;
  mapSeed?: number;
  mapBiome?: string;
  /** Same number = allies (2v2: 0 vs 1). */
  alliances?: Partial<Record<Team, number>>;
  /** Island landing pads from map gen */
  islands?: Array<{ x: number; z: number; r: number }>;
  difficulty?: Difficulty;
  civs?: Partial<Record<Team, CivId>>;
  diplomacy?: Partial<Record<Team, Partial<Record<Team, DipStance>>>>;
  winRule?: WinRule;
  scoreTarget?: number;
  mission?: MissionId;
  shallows?: Record<string, 1>;
  wonderSince?: Partial<Record<Team, number>>;
  ruinSince?: { team: Team; at: number } | null;
};

export const PRACTICE_SEATS = 4;
export const TRANSPORT_CAPACITY = 8;
export const TRADE_GOLD_PER_TRIP = 25;


export const WATER_GEN_VERSION = 6;

export const TICK_MS = 200;
/** No team, tower, or animal may damage another until this long after the match starts. */
export const PEACE_MS = 25 * 60 * 1000;

export function attacksAllowed(state: GameState, now = Date.now()): boolean {
  if (!state.startedAt) return false;
  return now - state.startedAt >= PEACE_MS;
}

export function isWarrior(kind: string): boolean {
  if (kind === "worker" || kind === "explorer" || kind === "healer" || kind === "geologist") return false;
  if (kind === "fishing_boat" || kind === "transport" || kind === "trade_cart") return false;
  return LAND_UNITS.has(kind as EntityKind) || kind === "war_galley";
}
export const WATER_CELL = 2;
export const FOG_CELL = 2;
export const ATTACK_RANGE_MELEE = 1.7;
/** Reach from site center to a ring stand — wide enough for 4-side slots. */
export const GATHER_RANGE = 2.2;
export const RETURN_RANGE = 2.8;
export const BUILD_RANGE = 2.6;
export const CAPTURE_RANGE = 1.5;
export const VISION_UNIT = 5.5;
export const VISION_BUILDING = 8;
export const VISION_TOWER = 10;
export const REWIND_GRACE_SEC = 60;
export const SNAPSHOT_INTERVAL_MS = 12_000;
export const SNAPSHOT_KEEP_MS = 5 * 60 * 1000 + 30_000;
export const BASE_POP_CAP = 200;
export const HOUSE_POP = 5;
export const KEEP_POP = 10;
export const CARRY_MAX = 10;

/** Farm food capacity by age — farms refill forever and scale with age. */
export const FARM_STOCK_CAP: Record<Age, number> = {
  dark: 250,
  feudal: 350,
  castle: 500,
  imperial: 700,
};
/** Food restored to a farm every regen tick (keeps farms producing forever). */
export const FARM_REGEN = 4;
export const FARM_REGEN_EVERY = 6;

/** Passive livestock / wildlife — workers gather for food. */
export const FOOD_ANIMALS = [
  "sheep",
  "chicken",
  "cow",
  "goat",
  "duck",
  "deer",
] as const;
export type FoodAnimal = (typeof FOOD_ANIMALS)[number];

/** Predators that hunt villagers. */
export const PREDATORS = ["coyote", "gorilla", "lion"] as const;
export type Predator = (typeof PREDATORS)[number];

export const FOOD_ANIMAL_SET = new Set<string>(FOOD_ANIMALS);
export const PREDATOR_SET = new Set<string>(PREDATORS);

export function isFoodAnimal(k: string): boolean {
  return FOOD_ANIMAL_SET.has(k);
}
export function isPredator(k: string): boolean {
  return PREDATOR_SET.has(k);
}

const ANIMAL_HP: Record<string, number> = {
  sheep: 28,
  chicken: 10,
  cow: 55,
  goat: 24,
  duck: 12,
  deer: 32,
  coyote: 48,
  gorilla: 110,
  lion: 140,
};
const ANIMAL_FOOD_STOCK: Record<string, number> = {
  sheep: 100,
  chicken: 40,
  cow: 140,
  goat: 90,
  duck: 45,
  deer: 110,
};
const PREDATOR_DMG: Record<string, number> = { coyote: 7, gorilla: 16, lion: 18 };
const PREDATOR_RANGE = 1.65;
/** How far predators notice villagers in the wilderness. */
const PREDATOR_AGGRO = 6.5;
/**
 * Town-hall bubble (matches BASE_LAND_RADIUS + 6): predators never hunt or linger
 * this close to a TC, so plaza wood/food lines stay safe.
 */
const PREDATOR_BASE_SAFE = 16;
/** Extra clearance when placing predators at map gen. */
const PREDATOR_SPAWN_CLEAR = 20;


export const CHRONO_DEFS: Record<
  ChronoTier,
  { name: string; rewindSec: number; weight: number }
> = {
  A: { name: "Epoch Spindle", rewindSec: 5 * 60, weight: 1 },
  B: { name: "Hourglass Reliquary", rewindSec: 3 * 60, weight: 2 },
  C: { name: "Sundial Core", rewindSec: 2 * 60, weight: 3 },
  D: { name: "Minute Shard", rewindSec: 1 * 60, weight: 5 },
};

type Cost = Partial<Record<ResType, number>>;

export const COST: Record<string, Cost> = {
  house: { wood: 50 },
  farm: { wood: 60 },
  mill: { wood: 100 },
  lumber_camp: { wood: 100 },
  mining_camp: { wood: 100 },
  barracks: { wood: 175 },
  archery_range: { wood: 175 },
  stable: { wood: 175 },
  blacksmith: { wood: 150 },
  university: { wood: 200, stone: 100 },
  market: { wood: 175 },
  dock: { wood: 150 },
  watch_tower: { wood: 50, stone: 125 },
  palisade: { wood: 3 },
  wall: { stone: 5 },
  fortified_wall: { stone: 8, wood: 2 },
  gate: { stone: 30 },
  siege_workshop: { wood: 200 },
  keep: { wood: 350, stone: 250 },
  church: { wood: 150 },
  granary: { wood: 120 },
  storage_pit: { wood: 120 },
  wonder: { wood: 1000, gold: 800, stone: 800 },
  healer: { food: 40, gold: 25 },
  worker: { food: 50 },
  militia: { food: 60, gold: 20 },
  spearman: { food: 35, wood: 25 },
  man_at_arms: { food: 65, gold: 40 },
  archer: { wood: 25, gold: 45 },
  skirmisher: { food: 25, wood: 35 },
  crossbowman: { food: 40, gold: 50 },
  hand_cannon: { food: 50, gold: 70 },
  scout_cav: { food: 80 },
  knight: { food: 60, gold: 75 },
  camel_rider: { food: 55, gold: 50 },
  war_elephant: { food: 140, gold: 70 },
  ram: { wood: 160, gold: 75 },
  mangonel: { wood: 160, gold: 135 },
  scorpion: { wood: 140, gold: 110 },
  trebuchet: { wood: 200, gold: 180 },
  fishing_boat: { wood: 75 },
  transport: { wood: 125 },
  war_galley: { wood: 90, gold: 30 },
  geologist: { food: 60, gold: 40 },
  age_feudal: { food: 500 },
  age_castle: { food: 800, gold: 200 },
  age_imperial: { food: 1000, gold: 800 },
  bs_attack: { food: 100, gold: 50 },
  bs_armor: { food: 100, gold: 50 },
  uni_masonry: { food: 150, wood: 175 },
  uni_ballistics: { wood: 200, gold: 100 },
};

export const TRAIN_TICKS: Record<TrainUnit, number> = {
  worker: 25,
  militia: 28,
  spearman: 26,
  man_at_arms: 36,
  archer: 30,
  skirmisher: 28,
  crossbowman: 34,
  hand_cannon: 42,
  scout_cav: 32,
  knight: 40,
  camel_rider: 38,
  war_elephant: 48,
  ram: 45,
  mangonel: 50,
  scorpion: 48,
  trebuchet: 60,
  fishing_boat: 35,
  transport: 40,
  war_galley: 42,
  geologist: 32,
  healer: 28,
};

export const RESEARCH_TICKS: Record<TechId, number> = {
  age_feudal: 60,
  age_castle: 80,
  age_imperial: 100,
  bs_attack: 40,
  bs_armor: 40,
  uni_masonry: 45,
  uni_ballistics: 50,
};

export const BUILD_HP: Record<BuildKind | "town_hall", number> = {
  town_hall: 1200,
  house: 200,
  farm: 150,
  mill: 400,
  lumber_camp: 400,
  mining_camp: 400,
  barracks: 600,
  archery_range: 600,
  stable: 600,
  blacksmith: 500,
  university: 500,
  market: 450,
  dock: 500,
  watch_tower: 500,
  palisade: 120,
  wall: 300,
  fortified_wall: 550,
  gate: 450,
  siege_workshop: 550,
  keep: 2000,
  church: 700,
  granary: 450,
  storage_pit: 450,
  wonder: 2400,
};

export const UNIT_STATS: Record<
  TrainUnit,
  {
    hp: number;
    dmg: number;
    range: number;
    speed: number;
    armor: number;
    naval?: boolean;
    siege?: boolean;
    vsCav?: number;
    vsArcher?: number;
    vsInf?: number;
    splash?: number;
    pierce?: boolean;
  }
> = {
  worker: { hp: 55, dmg: 3, range: 1.5, speed: 2.4, armor: 0 },
  militia: { hp: 90, dmg: 10, range: 1.6, speed: 3.0, armor: 0 },
  spearman: { hp: 75, dmg: 8, range: 1.6, speed: 3.0, armor: 0, vsCav: 14 },
  man_at_arms: { hp: 120, dmg: 13, range: 1.6, speed: 2.9, armor: 2, vsInf: 4 },
  archer: { hp: 50, dmg: 7, range: 6.5, speed: 2.8, armor: 0 },
  skirmisher: { hp: 45, dmg: 5, range: 5.5, speed: 2.9, armor: 0, vsArcher: 8 },
  crossbowman: { hp: 55, dmg: 9, range: 7.0, speed: 2.7, armor: 0, vsInf: 5 },
  hand_cannon: { hp: 60, dmg: 16, range: 6.0, speed: 2.5, armor: 0, vsInf: 8 },
  scout_cav: { hp: 85, dmg: 7, range: 1.6, speed: 4.4, armor: 0 },
  knight: { hp: 140, dmg: 14, range: 1.6, speed: 3.8, armor: 2 },
  camel_rider: { hp: 110, dmg: 10, range: 1.6, speed: 3.9, armor: 1, vsCav: 16 },
  war_elephant: { hp: 260, dmg: 18, range: 1.8, speed: 2.3, armor: 2, vsInf: 6 },
  ram: { hp: 220, dmg: 28, range: 1.8, speed: 1.6, armor: 4, siege: true },
  mangonel: { hp: 100, dmg: 22, range: 7.5, speed: 1.8, armor: 1, siege: true, splash: 2.4 },
  scorpion: { hp: 90, dmg: 18, range: 8.0, speed: 1.9, armor: 1, siege: true, pierce: true },
  trebuchet: { hp: 130, dmg: 45, range: 12, speed: 1.1, armor: 1, siege: true },
  fishing_boat: { hp: 70, dmg: 0, range: 0, speed: 3.2, armor: 0, naval: true },
  transport: { hp: 280, dmg: 10, range: 5.5, speed: 2.7, armor: 2, naval: true },
  war_galley: { hp: 120, dmg: 12, range: 5.5, speed: 3.4, armor: 1, naval: true },
  geologist: { hp: 50, dmg: 2, range: 1.4, speed: 2.5, armor: 0 },
  healer: { hp: 50, dmg: 0, range: 1.7, speed: 2.6, armor: 0 },
};

export const AGE_RANK: Record<Age, number> = { dark: 0, feudal: 1, castle: 2, imperial: 3 };

export const BUILD_AGE: Record<BuildKind, Age> = {
  house: "dark",
  farm: "dark",
  mill: "dark",
  lumber_camp: "dark",
  mining_camp: "dark",
  barracks: "dark",
  dock: "dark",
  palisade: "dark",
  gate: "dark",
  wall: "feudal",
  archery_range: "feudal",
  stable: "feudal",
  blacksmith: "feudal",
  market: "feudal",
  watch_tower: "feudal",
  fortified_wall: "castle",
  university: "castle",
  siege_workshop: "castle",
  keep: "castle",
  church: "dark",
  granary: "dark",
  storage_pit: "dark",
  wonder: "imperial",
};

export const UNIT_AGE: Record<TrainUnit, Age> = {
  worker: "dark",
  militia: "dark",
  spearman: "feudal",
  fishing_boat: "dark",
  archer: "feudal",
  skirmisher: "feudal",
  scout_cav: "feudal",
  transport: "feudal",
  war_galley: "feudal",
  man_at_arms: "castle",
  crossbowman: "castle",
  camel_rider: "castle",
  war_elephant: "feudal",
  knight: "castle",
  ram: "castle",
  mangonel: "castle",
  scorpion: "castle",
  hand_cannon: "imperial",
  trebuchet: "imperial",
  geologist: "castle",
  healer: "dark",
};

export const BUILD_LABELS: Record<BuildKind, string> = {
  house: "House",
  farm: "Farm",
  mill: "Mill",
  lumber_camp: "Lumber Camp",
  mining_camp: "Mining Camp",
  barracks: "Barracks",
  archery_range: "Archery Range",
  stable: "Stable",
  blacksmith: "Blacksmith",
  university: "University",
  market: "Market",
  dock: "Dock",
  watch_tower: "Watch Tower",
  palisade: "Palisade",
  wall: "Stone Wall",
  fortified_wall: "Fortified Wall",
  gate: "Gatehouse",
  siege_workshop: "Siege Workshop",
  keep: "Keep",
  church: "Church",
  granary: "Granary",
  storage_pit: "Storage Pit",
  wonder: "Wonder",
};

export const UNIT_LABELS: Record<TrainUnit, string> = {
  worker: "Villager",
  militia: "Militia",
  spearman: "Spearman",
  man_at_arms: "Man-at-Arms",
  archer: "Archer",
  skirmisher: "Skirmisher",
  crossbowman: "Crossbowman",
  hand_cannon: "Hand Cannon",
  scout_cav: "Scout Cavalry",
  knight: "Knight",
  camel_rider: "Camel Rider",
  war_elephant: "War Elephant",
  ram: "Battering Ram",
  mangonel: "Mangonel",
  scorpion: "Scorpion",
  trebuchet: "Trebuchet",
  fishing_boat: "Fishing Boat",
  transport: "Cannon Ferry",
  war_galley: "War Galley",
  geologist: "Geologist",
  healer: "Healer",
};

export const AGE_LABELS: Record<Age, string> = {
  dark: "Dark Age",
  feudal: "Feudal Age",
  castle: "Castle Age",
  imperial: "Imperial Age",
};

const BUILDING_KINDS = new Set<EntityKind>([
  "town_hall",
  "house",
  "farm",
  "mill",
  "lumber_camp",
  "mining_camp",
  "barracks",
  "archery_range",
  "stable",
  "blacksmith",
  "university",
  "market",
  "dock",
  "watch_tower",
  "palisade",
  "wall",
  "fortified_wall",
  "gate",
  "siege_workshop",
  "keep",
  "church",
  "granary",
  "storage_pit",
  "wonder",
  "ruin",
]);

const LAND_UNITS = new Set<EntityKind>([
  "worker",
  "militia",
  "spearman",
  "man_at_arms",
  "archer",
  "skirmisher",
  "crossbowman",
  "hand_cannon",
  "scout_cav",
  "knight",
  "camel_rider",
  "war_elephant",
  "ram",
  "mangonel",
  "scorpion",
  "trebuchet",
  "geologist",
  "healer",
  "explorer",
]);

const NAVAL_UNITS = new Set<EntityKind>([
  "fishing_boat",
  "transport",
  "war_galley",
]);

const DROP_SITES = new Set<EntityKind>([
  "town_hall",
  "mill",
  "lumber_camp",
  "mining_camp",
  "dock",
  "granary",
  "storage_pit",
]);

let _seq = 0;
function nid(prefix: string): string {
  _seq += 1;
  return `${prefix}_${_seq}_${Math.floor(Math.random() * 1e6).toString(36)}`;
}

export function dist(a: { x: number; z: number }, b: { x: number; z: number }): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

function clampMap(v: number): number {
  return Math.max(-MAP_HALF, Math.min(MAP_HALF, v));
}

export function cellKey(x: number, z: number, cell = FOG_CELL): string {
  const ix = Math.floor((x + MAP_HALF) / cell);
  const iz = Math.floor((z + MAP_HALF) / cell);
  return `${ix},${iz}`;
}

export function waterKey(x: number, z: number): string {
  return cellKey(x, z, WATER_CELL);
}

export function isWaterAt(state: GameState, x: number, z: number): boolean {
  return !!state.water?.[waterKey(x, z)];
}

function isShallowAt(state: GameState, x: number, z: number): boolean {
  return !!state.shallows?.[waterKey(x, z)];
}

function markShallows(state: GameState) {
  const shallows: Record<string, 1> = {};
  for (const key of Object.keys(state.water || {})) {
    const [ix, iz] = key.split(",").map(Number);
    const x = ix * WATER_CELL - MAP_HALF + WATER_CELL * 0.5;
    const z = iz * WATER_CELL - MAP_HALF + WATER_CELL * 0.5;
    const n = WATER_CELL;
    const dry =
      !state.water[waterKey(x + n, z)] ||
      !state.water[waterKey(x - n, z)] ||
      !state.water[waterKey(x, z + n)] ||
      !state.water[waterKey(x, z - n)];
    if (dry) shallows[key] = 1;
  }
  state.shallows = shallows;
}

function stanceToward(state: GameState, from: Team, to: Team): DipStance {
  const set = state.diplomacy?.[from]?.[to];
  if (set) return set;
  return areAllied(state, from, to) ? "ally" : "enemy";
}

function hostileTarget(state: GameState, from: Team | null, target: Entity): boolean {
  if (!from || !target.team || from === target.team) return false;
  if (target.kind === "explorer") return false;
  const stance = stanceToward(state, from, target.team);
  if (stance === "ally") return false;
  if (stance === "neutral") {
    return target.kind !== "worker" && target.kind !== "fishing_boat" && target.kind !== "healer";
  }
  return true;
}

function teamCiv(state: GameState, team: Team | null | undefined) {
  if (!team) return civById(undefined);
  return civById(state.civs?.[team] || state.meta?.[team]?.civ);
}

function defaultMeta(): PlayerMeta {
  return {
    age: "dark",
    pop: 0,
    popCap: BASE_POP_CAP,
    atkBonus: 0,
    armorBonus: 0,
    wallHpBonus: 0,
    rangeBonus: 0,
    masonry: false,
    ballistics: false,
  };
}

function defaultEco(): PlayerEco {
  return { wood: 200, food: 200, gold: 100, stone: 100 };
}

function clampMarketQuote(q: MarketQuote): MarketQuote {
  return {
    buy: Math.max(40, Math.min(280, Math.round(q.buy))),
    sell: Math.max(8, Math.min(160, Math.round(q.sell))),
  };
}

function ensureMarketPrices(state: GameState) {
  const base = defaultMarketPrices();
  if (!state.marketPrices) {
    state.marketPrices = base;
    return;
  }
  const next = { ...base };
  for (const g of MARKET_GOODS) {
    const cur = state.marketPrices[g] || base[g];
    const buy = Number(cur.buy) || base[g].buy;
    const sell = Number(cur.sell) || base[g].sell;
    const q = clampMarketQuote({ buy, sell });
    // Sell must stay below buy (spread)
    if (q.sell >= q.buy) q.sell = Math.max(8, q.buy - 12);
    next[g] = q;
  }
  state.marketPrices = next;
}

/** After a buy, prices climb; after a sell, they ease — AoE-style commodity shift. */
function shiftMarketPrices(state: GameState, good: MarketGood, side: "buy" | "sell") {
  ensureMarketPrices(state);
  const q = { ...state.marketPrices![good] };
  if (side === "buy") {
    q.buy += 3;
    q.sell += 1;
  } else {
    q.buy = Math.max(40, q.buy - 1);
    q.sell = Math.max(8, q.sell - 2);
  }
  const clamped = clampMarketQuote(q);
  if (clamped.sell >= clamped.buy) clamped.sell = Math.max(8, clamped.buy - 12);
  state.marketPrices![good] = clamped;
}

function teamHasMarket(state: GameState, team: Team): boolean {
  return state.entities.some(
    (e) => e.kind === "market" && e.team === team && e.hp > 0 && !e.constructing
  );
}

function ensureMeta(state: GameState) {
  if (!state.meta) {
    state.meta = Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, defaultMeta()])) as Record<Team, PlayerMeta>;
  }
  for (const t of EMPIRE_TEAMS as unknown as Team[]) {
    const m = state.meta[t] || defaultMeta();
    state.meta[t] = {
      ...defaultMeta(),
      ...m,
      age: (m.age as Age) || "dark",
    };
  }
  for (const t of EMPIRE_TEAMS as unknown as Team[]) {
    const e = state.eco[t] || defaultEco();
    state.eco[t] = {
      wood: e.wood ?? 0,
      food: e.food ?? 0,
      gold: e.gold ?? 0,
      stone: e.stone ?? 0,
    };
  }
  if (!state.water) state.water = {};
  if (!state.knownResources) state.knownResources = Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, {}])) as Record<Team, Record<string, 1>>;
  for (const t of EMPIRE_TEAMS as unknown as Team[]) {
    if (!state.knownResources[t]) state.knownResources[t] = {};
  }
  ensureMarketPrices(state);
}

export function isBuildingKind(k: string): boolean {
  return BUILDING_KINDS.has(k as EntityKind);
}

export function isLandUnit(k: string): boolean {
  return LAND_UNITS.has(k as EntityKind);
}

export function isNavalUnit(k: string): boolean {
  return NAVAL_UNITS.has(k as EntityKind);
}

export function isControllableUnit(k: string): boolean {
  return isLandUnit(k) || isNavalUnit(k);
}

function markVision(state: GameState, team: Team, x: number, z: number, radius: number) {
  if (!state.explored) state.explored = Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, {}])) as Record<Team, Record<string, 1>>;
  if (!state.explored[team]) state.explored[team] = {};
  const r2 = radius * radius;
  const step = FOG_CELL;
  for (let ox = -radius; ox <= radius; ox += step) {
    for (let oz = -radius; oz <= radius; oz += step) {
      if (ox * ox + oz * oz > r2) continue;
      state.explored[team][cellKey(x + ox, z + oz)] = 1;
    }
  }
}

function visionRadius(e: Entity): number {
  if (e.kind === "explorer") return 16;
  if (e.kind === "watch_tower" || e.kind === "keep") return VISION_TOWER;
  if (isBuildingKind(e.kind)) return VISION_BUILDING;
  return VISION_UNIT;
}

export function refreshFog(state: GameState) {
  ensureMeta(state);
  if (!state.explored) state.explored = Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, {}])) as Record<Team, Record<string, 1>>;
  const teams = state.activeTeams?.length ? state.activeTeams : (EMPIRE_TEAMS as unknown as Team[]);
  for (const team of teams) {
    for (const e of state.entities) {
      if (e.hp <= 0 || e.team !== team) continue;
      markVision(state, team, e.x, e.z, visionRadius(e));
    }
  }
  if ((state.tick || 0) > 0) discoverVisibleResources(state);
}

export function isExplored(state: GameState, team: Team, x: number, z: number): boolean {
  return !!state.explored?.[team]?.[cellKey(x, z)];
}

export function isVisibleNow(state: GameState, team: Team, x: number, z: number): boolean {
  for (const e of state.entities) {
    if (e.hp <= 0 || e.team !== team) continue;
    if (dist(e, { x, z }) <= visionRadius(e)) return true;
  }
  return false;
}

function pushEvent(
  state: GameState,
  ev: Omit<GameEvent, "id" | "at"> & { at?: number }
) {
  state.eventSeq = (state.eventSeq || 0) + 1;
  state.events = (state.events || []).slice(-40);
  state.events.push({
    id: state.eventSeq,
    at: ev.at ?? Date.now(),
    kind: ev.kind,
    team: ev.team,
    text: ev.text,
    rewindSec: ev.rewindSec,
    vaultName: ev.vaultName,
  });
}

function pickChronoTier(): ChronoTier {
  const entries = Object.entries(CHRONO_DEFS) as Array<
    [ChronoTier, (typeof CHRONO_DEFS)[ChronoTier]]
  >;
  const total = entries.reduce((s, [, d]) => s + d.weight, 0);
  let r = Math.random() * total;
  for (const [tier, d] of entries) {
    r -= d.weight;
    if (r <= 0) return tier;
  }
  return "D";
}

/** Town Hall pads. Four players take the four corners. Extra players take the edge middles. */
export const BASE_SPAWNS: ReadonlyArray<{ x: number; z: number }> = TOWN_PADS;

/** Which pads a match uses. Two players sit in opposite corners. */
function seatSlots(count: number): number[] {
  if (count <= 2) return [0, 2];
  if (count === 3) return [0, 1, 2];
  if (count === 4) return [0, 1, 2, 3];
  if (count === 5) return [0, 1, 2, 3, 4];
  if (count === 6) return [0, 1, 2, 3, 4, 6];
  if (count === 7) return [0, 1, 2, 3, 4, 5, 6];
  return [0, 1, 2, 3, 4, 5, 6, 7];
}

export function seatBases(count: number): Array<{ team: Team; x: number; z: number; face: number }> {
  const n = Math.max(2, Math.min(MAX_EMPIRE_SEATS, count | 0));
  return seatSlots(n).map((slot, i) => {
    const sp = BASE_SPAWNS[slot];
    const a = Math.atan2(sp.z, sp.x);
    return {
      team: EMPIRE_TEAMS[i],
      x: sp.x,
      z: sp.z,
      face: a + Math.PI,
    };
  });
}

/** Land radius around each TC (no water). */
export const BASE_LAND_RADIUS = 10;

/**
 * Erase water around Town Hall pads and cut a dry corridor toward map center
 * so workers can leave the "first block".
 */
export function carveBaseLand(water: Record<string, 1>): Record<string, 1> {
  const eraseDisk = (cx: number, cz: number, r: number) => {
    for (let x = cx - r; x <= cx + r; x += WATER_CELL * 0.5) {
      for (let z = cz - r; z <= cz + r; z += WATER_CELL * 0.5) {
        if ((x - cx) ** 2 + (z - cz) ** 2 <= r * r) {
          delete water[waterKey(x, z)];
        }
      }
    }
  };
  for (const b of BASE_SPAWNS) {
    eraseDisk(b.x, b.z, BASE_LAND_RADIUS);
    // Short dry ramp out of the pad — stop before mid-map so lakes stay intact
    const steps = 8;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      // Only walk ~45% toward center (leave central lake / river)
      const x = b.x * (1 - t * 0.45);
      const z = b.z * (1 - t * 0.45);
      eraseDisk(x, z, 2.8);
    }
  }
  return water;
}

/** Generate natural lakes + a meandering river — never flooding spawn pads. */
export function generateWater(): Record<string, 1> {
  const water: Record<string, 1> = {};
  const step = WATER_CELL * 0.35;
  const mark = (x: number, z: number) => {
    if (Math.abs(x) > MAP_HALF || Math.abs(z) > MAP_HALF) return;
    water[waterKey(x, z)] = 1;
  };
  /** Soft oval lake (rx/rz axes, optional rotation). */
  const fillLake = (
    cx: number,
    cz: number,
    rx: number,
    rz: number,
    rot = 0,
    shoreNoise = 0.55
  ) => {
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const pad = Math.max(rx, rz) + shoreNoise + 1;
    for (let x = cx - pad; x <= cx + pad; x += step) {
      for (let z = cz - pad; z <= cz + pad; z += step) {
        const dx = x - cx;
        const dz = z - cz;
        const lx = dx * cos + dz * sin;
        const lz = -dx * sin + dz * cos;
        // gentle shoreline wobble so it doesn't look like a perfect oval stamp
        const n =
          1 +
          shoreNoise *
            0.12 *
            (Math.sin(lx * 0.55 + cz * 0.1) + Math.cos(lz * 0.7 - cx * 0.08));
        const nx = rx * n;
        const nz = rz * n;
        if ((lx * lx) / (nx * nx) + (lz * lz) / (nz * nz) <= 1) mark(x, z);
      }
    }
  };
  const fillDisk = (cx: number, cz: number, r: number) =>
    fillLake(cx, cz, r, r * 0.92, 0.2, 0.4);

  // Main central lake — wide oval, AoE-style mid feature
  fillLake(0, 2, 9.5, 6.8, 0.35, 0.8);
  fillLake(-2, -1, 4.2, 3.4, -0.4, 0.5); // connected cove

  // Far NE coastal bay (away from teal TC 20,20)
  fillLake(27, 6, 6.5, 4.2, 0.5, 0.7);
  fillLake(30, -1, 4.0, 3.2, -0.2, 0.5);

  // Far SW lake (away from amber TC -20,-20)
  fillLake(-27, -6, 6.2, 4.0, -0.45, 0.7);
  fillLake(-30, 1, 3.8, 3.0, 0.3, 0.5);

  // Meandering river NW → SE (varies width, bends around mid lake)
  for (let t = -30; t <= 30; t += 0.45) {
    const bend = Math.sin(t * 0.18) * 5.5 + Math.sin(t * 0.07) * 2.2;
    const x = t * 0.52 + bend * 0.35;
    const z = t * 0.72 + Math.cos(t * 0.15) * 3.8;
    // Widen near mid, narrow near ends; bridge gap around lake with thinner channel
    const nearLake = Math.hypot(x, z) < 11;
    const halfW = nearLake ? 1.35 : 1.9 + 0.55 * Math.sin(t * 0.3 + 1.2);
    fillDisk(x, z, halfW);
  }

  // Side ponds for fishing / navy flavor (not on spawn pads)
  fillLake(-8, 16, 3.6, 2.8, 0.6, 0.45);
  fillLake(8, -16, 3.4, 2.6, -0.5, 0.45);
  fillLake(14, 8, 2.6, 2.2, 0.9, 0.35);

  return carveBaseLand(water);
}

function nearBaseClear(x: number, z: number): boolean {
  // Keep spawn plazas dry / lightly forested
  for (const b of BASE_SPAWNS) {
    if (dist({ x, z }, b) < BASE_LAND_RADIUS) return true;
  }
  return false;
}

/** Predator spawn exclusion around Town Halls already placed on this map. */
function nearPlacedTownHall(
  entities: Entity[],
  x: number,
  z: number,
  radius: number
): boolean {
  for (const e of entities) {
    if (e.kind !== "town_hall") continue;
    if (dist({ x, z }, e) < radius) return true;
  }
  return false;
}

/** True if (x,z) sits inside any living Town Hall's eco-safe bubble. */
function nearLivingTownHall(
  state: GameState,
  x: number,
  z: number,
  radius = PREDATOR_BASE_SAFE
): boolean {
  for (const e of state.entities) {
    if (e.kind !== "town_hall" || e.hp <= 0 || !e.team) continue;
    if (dist({ x, z }, e) < radius) return true;
  }
  return false;
}

function spawnForestClump(
  entities: Entity[],
  cx: number,
  cz: number,
  count: number,
  spread: number,
  water: Record<string, 1>
) {
  for (let i = 0; i < count; i++) {
    const ang = Math.random() * Math.PI * 2;
    const r = Math.random() * spread;
    const x = clampMap(cx + Math.cos(ang) * r);
    const z = clampMap(cz + Math.sin(ang) * r);
    if (water[waterKey(x, z)]) continue;
    if (nearBaseClear(x, z) && Math.random() > 0.35) continue;
    entities.push({
      id: nid("wd"),
      kind: "wood",
      team: null,
      x,
      z,
      hp: 1,
      maxHp: 1,
      stock: 180 + Math.floor(Math.random() * 120),
    });
  }
}

/** Every painted pine becomes a wood pile, so a villager can chop it down. */
function spawnVisibleTrees(
  entities: Entity[],
  seed: number,
  biome: string,
  water: Record<string, 1>
) {
  const woods = entities.filter((e) => e.kind === "wood");
  const taken = new Set(woods.map((e) => `${Math.round(e.x / 2)},${Math.round(e.z / 2)}`));
  for (const t of terrainTrees(seed, biome)) {
    if (Math.abs(t.x) > MAP_HALF - 1 || Math.abs(t.z) > MAP_HALF - 1) continue;
    if (water[waterKey(t.x, t.z)]) continue;
    const spot = `${Math.round(t.x / 2)},${Math.round(t.z / 2)}`;
    if (taken.has(spot)) continue;
    taken.add(spot);
    const tree: Entity = {
      id: nid("wd"),
      kind: "wood",
      team: null,
      x: t.x,
      z: t.z,
      hp: 1,
      maxHp: 1,
      stock: 120 + Math.floor(Math.random() * 80),
    };
    entities.push(tree);
    woods.push(tree);
  }
}

function spotTaken(taken: Set<string>, x: number, z: number, r: number): boolean {
  const ix = Math.round(x);
  const iz = Math.round(z);
  for (let dx = -r; dx <= r; dx++) {
    for (let dz = -r; dz <= r; dz++) {
      if (dx * dx + dz * dz > r * r) continue;
      if (taken.has(`${ix + dx},${iz + dz}`)) return true;
    }
  }
  return false;
}

function spawnBush(
  entities: Entity[],
  x: number,
  z: number,
  stock: number,
  water: Record<string, 1>
) {
  if (water[waterKey(x, z)]) return;
  entities.push({
    id: nid("fd"),
    kind: "food",
    team: null,
    x: clampMap(x),
    z: clampMap(z),
    hp: 1,
    maxHp: 1,
    stock,
  });
}

function spawnMine(
  entities: Entity[],
  kind: "gold" | "stone",
  x: number,
  z: number,
  stock: number,
  water: Record<string, 1>
) {
  if (water[waterKey(x, z)]) return;
  entities.push({
    id: nid(kind === "gold" ? "au" : "st"),
    kind,
    team: null,
    x: clampMap(x),
    z: clampMap(z),
    hp: 1,
    maxHp: 1,
    stock,
  });
}

function spawnFish(entities: Entity[], water: Record<string, 1>) {
  const keys = Object.keys(water);
  let n = 0;
  for (let i = 0; i < keys.length && n < 28; i += Math.max(1, Math.floor(keys.length / 40))) {
    const [ix, iz] = keys[i].split(",").map(Number);
    const x = ix * WATER_CELL - MAP_HALF + WATER_CELL * 0.5;
    const z = iz * WATER_CELL - MAP_HALF + WATER_CELL * 0.5;
    if (Math.random() > 0.55) continue;
    entities.push({
      id: nid("fi"),
      kind: "fish",
      team: null,
      x: clampMap(x + (Math.random() - 0.5)),
      z: clampMap(z + (Math.random() - 0.5)),
      hp: 1,
      maxHp: 1,
      stock: 220 + Math.floor(Math.random() * 100),
    });
    n++;
  }
}

function spawnChronoRelics(entities: Entity[], water: Record<string, 1>) {
  const spots = [
    { x: -4, z: 16 },
    { x: 4, z: -16 },
    { x: -18, z: 4 },
    { x: 18, z: -4 },
    { x: -12, z: 12 },
    { x: 12, z: -12 },
    { x: 0, z: -22 },
    { x: 0, z: 22 },
  ];
  for (let i = spots.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [spots[i], spots[j]] = [spots[j], spots[i]];
  }
  let placed = 0;
  for (const s of spots) {
    if (placed >= 4) break;
    if (water[waterKey(s.x, s.z)]) continue;
    const tier = pickChronoTier();
    entities.push({
      id: nid("ch"),
      kind: "chrono",
      team: null,
      x: s.x + (Math.random() - 0.5) * 1.2,
      z: s.z + (Math.random() - 0.5) * 1.2,
      hp: 1,
      maxHp: 1,
      chronoTier: tier,
    });
    placed++;
  }
}



function shoreCells(water: Record<string, 1>): Array<{ x: number; z: number }> {
  const out: Array<{ x: number; z: number }> = [];
  const seen = new Set<string>();
  const keys = Object.keys(water);
  const stride = Math.max(1, Math.floor(keys.length / 60));
  for (let i = 0; i < keys.length && out.length < 70; i += stride) {
    const key = keys[i];
    const [ix, iz] = key.split(",").map(Number);
    const x = ix * WATER_CELL;
    const z = iz * WATER_CELL;
    for (const [dx, dz] of [
      [WATER_CELL, 0],
      [-WATER_CELL, 0],
      [0, WATER_CELL],
      [0, -WATER_CELL],
    ] as const) {
      const nx = x + dx;
      const nz = z + dz;
      if (water[waterKey(nx, nz)]) continue;
      if (nearBaseClear(nx, nz)) continue;
      const k = waterKey(nx, nz);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ x: nx, z: nz });
    }
  }
  return out;
}

/** Beaches, kids, lighthouses, oil/gas seeps — scenic + gameplay hooks. */
function spawnScenicWorld(
  entities: Entity[],
  water: Record<string, 1>
): Record<string, 1> {
  const beaches: Record<string, 1> = {};
  const shores = shoreCells(water);
  const taken = new Set(entities.map((e) => `${Math.round(e.x)},${Math.round(e.z)}`));
  // Paint beach rings along ~35% of shores
  for (let i = 0; i < shores.length; i++) {
    if (i % 3 !== 0) continue;
    const s = shores[i];
    beaches[waterKey(s.x, s.z)] = 1;
  }
  // Kids on beaches
  const beachList = Object.keys(beaches)
    .map((k) => {
      const [ix, iz] = k.split(",").map(Number);
      return { x: ix * WATER_CELL, z: iz * WATER_CELL };
    })
    .sort(() => Math.random() - 0.5);
  for (let i = 0; i < Math.min(10, beachList.length); i++) {
    const b = beachList[i];
    entities.push({
      id: nid("kid"),
      kind: "beach_kid",
      team: null,
      x: b.x + (Math.random() - 0.5),
      z: b.z + (Math.random() - 0.5),
      hp: 25,
      maxHp: 25,
      order: { type: "idle" },
      facing: Math.random() * Math.PI * 2,
    });
  }
  // Lighthouses near river/sea shores (not on spawn pads)
  const lhSpots = shores.filter((s) => Math.hypot(s.x, s.z) > 12).slice(0, 80);
  lhSpots.sort(() => Math.random() - 0.5);
  for (let i = 0; i < Math.min(5, lhSpots.length); i++) {
    const s = lhSpots[i * 7] || lhSpots[i];
    if (!s) continue;
    if (entities.some((e) => e.kind === "lighthouse" && Math.hypot(e.x - s.x, e.z - s.z) < 8))
      continue;
    entities.push({
      id: nid("lh"),
      kind: "lighthouse",
      team: null,
      x: s.x,
      z: s.z,
      hp: 400,
      maxHp: 400,
    });
  }
  // Oil & gas seeps inland
  const placeSeep = (kind: "oil_seep" | "gas_seep") => {
    for (let attempt = 0; attempt < 40; attempt++) {
      const x = (Math.random() * 2 - 1) * (MAP_HALF - 6);
      const z = (Math.random() * 2 - 1) * (MAP_HALF - 6);
      if (water[waterKey(x, z)]) continue;
      if (nearBaseClear(x, z)) continue;
      if (spotTaken(taken, x, z, 3)) continue;
      taken.add(`${Math.round(x)},${Math.round(z)}`);
      entities.push({
        id: nid(kind === "oil_seep" ? "oil" : "gas"),
        kind,
        team: null,
        x,
        z,
        hp: 1,
        maxHp: 1,
        stock: kind === "oil_seep" ? 800 : 600,
      });
      return;
    }
  };
  for (let i = 0; i < 4; i++) placeSeep("oil_seep");
  for (let i = 0; i < 3; i++) placeSeep("gas_seep");
  // A free geologist near map edge for discovery fantasy
  for (let attempt = 0; attempt < 30; attempt++) {
    const x = (Math.random() * 2 - 1) * (MAP_HALF - 5);
    const z = (Math.random() * 2 - 1) * (MAP_HALF - 5);
    if (water[waterKey(x, z)]) continue;
    if (nearBaseClear(x, z)) continue;
    entities.push({
      id: nid("geo"),
      kind: "geologist",
      team: null,
      x,
      z,
      hp: 40,
      maxHp: 40,
      order: { type: "idle" },
      facing: Math.random() * Math.PI * 2,
    });
    break;
  }
  return beaches;
}

function spawnWildlife(entities: Entity[], water: Record<string, 1>) {
  const taken = new Set(entities.map((e) => `${Math.round(e.x)},${Math.round(e.z)}`));
  const passives: Array<[FoodAnimal, number]> = [
    ["sheep", 5],
    ["chicken", 6],
    ["cow", 3],
    ["goat", 4],
    ["duck", 4],
    ["deer", 5],
  ];
  const tryPlace = (kind: EntityKind, food: boolean) => {
    const predator = isPredator(kind);
    for (let attempt = 0; attempt < 48; attempt++) {
      const x = (Math.random() * 2 - 1) * (MAP_HALF - 4);
      const z = (Math.random() * 2 - 1) * (MAP_HALF - 4);
      if (water[waterKey(x, z)]) continue;
      if (predator ? nearPlacedTownHall(entities, x, z, PREDATOR_SPAWN_CLEAR) : nearBaseClear(x, z)) continue;
      if (spotTaken(taken, x, z, 2)) continue;
      taken.add(`${Math.round(x)},${Math.round(z)}`);
      const hp = ANIMAL_HP[kind] || 20;
      entities.push({
        id: nid(kind.slice(0, 2)),
        kind,
        team: null,
        x,
        z,
        hp,
        maxHp: hp,
        stock: food ? ANIMAL_FOOD_STOCK[kind] || 80 : undefined,
        order: { type: "idle" },
        facing: Math.random() * Math.PI * 2,
        attackCd: 0,
      });
      return true;
    }
    return false;
  };
  for (const [kind, n] of passives) {
    for (let i = 0; i < n; i++) tryPlace(kind, true);
  }
  for (let i = 0; i < 4; i++) tryPlace("coyote", false);
  for (let i = 0; i < 2; i++) tryPlace("gorilla", false);
  for (let i = 0; i < 2; i++) tryPlace("lion", false);
}

export function createInitialState(opts?: {
  practice?: boolean;
  seats?: number;
  computerTeams?: Team[];
  mapId?: string;
  mapSeed?: number;
  mapBiome?: string;
  difficulty?: Difficulty;
  civ?: CivId;
  winRule?: WinRule;
  mission?: MissionId;
}): GameState {
  _seq = 0;
  const seatCount = opts?.practice
    ? PRACTICE_SEATS
    : Math.max(2, Math.min(MAX_EMPIRE_SEATS, opts?.seats ?? MAX_EMPIRE_SEATS));
  const entities: Entity[] = [];
  const bases = seatBases(seatCount);
  const activeTeams = bases.map((b) => b.team);
  const computerSet = new Set<Team>(
    (opts?.computerTeams || []).filter((t) => activeTeams.includes(t))
  );
  // Practice 2v2: host amber; teal=ally AI; rose+violet=enemy AI. Live: all non-host computers.
  if (!opts?.computerTeams) {
    if (opts?.practice) {
      for (const t of activeTeams) if (t !== "amber") computerSet.add(t);
    } else for (const t of activeTeams) if (t !== "amber") computerSet.add(t);
  }

  // Alliances: practice seats 0+1 vs 2+3; otherwise every team alone (FFA) unless 4 seats → 2v2
  const alliances: Partial<Record<Team, number>> = {};
  if (opts?.practice || seatCount === 4) {
    for (let i = 0; i < activeTeams.length; i++) {
      alliances[activeTeams[i]] = i < 2 ? 0 : 1;
    }
  } else {
    activeTeams.forEach((t, i) => {
      alliances[t] = i;
    });
  }

  const picked = getMapById(opts?.mapId || "");
  const askedSeed = Number(opts?.mapSeed);
  const askedBiome = typeof opts?.mapBiome === "string" ? opts.mapBiome : "";
  const mapDef = picked
    ? {
        ...picked,
        seed: Number.isFinite(askedSeed) && askedSeed > 0 ? askedSeed >>> 0 : picked.seed,
        biome: (askedBiome || picked.biome) as typeof picked.biome,
      }
    : pickRandomMap();
  const terrain = generateMapTerrain(
    mapDef,
    bases.map((b) => ({ x: b.x, z: b.z }))
  );
  const water = terrain.water;
  const mapBeaches = terrain.beaches;

  for (const b of bases) {
    entities.push({
      id: nid("th"),
      kind: "town_hall",
      team: b.team,
      x: b.x,
      z: b.z,
      hp: BUILD_HP.town_hall,
      maxHp: BUILD_HP.town_hall,
      train: null,
    });
    for (let i = 0; i < 3; i++) {
      const ox = Math.cos(b.face) * 2.2;
      const oz = Math.sin(b.face) * 2.2;
      entities.push({
        id: nid("w"),
        kind: "worker",
        team: b.team,
        x: b.x + (i - 1) * 1.5 * Math.cos(b.face + Math.PI / 2) + ox * 0.15,
        z: b.z + (i - 1) * 1.5 * Math.sin(b.face + Math.PI / 2) + oz * 0.15,
        hp: UNIT_STATS.worker.hp,
        maxHp: UNIT_STATS.worker.hp,
        carry: { type: "wood", amount: 0 },
        order: { type: "idle" },
        facing: b.face,
      });
    }
    entities.push({
      id: nid("ex"),
      kind: "explorer",
      team: b.team,
      x: clampMap(b.x + Math.cos(b.face) * 8 + Math.cos(b.face + Math.PI / 2) * 3.2),
      z: clampMap(b.z + Math.sin(b.face) * 8 + Math.sin(b.face + Math.PI / 2) * 3.2),
      hp: 80,
      maxHp: 80,
      order: { type: "idle" },
      facing: b.face,
    });
  }

  // Forests and mines spread across the wide land, not piled in the middle.
  const spread = 3.1;
  spawnForestClump(entities, -14 * spread, -12 * spread, 14, 8, water);
  spawnForestClump(entities, -10 * spread, -24 * spread, 12, 7, water);
  spawnForestClump(entities, -26 * spread, -10 * spread, 12, 7, water);
  spawnForestClump(entities, 14 * spread, 12 * spread, 14, 8, water);
  spawnForestClump(entities, 10 * spread, 24 * spread, 12, 7, water);
  spawnForestClump(entities, 26 * spread, 10 * spread, 12, 7, water);
  spawnForestClump(entities, -6 * spread, 8 * spread, 10, 6, water);
  spawnForestClump(entities, 6 * spread, -8 * spread, 10, 6, water);
  spawnForestClump(entities, -22 * spread, 8 * spread, 9, 6, water);
  spawnForestClump(entities, 22 * spread, -8 * spread, 9, 6, water);
  spawnForestClump(entities, 0, -28 * spread, 8, 6, water);
  spawnForestClump(entities, 0, 28 * spread, 8, 6, water);
  spawnForestClump(entities, -28 * spread, 0, 8, 6, water);
  spawnForestClump(entities, 28 * spread, 0, 8, 6, water);

  // Food bushes across map
  const bushes = [
    [-16, -16],
    [-12, -8],
    [-24, -16],
    [16, 16],
    [12, 8],
    [24, 16],
    [-8, 4],
    [8, -4],
    [-18, 14],
    [18, -14],
    [0, -14],
    [0, 14],
    [-28, -8],
    [28, 8],
    [-4, -26],
    [4, 26],
  ];
  for (const [x, z] of bushes) {
    spawnBush(entities, x * spread, z * spread, 280 + Math.floor(Math.random() * 80), water);
  }

  // Gold & stone patches
  const mines: Array<["gold" | "stone", number, number, number]> = [
    ["gold", -14, -6, 500],
    ["gold", 14, 6, 500],
    ["gold", -8, 18, 400],
    ["gold", 8, -18, 400],
    ["gold", -26, 4, 380],
    ["gold", 26, -4, 380],
    ["stone", -10, -14, 450],
    ["stone", 10, 14, 450],
    ["stone", -20, 10, 400],
    ["stone", 20, -10, 400],
    ["stone", 0, -10, 350],
    ["stone", 0, 10, 350],
  ];
  for (const [k, x, z, s] of mines) spawnMine(entities, k, x * spread, z * spread, s, water);

  // Map-driven resource hubs (fair starts + island caches)
  for (const hub of terrain.resourceHubs) {
    if (water[waterKey(hub.x, hub.z)]) continue;
    if (hub.kind === "wood") spawnForestClump(entities, hub.x, hub.z, 8, 3.2, water);
    else if (hub.kind === "food") spawnBush(entities, hub.x, hub.z, 300, water);
    else spawnMine(entities, hub.kind, hub.x, hub.z, 420, water);
  }

  spawnVisibleTrees(entities, mapDef.seed, mapDef.biome, water);

  spawnFish(entities, water);
  spawnChronoRelics(entities, water);
  spawnWildlife(entities, water);
  const scenicBeaches = spawnScenicWorld(entities, water);
  const beaches = { ...scenicBeaches, ...mapBeaches };

  const woodCount = entities.filter((e) => e.kind === "wood").length;

  const state: GameState = {
    tick: 0,
    entities,
    eco: Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, defaultEco()])) as Record<Team, PlayerEco>,
    meta: Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, defaultMeta()])) as Record<Team, PlayerMeta>,
    winner: "",
    started: false,
    practice: !!opts?.practice,
    chronoCharges: Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, null])) as Record<Team, ChronoCharge | null>,
    events: [],
    eventSeq: 0,
    explored: Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, {}])) as Record<Team, Record<string, 1>>,
    knownResources: Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, {}])) as Record<Team, Record<string, 1>>,
    water,
    waterVersion: WATER_GEN_VERSION,
    marketPrices: defaultMarketPrices(),
    activeTeams,
    computerTeams: Object.fromEntries([...computerSet].map((t) => [t, true as const])),
    beaches,
    mapId: mapDef.id,
    mapName: mapDef.name,
    mapSeed: mapDef.seed,
    mapBiome: mapDef.biome,
    alliances,
    islands: terrain.islands,
  };
  pushEvent(state, {
    kind: "info",
    text: `Map: ${mapDef.name} · ${woodCount} timber · ${terrain.islands.length} islands`,
  });
  pushEvent(state, {
    kind: "info",
    text: "Each town has an explorer who only scouts. No attacks for 25 minutes. Villagers never fight.",
  });
  const difficulty: Difficulty =
    opts?.mission === "first-hearth" ? "easy" : opts?.mission === "high-ground" ? "hard" : opts?.difficulty || "normal";
  state.difficulty = difficulty;
  state.winRule = opts?.winRule || "standard";
  state.scoreTarget = 8000;
  state.mission = opts?.mission || "skirmish";
  const civs: Partial<Record<Team, CivId>> = {};
  const pool: CivId[] = ["assyria", "babylon", "egypt", "greece", "persia", "rome", "yamato", "sumer"];
  activeTeams.forEach((t, i) => {
    civs[t] = t === "amber" && opts?.civ ? opts.civ : pool[i % pool.length];
    state.meta[t].civ = civs[t];
    const bonus = civById(civs[t]);
    if (bonus.startFood) state.eco[t].food += bonus.startFood;
    if (bonus.villagerCost === 40) {
      /* cheaper villagers are applied when training */
    }
  });
  state.civs = civs;
  state.diplomacy = {};
  for (const a of activeTeams) {
    state.diplomacy[a] = {};
    for (const b of activeTeams) {
      if (a === b) continue;
      state.diplomacy[a]![b] = areAllied(state, a, b) ? "ally" : "enemy";
    }
  }
  if (difficulty === "easy") {
    state.eco.amber.food += 250;
    state.eco.amber.wood += 200;
  }
  if (difficulty === "hard") {
    for (const t of activeTeams) {
      if (t === "amber") continue;
      state.eco[t].food += 150;
      state.eco[t].wood += 120;
      state.eco[t].gold += 80;
    }
  }
  for (const e of state.entities) {
    if (e.kind !== "worker" || !e.team) continue;
    const extra = teamCiv(state, e.team).villagerHp || 0;
    e.hp += extra;
    e.maxHp += extra;
  }
  markShallows(state);
  for (let i = 0; i < 3; i++) {
    const ang = (i / 3) * Math.PI * 2;
    state.entities.push({
      id: nid("ru"),
      kind: "ruin",
      team: null,
      x: clampMap(Math.cos(ang) * 18),
      z: clampMap(Math.sin(ang) * 18),
      hp: 800,
      maxHp: 800,
    });
    state.entities.push({
      id: nid("af"),
      kind: "artifact",
      team: null,
      x: clampMap(Math.cos(ang + 0.4) * 28),
      z: clampMap(Math.sin(ang + 0.4) * 28),
      hp: 40,
      maxHp: 40,
    });
  }
  parkResources(state);
  recountPop(state);
  refreshFog(state);
  return state;
}

function recountPop(state: GameState) {
  ensureMeta(state);
  for (const team of EMPIRE_TEAMS as unknown as Team[]) {
    let pop = 0;
    for (const e of state.entities) {
      if (e.team !== team || e.hp <= 0) continue;
      if (isControllableUnit(e.kind)) pop += 1;
    }
    let homes = 0;
    for (const e of state.entities) {
      if (e.team !== team || e.hp <= 0 || e.constructing) continue;
      if (e.kind === "town_hall" || e.kind === "house") homes += 1;
    }
    state.meta[team].pop = pop;
    state.meta[team].popCap = Math.min(50, homes * 4);
  }
}

export type Command =
  | { type: "move"; unitIds: string[]; x: number; z: number }
  | { type: "attack_move"; unitIds: string[]; x: number; z: number }
  | { type: "gather"; unitIds: string[]; resourceId: string }
  /** Split villagers evenly across chosen resource tasks (auto closest known). */
  | { type: "divide_work"; unitIds: string[]; tasks: ResType[]; people?: number }
  | { type: "attack"; unitIds: string[]; targetId: string }
  | { type: "heal"; unitIds: string[]; targetId: string }
  | {
      type: "build";
      unitId: string;
      /** Every selected villager. Empty = nearest villagers on this team. */
      unitIds?: string[];
      building: BuildKind;
      x: number;
      z: number;
      /** Optional drag end — walls fill H/V/45°/L path to (x1,z1). */
      x1?: number;
      z1?: number;
      /** Optional multi-corner polyline — preferred over x1/z1 alone. */
      path?: FortPoint[];
      /** Cells on the wall line that should be gates instead of wall segments. */
      gates?: FortPoint[];
    }
  | { type: "train"; buildingId: string; unit: TrainUnit }
  | { type: "research"; buildingId: string; tech: TechId }
  | { type: "capture"; unitIds: string[]; relicId: string }
  | { type: "market_buy"; good: MarketGood; amount?: number }
  | { type: "market_sell"; good: MarketGood; amount?: number }
  | { type: "market_swap"; give: "wood" | "food" | "gold" | "stone"; take: "wood" | "food" | "gold" | "stone" }
  | { type: "trade_route" }
  | { type: "stop"; unitIds: string[] }
  | { type: "waypoint"; unitIds: string[]; x: number; z: number }
  | { type: "stance"; unitIds: string[]; stance: "aggressive" | "stand" }
  | { type: "repair"; unitIds: string[]; targetId: string }
  | { type: "convert"; unitIds: string[]; targetId: string }
  | { type: "patrol"; unitIds: string[]; x: number; z: number }
  | { type: "rally"; buildingId: string; x: number; z: number }
  | { type: "tribute"; to: Team; good: ResType; amount: number }
  | { type: "delete"; targetId: string }
  | { type: "diplomacy"; other: Team; stance: DipStance }
  | { type: "garrison"; unitIds: string[]; buildingId: string }
  | { type: "ungarrison"; buildingId: string }
  | { type: "attack_ground"; unitIds: string[]; x: number; z: number }
  | { type: "rewind" }
  | { type: "board"; unitIds: string[]; shipId: string }
  | { type: "unload"; shipId: string; x: number; z: number }
  | { type: "toggle_gate"; gateId: string; mode?: GateState }
  /**
   * Raze own wall / gate / palisade (partial refund).
   * nearby: delete every own gate within 8 of the target (a stacked pile).
   */
  | { type: "destroy_fort"; targetId: string; nearby?: boolean };

/** Gates are wide. Don't drop another one on a town center, house, or an existing gate. */
function gateSpotBlocked(state: GameState, x: number, z: number): boolean {
  for (const e of state.entities) {
    if (e.hp <= 0) continue;
    if (e.kind === "gate" && dist(e, { x, z }) < 2.4) return true;
    if (isBuildingKind(e.kind) && !isFortKind(e.kind)) {
      const need = 1.15 + buildRadius(e.kind) * 0.85;
      if (dist(e, { x, z }) < need) return true;
    }
  }
  return false;
}

function fortCellTaken(
  state: GameState,
  x: number,
  z: number,
  exceptId?: string
): boolean {
  for (const e of state.entities) {
    if (e.hp <= 0) continue;
    if (exceptId && e.id === exceptId) continue;
    if (!isFortKind(e.kind)) continue;
    if (Math.round(e.x) === x && Math.round(e.z) === z) return true;
  }
  return false;
}

type FortBuildKind = "palisade" | "wall" | "fortified_wall" | "gate";

function spawnFortSite(
  state: GameState,
  team: Team,
  building: FortBuildKind,
  x: number,
  z: number
): Entity {
  let maxHp = BUILD_HP[building] + state.meta[team].wallHpBonus;
  const site: Entity = {
    id: nid(building.slice(0, 3)),
    kind: building,
    team,
    x,
    z,
    hp: Math.max(25, Math.floor(maxHp * 0.1)),
    maxHp,
    train: null,
    constructing: true,
    gateState: building === "gate" ? "closed" : undefined,
  };
  state.entities.push(site);
  return site;
}

function nextUnfinishedFort(
  state: GameState,
  team: Team,
  from: { x: number; z: number }
): Entity | null {
  let best: Entity | null = null;
  let bestD = Infinity;
  for (const e of state.entities) {
    if (e.team !== team || e.hp <= 0 || !e.constructing) continue;
    if (!isFortKind(e.kind)) continue;
    const d = dist(e, from);
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

const DIVIDE_TASK_SET = new Set<ResType>(["wood", "food", "gold", "stone"]);

const MARKET_GOOD_SET = new Set<string>(MARKET_GOODS);

const BUILD_KINDS_SET = new Set<string>(Object.keys(BUILD_AGE));
const TRAIN_SET = new Set<string>(Object.keys(UNIT_AGE));
const TECH_SET = new Set<string>([
  "age_feudal",
  "age_castle",
  "age_imperial",
  "bs_attack",
  "bs_armor",
  "uni_masonry",
  "uni_ballistics",
]);

let idCache: { state: GameState; n: number; map: Map<string, Entity> } | null = null;

const RESOURCE_KINDS = new Set(["wood", "food", "gold", "stone", "fish"]);

function parkResources(state: GameState) {
  if (state.props) return;
  const props: Entity[] = [];
  const live: Entity[] = [];
  for (const e of state.entities) {
    if (RESOURCE_KINDS.has(e.kind)) props.push(e);
    else live.push(e);
  }
  state.props = props;
  state.entities = live;
  idCache = null;
}

function byId(state: GameState, id: string): Entity | undefined {
  const extra = state.props?.length || 0;
  if (!idCache || idCache.state !== state || idCache.n !== state.entities.length + extra) {
    const map = new Map<string, Entity>();
    for (const e of state.entities) if (e.hp > 0) map.set(e.id, e);
    if (state.props) for (const e of state.props) if (e.hp > 0) map.set(e.id, e);
    idCache = { state, n: state.entities.length + extra, map };
  }
  const hit = idCache.map.get(id);
  return hit && hit.hp > 0 ? hit : undefined;
}

function costOf(key: string): Cost {
  return COST[key] || {};
}

function canAfford(eco: PlayerEco, cost: Cost): boolean {
  return (
    (eco.wood || 0) >= (cost.wood || 0) &&
    (eco.food || 0) >= (cost.food || 0) &&
    (eco.gold || 0) >= (cost.gold || 0) &&
    (eco.stone || 0) >= (cost.stone || 0)
  );
}

function pay(eco: PlayerEco, cost: Cost) {
  eco.wood -= cost.wood || 0;
  eco.food -= cost.food || 0;
  eco.gold -= cost.gold || 0;
  eco.stone -= cost.stone || 0;
}

function refund(eco: PlayerEco, cost: Cost) {
  eco.wood += cost.wood || 0;
  eco.food += cost.food || 0;
  eco.gold += cost.gold || 0;
  eco.stone += cost.stone || 0;
}

function ageOk(state: GameState, team: Team, need: Age): boolean {
  return AGE_RANK[state.meta[team].age] >= AGE_RANK[need];
}

function trainBuildingFor(unit: TrainUnit): EntityKind[] {
  switch (unit) {
    case "worker":
      return ["town_hall"];
    case "militia":
    case "spearman":
    case "man_at_arms":
      return ["barracks"];
    case "archer":
    case "skirmisher":
    case "crossbowman":
    case "hand_cannon":
      return ["archery_range"];
    case "scout_cav":
    case "knight":
    case "camel_rider":
    case "war_elephant":
      return ["stable"];
    case "ram":
    case "mangonel":
    case "scorpion":
    case "trebuchet":
      return ["siege_workshop"];
    case "fishing_boat":
    case "transport":
    case "war_galley":
      return ["dock"];
    case "geologist":
      return ["university"];
    case "healer":
      return ["church"];
    default:
      return [];
  }
}

function researchBuildingFor(tech: TechId): EntityKind[] {
  if (tech === "age_feudal" || tech === "age_castle" || tech === "age_imperial") return ["town_hall"];
  if (tech === "bs_attack" || tech === "bs_armor") return ["blacksmith"];
  if (tech === "uni_masonry" || tech === "uni_ballistics") return ["university"];
  return [];
}

function areAllied(state: GameState, a: Team | null, b: Team | null): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const aa = state.alliances?.[a];
  const bb = state.alliances?.[b];
  return aa != null && bb != null && aa === bb;
}

function isHostile(state: GameState, a: Team | null, b: Team | null): boolean {
  if (!a || !b || a === b) return false;
  return !areAllied(state, a, b);
}

const fortWallCache = new WeakMap<GameState, { tick: number; walls: Entity[] }>();

function fortWalls(state: GameState): Entity[] {
  const hit = fortWallCache.get(state);
  if (hit && hit.tick === state.tick) return hit.walls;
  const walls: Entity[] = [];
  for (const e of state.entities) {
    if (e.hp <= 0) continue;
    if (isFortWallKind(e.kind) || e.kind === "gate") walls.push(e);
  }
  fortWallCache.set(state, { tick: state.tick, walls });
  return walls;
}

function nearestSurface(
  state: GameState,
  x: number,
  z: number,
  naval: boolean
): { x: number; z: number } | null {
  const ok = (px: number, pz: number) => {
    if (naval) return isWaterAt(state, px, pz);
    if (isWaterAt(state, px, pz)) return false;
    return true;
  };
  if (ok(x, z)) return { x, z };
  for (let r = 1; r <= 40; r += 2) {
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const px = clampMap(x + Math.cos(a) * r);
      const pz = clampMap(z + Math.sin(a) * r);
      if (ok(px, pz)) return { x: px, z: pz };
    }
  }
  return null;
}

function wallBlocks(state: GameState, x: number, z: number, team: Team | null, naval: boolean): boolean {
  if (naval) {
    return !isWaterAt(state, x, z);
  }
  if (isShallowAt(state, x, z)) {
    /* Shore shallows are walkable. Walls on that cell still block below. */
  } else if (isWaterAt(state, x, z)) return true;
  const walls = fortWalls(state);
  if (!walls.length) return false;
  for (const e of walls) {
    if (isFortWallKind(e.kind)) {
      if (dist(e, { x, z }) < 0.85) return true;
    } else if (e.kind === "gate" && dist(e, { x, z }) < 0.95) {
      const st = e.gateState || "closed";
      if (st === "locked") return true;
      if (st === "open") return false;
      if (team && areAllied(state, e.team, team)) return false;
      return true;
    }
  }
  return false;
}

function cellBlockedForTeam(
  state: GameState,
  x: number,
  z: number,
  team: Team | null,
  naval: boolean
): boolean {
  return wallBlocks(state, x, z, team, naval);
}

const pathFailAt = new WeakMap<Entity, { tick: number; key: string }>();
/** Full searches per tick. Extra units nudge instead of stalling the worker. */
let pathBudget = 3;

function pathTargetKey(tx: number, tz: number): string {
  return `${Math.round(tx)},${Math.round(tz)}`;
}

function ensurePath(
  state: GameState,
  e: Entity,
  tx: number,
  tz: number
): void {
  const naval = isNavalUnit(e.kind);
  if (naval) {
    e.path = undefined;
    return;
  }
  if (pathBudget <= 0) {
    e.path = undefined;
    return;
  }
  pathBudget -= 1;
  const aim = nearestOpenPoint(state, e, tx, tz, false);
  const found = findGridPath(
    e.x,
    e.z,
    aim.x,
    aim.z,
    (cx, cz) => cellBlockedForTeam(state, cx, cz, e.team, false),
    MAP_HALF,
    200,
    6
  );
  e.path = found?.path?.length ? found.path : undefined;
  e.routeBlocked = found ? !found.reached : true;
  if (!e.path || e.path.length === 0) {
    pathFailAt.set(e, { tick: state.tick || 0, key: pathTargetKey(tx, tz) });
  } else {
    pathFailAt.delete(e);
  }
}

/** Nearest open ground if the job point itself is inside a wall or the water. */
function reachableAim(
  state: GameState,
  e: Entity,
  tx: number,
  tz: number
): { x: number; z: number } {
  const naval = isNavalUnit(e.kind);
  if (!wallBlocks(state, tx, tz, e.team, naval)) return { x: tx, z: tz };
  let best: { x: number; z: number } | null = null;
  let bestD = 1e9;
  for (let r = 1; r <= 4; r++) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const x = clampMap(tx + Math.cos(a) * r);
      const z = clampMap(tz + Math.sin(a) * r);
      if (wallBlocks(state, x, z, e.team, naval)) continue;
      const d = Math.hypot(e.x - x, e.z - z);
      if (d < bestD) {
        bestD = d;
        best = { x, z };
      }
    }
    if (best) return best;
  }
  return { x: tx, z: tz };
}

/** If a villager is standing inside a wall, step them out to open ground. */
function escapeIfEmbedded(state: GameState, e: Entity) {
  const naval = isNavalUnit(e.kind);
  if (!wallBlocks(state, e.x, e.z, e.team, naval)) return;
  const spin = ((e.stuck || 0) % 8) * 0.5;
  for (let r = 0.35; r <= 2.4; r += 0.35) {
    for (let i = 0; i < 8; i++) {
      const a = spin + (i / 8) * Math.PI * 2;
      const x = clampMap(e.x + Math.cos(a) * r);
      const z = clampMap(e.z + Math.sin(a) * r);
      if (wallBlocks(state, x, z, e.team, naval)) continue;
      e.x = x;
      e.z = z;
      e.path = undefined;
      return;
    }
  }
}

/** Try eight steps and take the one that gets closer. Used when the straight step is blocked. */
function nudgeToward(
  state: GameState,
  e: Entity,
  tx: number,
  tz: number,
  step: number
): boolean {
  const naval = isNavalUnit(e.kind);
  const base = Math.hypot(tx - e.x, tz - e.z);
  let best: { x: number; z: number; d: number } | null = null;
  const spin = ((e.stuck || 0) % 8) * 0.55;
  for (let i = 0; i < 8; i++) {
    const a = spin + (i / 8) * Math.PI * 2;
    const x = clampMap(e.x + Math.cos(a) * step);
    const z = clampMap(e.z + Math.sin(a) * step);
    if (wallBlocks(state, x, z, e.team, naval)) continue;
    const d = Math.hypot(tx - x, tz - z);
    if (!best || d < best.d) best = { x, z, d };
  }
  if (!best || best.d >= base - 0.02) return false;
  e.facing = Math.atan2(best.x - e.x, best.z - e.z);
  e.x = best.x;
  e.z = best.z;
  return true;
}

/** Step onto any open ground so a blocked order cannot freeze the unit. */
function slideToOpen(state: GameState, e: Entity) {
  const naval = isNavalUnit(e.kind);
  const spin = ((e.stuck || 0) % 8) * 0.7;
  for (let r = 0.55; r <= 2.2; r += 0.4) {
    for (let i = 0; i < 8; i++) {
      const a = spin + (i / 8) * Math.PI * 2;
      const x = clampMap(e.x + Math.cos(a) * r);
      const z = clampMap(e.z + Math.sin(a) * r);
      if (wallBlocks(state, x, z, e.team, naval)) continue;
      if (Math.hypot(x - e.x, z - e.z) < 0.2) continue;
      e.facing = Math.atan2(x - e.x, z - e.z);
      e.x = x;
      e.z = z;
      e.path = undefined;
      pathFailAt.delete(e);
      return;
    }
  }
}

/** One path search per order. A new target searches immediately; the same unreachable target waits. */
function refreshPath(state: GameState, e: Entity, tx: number, tz: number): void {
  const aim = reachableAim(state, e, tx, tz);
  tx = aim.x;
  tz = aim.z;
  const end = e.path && e.path.length ? e.path[e.path.length - 1] : null;
  const drifted = !end || Math.hypot(end.x - tx, end.z - tz) > 4;
  if (e.path && e.path.length && !drifted) return;
  const fail = pathFailAt.get(e);
  const key = pathTargetKey(tx, tz);
  if (fail && fail.key === key && (state.tick || 0) - fail.tick < 5) return;
  e.path = undefined;
  ensurePath(state, e, tx, tz);
}

function nearestOpenPoint(
  state: GameState,
  e: Entity,
  x: number,
  z: number,
  naval: boolean
): { x: number; z: number } {
  if (!wallBlocks(state, x, z, e.team, naval)) return { x, z };
  for (let r = 1; r <= 28; r++) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const px = clampMap(x + Math.cos(a) * r);
      const pz = clampMap(z + Math.sin(a) * r);
      if (!wallBlocks(state, px, pz, e.team, naval)) return { x: px, z: pz };
    }
  }
  return { x, z };
}

function segmentOpen(
  state: GameState,
  e: Entity,
  x: number,
  z: number,
  naval: boolean
): boolean {
  const dx = x - e.x;
  const dz = z - e.z;
  const d = Math.hypot(dx, dz);
  const n = Math.max(1, Math.ceil(d / 1.25));
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    if (wallBlocks(state, e.x + dx * t, e.z + dz * t, e.team, naval)) return false;
  }
  return true;
}

function commitWalk(e: Entity, x: number, z: number) {
  const px = e.x;
  const pz = e.z;
  e.x = clampMap(x);
  e.z = clampMap(z);
  if (Math.hypot(e.x - px, e.z - pz) > 0.01) e.facing = Math.atan2(e.x - px, e.z - pz);
}

/** Step toward a point. If water or a wall is in the way, slide along it. Always moves when any ground is open. */
function steerToward(
  state: GameState,
  e: Entity,
  tx: number,
  tz: number,
  step: number,
  naval: boolean
): void {
  const dx = tx - e.x;
  const dz = tz - e.z;
  const d = Math.hypot(dx, dz);
  if (d <= 0.45) {
    if (!wallBlocks(state, tx, tz, e.team, naval)) commitWalk(e, tx, tz);
    return;
  }
  const ux = dx / d;
  const uz = dz / d;
  const lengths = [Math.min(step, d), Math.min(step * 0.5, d), Math.min(0.4, d)];
  for (const len of lengths) {
    if (len < 0.12) continue;
    const nx = e.x + ux * len;
    const nz = e.z + uz * len;
    if (!wallBlocks(state, nx, nz, e.team, naval)) {
      commitWalk(e, nx, nz);
      return;
    }
  }
  const len = lengths[0] < 0.12 ? 0.4 : lengths[0];
  const prefer: Array<1 | -1> = e.slide === -1 ? [-1, 1] : [1, -1];
  for (const s of prefer) {
    const px = -uz * s;
    const pz = ux * s;
    for (const along of [0.35, 0.7, 1]) {
      let vx = ux * (1 - along) + px * along;
      let vz = uz * (1 - along) + pz * along;
      const mag = Math.hypot(vx, vz) || 1;
      vx /= mag;
      vz /= mag;
      const nx = e.x + vx * len;
      const nz = e.z + vz * len;
      if (wallBlocks(state, nx, nz, e.team, naval)) continue;
      e.slide = s;
      commitWalk(e, nx, nz);
      return;
    }
  }
  let best: { x: number; z: number; d: number } | null = null;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const nx = e.x + Math.cos(a) * len;
    const nz = e.z + Math.sin(a) * len;
    if (wallBlocks(state, nx, nz, e.team, naval)) continue;
    const nd = Math.hypot(tx - nx, tz - nz);
    if (!best || nd < best.d) best = { x: nx, z: nz, d: nd };
  }
  if (best) commitWalk(e, best.x, best.z);
}

/** Leave a blocked pocket for the nearest open ground, preferring ground closer to the job. */
function hopToOpen(
  state: GameState,
  e: Entity,
  tx: number,
  tz: number,
  naval: boolean
): boolean {
  let best: { x: number; z: number } | null = null;
  let bestScore = 1e9;
  for (let r = 0.6; r <= 3.4; r += 0.4) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const x = clampMap(e.x + Math.cos(a) * r);
      const z = clampMap(e.z + Math.sin(a) * r);
      if (wallBlocks(state, x, z, e.team, naval)) continue;
      const score = Math.hypot(tx - x, tz - z) + r * 0.2;
      if (score < bestScore) {
        bestScore = score;
        best = { x, z };
      }
    }
  }
  if (!best) return false;
  commitWalk(e, best.x, best.z);
  return true;
}

function moveToward(
  state: GameState,
  e: Entity,
  tx: number,
  tz: number,
  speed: number,
  dt: number
): boolean {
  if (e.aboardShipId) return false;
  const naval = isNavalUnit(e.kind);
  const aimKey = pathTargetKey(tx, tz);
  if (e.stuckKey !== aimKey) {
    e.stuck = 0;
    e.stuckKey = aimKey;
    e.path = undefined;
    e.slide = undefined;
    e.routeBlocked = false;
    e.loiter = 0;
    e.anchorX = undefined;
    e.anchorZ = undefined;
    e.anchorTick = undefined;
  }
  tx = clampMap(tx);
  tz = clampMap(tz);
  if (wallBlocks(state, tx, tz, e.team, naval)) {
    const land = nearestOpenPoint(state, e, tx, tz, naval);
    tx = land.x;
    tz = land.z;
  }
  const x0 = e.x;
  const z0 = e.z;
  const d0 = Math.hypot(tx - e.x, tz - e.z);
  if (d0 <= 0.45) {
    if (!wallBlocks(state, tx, tz, e.team, naval)) commitWalk(e, tx, tz);
    e.path = undefined;
    e.stuck = 0;
    return true;
  }

  escapeIfEmbedded(state, e);

  // Open ground: walk the straight line. A saved route must not drag them the long way around.
  // A failed search never counts as arriving. That villager keeps walking the bank.
  if (!naval && segmentOpen(state, e, tx, tz, false)) {
    e.path = undefined;
    e.routeBlocked = false;
  } else if (!naval && !e.path?.length) {
    const fail = pathFailAt.get(e);
    const key = pathTargetKey(tx, tz);
    const cooling = fail && fail.key === key && (state.tick || 0) - fail.tick < 3;
    if (!cooling) ensurePath(state, e, tx, tz);
  }

  let aimX = tx;
  let aimZ = tz;
  if (!naval && e.path && e.path.length) {
    while (e.path.length > 1 && Math.hypot(e.path[0].x - e.x, e.path[0].z - e.z) < 1.2) {
      e.path.shift();
      e.wpStall = 0;
    }
    const wp = e.path[0];
    if (wp) {
      aimX = wp.x;
      aimZ = wp.z;
    }
  }

  const step = Math.max(0.35, speed * dt);
  const aimBefore = Math.hypot(aimX - e.x, aimZ - e.z);
  steerToward(state, e, aimX, aimZ, step, naval);
  if (e.path && e.path.length && Math.hypot(e.path[0].x - e.x, e.path[0].z - e.z) < 1.2) {
    e.path.shift();
    e.wpStall = 0;
    if (!e.path.length) e.path = undefined;
  } else if (e.path && e.path.length) {
    const aimAfter = Math.hypot(aimX - e.x, aimZ - e.z);
    if (aimAfter > aimBefore - 0.08) {
      e.wpStall = (e.wpStall || 0) + 1;
      if (e.wpStall >= 3) {
        e.path.shift();
        e.wpStall = 0;
        if (!e.path.length) e.path = undefined;
      }
    } else {
      e.wpStall = 0;
    }
  }

  const moved = Math.hypot(e.x - x0, e.z - z0);
  const d1 = Math.hypot(tx - e.x, tz - e.z);
  if (isCavKind(e.kind)) {
    if (moved > 0.05) e.charge = Math.min(1.2, (e.charge || 0) + moved * 0.45);
    else e.charge = Math.max(0, (e.charge || 0) - 0.12);
  }
  if (d1 <= 0.45) {
    e.stuck = 0;
    e.path = undefined;
    return true;
  }
  if (moved > 0.04 && d1 < d0 - 0.15) e.stuck = 0;
  else e.stuck = (e.stuck || 0) + 1;
  if (moved < 0.04) {
    e.path = undefined;
    if (!hopToOpen(state, e, tx, tz, naval)) slideToOpen(state, e);
  }
  if (e.anchorTick == null || (state.tick || 0) - e.anchorTick >= 8) {
    if (e.anchorX != null) {
      const drift = Math.hypot(e.x - e.anchorX, e.z - e.anchorZ);
      if (drift < 0.45 && d1 < 2.2) {
        e.loiter = (e.loiter || 0) + 1;
        e.path = undefined;
        if (e.loiter >= 3) {
          e.loiter = 0;
          return true;
        }
      } else e.loiter = 0;
    }
    e.anchorX = e.x;
    e.anchorZ = e.z;
    e.anchorTick = state.tick || 0;
  }
  return Math.hypot(tx - e.x, tz - e.z) <= 0.45;
}

/** N / E / S / W first, then diagonals — each villager gets their own side. */
const STAND_DIRS: Array<[number, number]> = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
  [Math.SQRT1_2, Math.SQRT1_2],
  [Math.SQRT1_2, -Math.SQRT1_2],
  [-Math.SQRT1_2, Math.SQRT1_2],
  [-Math.SQRT1_2, -Math.SQRT1_2],
];

/**
 * Fixed stand point on the ring around a tree, farm, or drop site.
 * The slot comes from the villager's id, so other people arriving does not
 * shove everyone onto a new spot.
 */
function standAround(
  state: GameState,
  worker: Entity,
  cx: number,
  cz: number,
  kind: "gather" | "return" | "build"
): { x: number; z: number } {
  const baseR = kind === "gather" ? 1.35 : kind === "return" ? 2.05 : 1.85;
  const ringStep = 0.55;
  const naval = isNavalUnit(worker.kind);
  let h = 0;
  for (let i = 0; i < worker.id.length; i++) {
    h = (h * 33 + worker.id.charCodeAt(i)) >>> 0;
  }
  const start = h % STAND_DIRS.length;

  for (let attempt = 0; attempt < STAND_DIRS.length * 3; attempt++) {
    const slot = (start + attempt) % STAND_DIRS.length;
    const ring = Math.floor(attempt / STAND_DIRS.length);
    const r = baseR + ring * ringStep;
    const [dx, dz] = STAND_DIRS[slot];
    const x = clampMap(cx + dx * r);
    const z = clampMap(cz + dz * r);
    if (!naval && isWaterAt(state, x, z)) continue;
    if (naval && !isWaterAt(state, x, z)) continue;
    if (wallBlocks(state, x, z, worker.team, naval)) continue;
    return { x, z };
  }

  const a = ((h % 360) * Math.PI) / 180;
  return {
    x: clampMap(cx + Math.cos(a) * (baseR + 0.4)),
    z: clampMap(cz + Math.sin(a) * (baseR + 0.4)),
  };
}

/**
 * Units do NOT block each other (villagers / army pass through).
 * Kept as a no-op hook so group moves never jam into a traffic jam.
 */
function separateWorkers(_state: GameState) {
  /* intentionally empty — no unit-unit collision */
}

/** Friendly infantry sidestep cavalry so horses are not stuck in the spears. */
function yieldInfantryForCavalry(state: GameState) {
  const cav = state.entities.filter((e) => e.hp > 0 && isCavKind(e.kind));
  if (!cav.length) return;
  for (const inf of state.entities) {
    if (inf.hp <= 0 || inf.kind === "worker" || !isInfKind(inf.kind)) continue;
    for (const c of cav) {
      if (c.team !== inf.team) continue;
      const dx = inf.x - c.x;
      const dz = inf.z - c.z;
      const d = Math.hypot(dx, dz);
      if (d > 0.72 || d < 0.02) continue;
      const nx = clampMap(inf.x + (dx / d) * 0.2);
      const nz = clampMap(inf.z + (dz / d) * 0.2);
      if (!wallBlocks(state, nx, nz, inf.team, false)) {
        inf.x = nx;
        inf.z = nz;
      }
      break;
    }
  }
}

function resumeGather(state: GameState, e: Entity, resourceId: string | undefined, carried: ResType) {
  const ordered = resourceId ? byId(state, resourceId) : undefined;
  const orderedType = ordered ? resTypeOfNode(ordered.kind) : null;
  if (
    ordered &&
    ordered.hp > 0 &&
    !ordered.constructing &&
    ((ordered.stock ?? 0) > 0 || ordered.kind === "farm")
  ) {
    e.order = { type: "gather", resourceId: ordered.id };
    e.path = undefined;
    return;
  }
  const want = orderedType || carried;
  const next = e.team
    ? pickOwnSource(state, e.team, want, e, { allowEmptyFarm: want === "food" })
    : null;
  e.order = next ? { type: "gather", resourceId: next.id } : { type: "idle" };
  e.path = undefined;
  e.browseCd = 8;
}

function nearestDrop(state: GameState, team: Team, resType: ResType, from: Entity): Entity | null {
  let best: Entity | null = null;
  let bestD = 1e9;
  for (const e of state.entities) {
    if (e.team !== team || e.hp <= 0 || e.constructing) continue;
    if (!DROP_SITES.has(e.kind)) continue;
    if (e.kind === "mill" && resType !== "food") continue;
    if (e.kind === "granary" && resType !== "food") continue;
    if (e.kind === "lumber_camp" && resType !== "wood") continue;
    if (e.kind === "mining_camp" && resType !== "gold" && resType !== "stone") continue;
    if (e.kind === "storage_pit" && resType === "food" && from.carry?.type !== "food") continue;
    if (e.kind === "dock" && resType !== "food") continue;
    const d = dist(from, e);
    if (d < bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

function isGatherNodeKind(k: string): boolean {
  return (
    k === "wood" ||
    k === "food" ||
    k === "gold" ||
    k === "stone" ||
    k === "fish" ||
    k === "farm" ||
    isFoodAnimal(k)
  );
}

function resTypeOfNode(kind: string): ResType | null {
  if (kind === "wood") return "wood";
  if (kind === "gold") return "gold";
  if (kind === "stone") return "stone";
  if (kind === "food" || kind === "fish" || kind === "farm" || isFoodAnimal(kind)) return "food";
  return null;
}

function rememberResource(state: GameState, team: Team, id: string) {
  if (!state.knownResources) state.knownResources = Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, {}])) as Record<Team, Record<string, 1>>;
  if (!state.knownResources[team]) state.knownResources[team] = {};
  state.knownResources[team][id] = 1;
}

/** Villagers / scouts spot resources in vision and share them with the whole team. */
function discoverVisibleResources(state: GameState) {
  ensureMeta(state);
  const nodes = (state.props?.length ? state.props : state.entities).filter(
    (node) =>
      node.hp > 0 &&
      isGatherNodeKind(node.kind) &&
      ((node.stock ?? 0) > 0 || node.kind === "farm")
  );
  if (!nodes.length) return;
  const teams = state.activeTeams?.length ? state.activeTeams : (EMPIRE_TEAMS as unknown as Team[]);
  for (const team of teams) {
    const eyes: Array<{ x: number; z: number; r2: number }> = [];
    for (const e of state.entities) {
      if (e.hp <= 0 || e.team !== team) continue;
      const r = visionRadius(e);
      eyes.push({ x: e.x, z: e.z, r2: r * r });
    }
    if (!eyes.length) continue;
    for (const node of nodes) {
      let seen = false;
      for (const eye of eyes) {
        const dx = eye.x - node.x;
        const dz = eye.z - node.z;
        if (dx * dx + dz * dz <= eye.r2) {
          seen = true;
          break;
        }
      }
      if (seen) rememberResource(state, team, node.id);
    }
  }
}

function countGatherersOn(
  state: GameState,
  team: Team,
  resourceId: string,
  exceptId?: string
): number {
  let n = 0;
  for (const u of state.entities) {
    if (u.hp <= 0 || u.team !== team || u.id === exceptId) continue;
    if (u.kind !== "worker" && u.kind !== "fishing_boat") continue;
    const o = u.order;
    if (!o) continue;
    if (
      (o.type === "gather" || o.type === "return") &&
      o.resourceId === resourceId
    ) {
      n += 1;
    }
  }
  return n;
}

/** Villagers gather around their own town. They do not walk off to another town's trees. */
function nearOwnTown(state: GameState, team: Team, x: number, z: number, radius = 34): boolean {
  let th: Entity | null = null;
  for (const e of state.entities) {
    if (e.kind === "town_hall" && e.team === team && e.hp > 0) {
      th = e;
      break;
    }
  }
  if (!th) return true;
  return Math.hypot(th.x - x, th.z - z) <= radius;
}

/**
 * Closest remembered source of a resource type.
 * Load-balances so each villager prefers its own node instead of stacking.
 */
function pickOwnSource(
  state: GameState,
  team: Team,
  resType: ResType,
  from: Entity,
  opts?: { preferId?: string; allowEmptyFarm?: boolean }
): Entity | null {
  ensureMeta(state);
  const known = state.knownResources?.[team] || {};
  let hall: Entity | null = null;
  for (const e of state.entities) {
    if (e.kind === "town_hall" && e.team === team && e.hp > 0) {
      hall = e;
      break;
    }
  }
  const home =
    from.kind === "worker" && hall
      ? (x: number, z: number) => {
          const dx = hall!.x - x;
          const dz = hall!.z - z;
          return dx * dx + dz * dz <= 34 * 34;
        }
      : () => true;
  let best: Entity | null = null;
  let bestScore = 1e9;

  // Prefer clicked/current id if still good
  if (opts?.preferId) {
    const pref = byId(state, opts.preferId);
    if (
      pref &&
      pref.hp > 0 &&
      resTypeOfNode(pref.kind) === resType &&
      ((pref.stock ?? 0) > 0 || (pref.kind === "farm" && opts.allowEmptyFarm))
    ) {
      rememberResource(state, team, pref.id);
      const load = countGatherersOn(state, team, pref.id, from.id);
      // Soft prefer — still allow closer free nodes to win
      // Explicit click wins. Load-balance only after that node is gone.
      const score = dist(from, pref) + load * 2 - 80;
      best = pref;
      bestScore = score;
    }
  }

  for (const id of Object.keys(known)) {
    const node = byId(state, id);
    if (!node || node.hp <= 0) continue;
    if (resTypeOfNode(node.kind) !== resType) continue;
    if ((node.stock ?? 0) <= 0) {
      if (!(node.kind === "farm" && opts?.allowEmptyFarm)) continue;
    }
    // Fishing boats only on fish; workers skip fish
    if (from.kind === "fishing_boat" && node.kind !== "fish") continue;
    if (from.kind === "worker" && node.kind === "fish") continue;
    if (!home(node.x, node.z)) continue;
    const load = countGatherersOn(state, team, node.id, from.id);
    const score = dist(from, node) + load * 8;
    if (score < bestScore) {
      bestScore = score;
      best = node;
    }
  }

  // Fallback: a living node of that type the team can already see.
  if (!best) {
    const eyes: Array<{ x: number; z: number; r2: number }> = [];
    const load = new Map<string, number>();
    for (const e of state.entities) {
      if (e.hp <= 0 || e.team !== team) continue;
      const r = visionRadius(e);
      eyes.push({ x: e.x, z: e.z, r2: r * r });
      const oid =
        e.order && (e.order.type === "gather" || e.order.type === "return")
          ? e.order.resourceId
          : "";
      if (oid && e.id !== from.id) load.set(oid, (load.get(oid) || 0) + 1);
    }
    const piles = state.props?.length ? state.props : state.entities;
    for (const node of piles) {
      if (node.hp <= 0 || resTypeOfNode(node.kind) !== resType) continue;
      if ((node.stock ?? 0) <= 0 && node.kind !== "farm") continue;
      if (from.kind === "fishing_boat" && node.kind !== "fish") continue;
      if (from.kind === "worker" && node.kind === "fish") continue;
      if (!home(node.x, node.z)) continue;
      let seen = false;
      for (const eye of eyes) {
        const dx = eye.x - node.x;
        const dz = eye.z - node.z;
        if (dx * dx + dz * dz <= eye.r2) {
          seen = true;
          break;
        }
      }
      if (!seen) continue;
      rememberResource(state, team, node.id);
      const score = dist(from, node) + (load.get(node.id) || 0) * 8;
      if (score < bestScore) {
        bestScore = score;
        best = node;
      }
    }
  }

  return best;
}

function farmStockCap(state: GameState, team: Team): number {
  return FARM_STOCK_CAP[state.meta[team]?.age || "dark"] || FARM_STOCK_CAP.dark;
}

function upgradeTeamFarms(state: GameState, team: Team) {
  const cap = farmStockCap(state, team);
  for (const e of state.entities) {
    if (e.kind !== "farm" || e.team !== team || e.hp <= 0) continue;
    if ((e.stock ?? 0) < cap * 0.35) e.stock = Math.floor(cap * 0.5);
    else if ((e.stock ?? 0) > cap) e.stock = cap;
  }
}

function shoreNear(state: GameState, x: number, z: number): boolean {
  // Dock must be on land adjacent to water
  if (isWaterAt(state, x, z)) return false;
  for (let ox = -2.5; ox <= 2.5; ox += 1) {
    for (let oz = -2.5; oz <= 2.5; oz += 1) {
      if (isWaterAt(state, x + ox, z + oz)) return true;
    }
  }
  return false;
}

function waterSpawnNearDock(state: GameState, dock: Entity): { x: number; z: number } | null {
  for (let r = 1.5; r <= 4; r += 0.5) {
    for (let a = 0; a < Math.PI * 2; a += Math.PI / 6) {
      const x = clampMap(dock.x + Math.cos(a) * r);
      const z = clampMap(dock.z + Math.sin(a) * r);
      if (isWaterAt(state, x, z)) return { x, z };
    }
  }
  return null;
}

function buildRadius(kind: string): number {
  if (kind === "town_hall" || kind === "keep") return 2.3;
  if (kind === "farm") return 1.75;
  if (kind === "dock" || kind === "barracks" || kind === "stable") return 1.6;
  if (kind === "house") return 1.35;
  if (isFortKind(kind)) return 0.55;
  return 1.45;
}

/** Land buildings need a dry pad above the lake, not a corner hanging in the water. */
function dryBuildSite(state: GameState, x: number, z: number, kind: string): boolean {
  const r = Math.min(2.4, buildRadius(kind));
  const samples = [
    [0, 0],
    [r, 0],
    [-r, 0],
    [0, r],
    [0, -r],
  ];
  for (const [ox, oz] of samples) {
    const sx = x + ox;
    const sz = z + oz;
    if (isWaterAt(state, sx, sz)) return false;
    if (terrainHeight(sx, sz, state.mapSeed, state.mapBiome) < WATERLINE + 0.22) return false;
    if (!isFortKind(kind) && isCliffFace(sx, sz, state.mapSeed, state.mapBiome)) return false;
  }
  if (!isFortKind(kind)) {
    let lo = 99;
    let hi = 0;
    for (const [ox, oz] of samples) {
      const h = terrainHeight(x + ox, z + oz, state.mapSeed, state.mapBiome);
      if (h < lo) lo = h;
      if (h > hi) hi = h;
    }
    const limit = kind === "farm" ? 0.42 : kind === "house" || kind === "mill" ? 0.55 : 0.7;
    if (hi - lo > limit) return false;
  }
  return true;
}

function footprintBlocked(state: GameState, x: number, z: number, kind: string): boolean {
  const r = buildRadius(kind);
  for (const e of state.entities) {
    if (e.hp <= 0 || !isBuildingKind(e.kind)) continue;
    const need = r + buildRadius(e.kind) * 0.72;
    if (dist(e, { x, z }) < need) return true;
  }
  return false;
}

/** Nearest legal cell. Docks snap to shoreline; other buildings snap off water and off footprints. */
function snapBuildPoint(
  state: GameState,
  building: string,
  x: number,
  z: number
): { x: number; z: number } | null {
  for (let ring = 0; ring <= 6; ring++) {
    const steps = ring === 0 ? 1 : 16;
    let best: { x: number; z: number; d: number } | null = null;
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * Math.PI * 2;
      const px = clampMap(x + Math.cos(a) * ring);
      const pz = clampMap(z + Math.sin(a) * ring);
      const ok =
        building === "dock"
          ? shoreNear(state, px, pz)
          : dryBuildSite(state, px, pz, building);
      if (!ok || footprintBlocked(state, px, pz, building)) continue;
      const d = dist({ x, z }, { x: px, z: pz });
      if (!best || d < best.d) best = { x: px, z: pz, d };
    }
    if (best) return { x: best.x, z: best.z };
  }
  return null;
}

function placeFoundation(
  state: GameState,
  team: Team,
  building: BuildKind,
  x: number,
  z: number
): Entity {
  let maxHp = BUILD_HP[building];
  if (isFortKind(building) || building === "watch_tower") {
    maxHp += state.meta[team].wallHpBonus;
    maxHp = Math.round(maxHp * (teamCiv(state, team).wallHp || 1));
  }
  const site: Entity = {
    id: nid(building.slice(0, 3)),
    kind: building,
    team,
    x,
    z,
    hp: Math.max(8, Math.floor(maxHp * 0.08)),
    maxHp,
    train: null,
    constructing: true,
    stock:
      building === "farm"
        ? farmStockCap(state, team) + (teamCiv(state, team).farm || 0)
        : undefined,
  };
  state.entities.push(site);
  return site;
}

function buildersFor(
  state: GameState,
  team: Team,
  ids: string[],
  spot: { x: number; z: number }
): Entity[] {
  const workers: Entity[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const u = byId(state, id);
    if (u && u.team === team && u.kind === "worker" && u.hp > 0) workers.push(u);
  }
  if (workers.length) return workers;
  return state.entities
    .filter((e) => e.team === team && e.kind === "worker" && e.hp > 0)
    .sort((a, b) => dist(a, spot) - dist(b, spot))
    .slice(0, 4);
}

export function applyCommand(state: GameState, team: Team, cmd: Command): string | null {
  ensureMeta(state);
  if (state.winner) return "Game over";
  if (!state.started) return "Waiting for both players";

  if (cmd.type === "rewind") return "Use server rewind handler";

  if (cmd.type === "board") {
    const ship = byId(state, cmd.shipId);
    if (!ship || ship.team !== team || ship.kind !== "transport") return "Need a Cannon Ferry";
    if (!ship.cargo) ship.cargo = [];
    let loaded = 0;
    for (const id of cmd.unitIds.slice(0, TRANSPORT_CAPACITY)) {
      if (ship.cargo.length >= TRANSPORT_CAPACITY) break;
      const u = byId(state, id);
      if (!u || u.team !== team) continue;
      if (!isLandUnit(u.kind) || u.aboardShipId) continue;
      if (dist(u, ship) > 4.5) continue;
      ship.cargo.push(u.id);
      u.aboardShipId = ship.id;
      u.order = { type: "idle" };
      u.path = undefined;
      loaded += 1;
    }
    return loaded ? null : "No units in range to board";
  }

  if (cmd.type === "unload") {
    const ship = byId(state, cmd.shipId);
    if (!ship || ship.team !== team || ship.kind !== "transport") return "Need a Cannon Ferry";
    if (!ship.cargo?.length) return "Ferry is empty";
    // Must be near shore / island land
    const tx = clampMap(cmd.x);
    const tz = clampMap(cmd.z);
    if (isWaterAt(state, tx, tz) && !shoreNear(state, tx, tz)) {
      // try nearby land
      let found = false;
      for (let r = 1; r <= 4 && !found; r++) {
        for (let a = 0; a < Math.PI * 2; a += Math.PI / 4) {
          const lx = clampMap(tx + Math.cos(a) * r);
          const lz = clampMap(tz + Math.sin(a) * r);
          if (!isWaterAt(state, lx, lz)) {
            found = true;
            break;
          }
        }
      }
      if (!found) return "Unload on shore or island";
    }
    if (dist(ship, { x: tx, z: tz }) > 6) return "Move ferry closer to shore";
    const ids = [...ship.cargo];
    ship.cargo = [];
    let i = 0;
    for (const id of ids) {
      const u = byId(state, id);
      if (!u) continue;
      u.aboardShipId = undefined;
      const ang = (i / Math.max(1, ids.length)) * Math.PI * 2;
      let ux = clampMap(tx + Math.cos(ang) * 1.2);
      let uz = clampMap(tz + Math.sin(ang) * 1.2);
      if (isWaterAt(state, ux, uz)) {
        ux = tx;
        uz = tz;
      }
      u.x = ux;
      u.z = uz;
      u.order = { type: "idle" };
      i += 1;
    }
    return null;
  }

  if (cmd.type === "toggle_gate") {
    const g = byId(state, cmd.gateId);
    if (!g || g.team !== team || g.kind !== "gate") return "Not your gate";
    const mode = cmd.mode || (g.gateState === "locked" ? "closed" : "locked");
    g.gateState = mode;
    return null;
  }

  if (cmd.type === "destroy_fort") {
    const e = byId(state, cmd.targetId);
    if (!e || e.team !== team) return "Not your fortification";
    const victims: Entity[] = [];
    if (cmd.nearby) {
      for (const g of state.entities) {
        if (g.team !== team || g.hp <= 0 || g.kind !== "gate") continue;
        if (dist(g, e) <= 8) victims.push(g);
        if (victims.length >= 80) break;
      }
      if (!victims.length) return "No gates here";
    } else {
      if (!isFortKind(e.kind)) return "Not a wall or gate";
      victims.push(e);
    }
    const doomed = new Set<string>();
    for (const fort of victims) {
      const cost = costOf(fort.kind);
      const ratio = fort.constructing ? 1 : 0.5;
      const back: Cost = {};
      if (cost.wood) back.wood = Math.floor(cost.wood * ratio);
      if (cost.stone) back.stone = Math.floor(cost.stone * ratio);
      if (cost.food) back.food = Math.floor(cost.food * ratio);
      if (cost.gold) back.gold = Math.floor(cost.gold * ratio);
      refund(state.eco[team], back);
      fort.hp = 0;
      fort.constructing = false;
      doomed.add(fort.id);
    }
    for (const u of state.entities) {
      const ord = u.order;
      if (!ord || ord.type !== "build" || u.team !== team) continue;
      if (!isFortKind(ord.building)) continue;
      const hits = victims.some(
        (fort) =>
          Math.round(fort.x) === Math.round(ord.tx) &&
          Math.round(fort.z) === Math.round(ord.tz)
      );
      if (!hits) continue;
      const next = nextUnfinishedFort(state, team, u);
      if (next) {
        u.order = {
          type: "build",
          building: next.kind as BuildKind,
          tx: next.x,
          tz: next.z,
        };
      } else {
        u.order = { type: "idle" };
      }
    }
    state.entities = state.entities.filter((ent) => !doomed.has(ent.id));
    return null;
  }


  if (cmd.type === "market_buy" || cmd.type === "market_sell") {
    if (!teamHasMarket(state, team)) return "Need a Market";
    const good = cmd.good;
    if (!MARKET_GOOD_SET.has(good)) return "Unknown commodity";
    ensureMarketPrices(state);
    const quote = state.marketPrices![good];
    const eco = state.eco[team];
    if (cmd.type === "market_buy") {
      const goldCost = quote.buy;
      if (eco.gold < goldCost) return "Not enough gold";
      eco.gold -= goldCost;
      eco[good] += MARKET_LOT;
      shiftMarketPrices(state, good, "buy");
      return null;
    }
    if (eco[good] < MARKET_LOT) return `Need ${MARKET_LOT} ${good}`;
    eco[good] -= MARKET_LOT;
    eco.gold += quote.sell;
    shiftMarketPrices(state, good, "sell");
    pushEvent(state, { kind: "info", team, text: `Sold ${MARKET_LOT} ${good} for ${quote.sell} gold` });
    return null;
  }

  if (cmd.type === "market_swap") {
    if (!teamHasMarket(state, team)) return "Need a Market";
    const give = cmd.give;
    const take = cmd.take;
    if (give === take) return "Pick two different resources";
    const stocks = new Set(["wood", "food", "gold", "stone"]);
    if (!stocks.has(give) || !stocks.has(take)) return "Unknown resource";
    ensureMarketPrices(state);
    const prices = state.marketPrices!;
    const pay = 100;
    const goldIn = give === "gold" ? pay : prices[give].sell;
    const recv = take === "gold" ? goldIn : Math.floor((goldIn * MARKET_LOT) / prices[take].buy);
    if (recv < 1) return "Rate is too poor for that swap";
    const eco = state.eco[team];
    if ((eco[give] || 0) < pay) return `Need ${pay} ${give}`;
    eco[give] -= pay;
    eco[take] = (eco[take] || 0) + recv;
    if (give !== "gold") shiftMarketPrices(state, give, "sell");
    if (take !== "gold") shiftMarketPrices(state, take, "buy");
    pushEvent(state, {
      kind: "info",
      team,
      text: `Traded ${pay} ${give} for ${recv} ${take}. Gold is the coin.`,
    });
    return null;
  }

  if (cmd.type === "trade_route") {
    if (!teamHasMarket(state, team)) return "Need a Market";
    const home = state.entities.find(
      (e) => e.kind === "market" && e.team === team && e.hp > 0 && !e.constructing
    );
    const allies = state.entities.filter(
      (e) =>
        e.hp > 0 &&
        !e.constructing &&
        e.team &&
        e.team !== team &&
        areAllied(state, team, e.team) &&
        (e.kind === "market" || e.kind === "town_hall")
    );
    const dest = allies.find((e) => e.kind === "market") || allies.find((e) => e.kind === "town_hall");
    if (!home || !dest) return "No allied market or town";
    let cart = state.entities.find((e) => e.kind === "trade_cart" && e.team === team && e.hp > 0);
    if (!cart) {
      cart = {
        id: nid("trd"),
        kind: "trade_cart",
        team,
        x: home.x + 1.2,
        z: home.z + 1.2,
        hp: 60,
        maxHp: 60,
        order: { type: "move", tx: dest.x, tz: dest.z },
        tradeTargetId: dest.id,
        facing: 0,
      };
      state.entities.push(cart);
    } else {
      cart.tradeTargetId = dest.id;
      cart.order = { type: "move", tx: dest.x, tz: dest.z };
    }
    pushEvent(state, { kind: "info", team, text: "Trade cart is on the road to your ally" });
    return null;
  }

  if (cmd.type === "stop") {
    for (const id of cmd.unitIds) {
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0) continue;
      u.order = { type: "idle" };
      u.waypoints = undefined;
      u.path = undefined;
    }
    return null;
  }

  if (cmd.type === "stance") {
    for (const id of cmd.unitIds) {
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0 || !isWarrior(u.kind)) continue;
      u.stance = cmd.stance;
      if (cmd.stance === "stand" && u.order?.type === "attack") {
        u.order = { type: "idle" };
        u.path = undefined;
      }
    }
    return null;
  }

  if (cmd.type === "waypoint") {
    const x = clampMap(cmd.x);
    const z = clampMap(cmd.z);
    for (const id of cmd.unitIds) {
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0 || !isControllableUnit(u.kind)) continue;
      const next = { x, z };
      if (!u.order || u.order.type === "idle") {
        u.order = { type: "move", tx: next.x, tz: next.z };
        u.path = undefined;
      } else {
        u.waypoints = [...(u.waypoints || []), next].slice(0, 12);
      }
    }
    return null;
  }

  if (cmd.type === "repair") {
    const b = byId(state, cmd.targetId);
    if (!b || b.hp <= 0) return "Nothing to repair";
    if (b.team !== team) return "Not yours to repair";
    if (!isBuildingKind(b.kind) && !isNavalUnit(b.kind)) return "Villagers repair buildings and boats";
    let any = false;
    for (const id of cmd.unitIds) {
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0 || u.kind !== "worker") continue;
      u.order = { type: "repair", targetId: b.id };
      u.path = undefined;
      u.waypoints = undefined;
      any = true;
    }
    return any ? null : "Need a villager";
  }

  if (cmd.type === "convert") {
    if (!attacksAllowed(state)) return "Peace holds. No conversions for the first 25 minutes.";
    const t = byId(state, cmd.targetId);
    if (!t || t.hp <= 0) return "Nothing to convert";
    if (t.kind === "town_hall" || t.kind === "explorer") return "That cannot be converted";
    if (t.team && !isHostile(state, team, t.team)) return "That is an ally";
    let any = false;
    for (const id of cmd.unitIds) {
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0 || u.kind !== "healer") continue;
      if ((u.convertCd || 0) > 0) continue;
      u.order = { type: "convert", targetId: t.id };
      u.path = undefined;
      u.convertProg = 0;
      any = true;
    }
    return any ? null : "Need a priest who is ready";
  }

  if (cmd.type === "patrol") {
    const x = clampMap(cmd.x);
    const z = clampMap(cmd.z);
    for (const id of cmd.unitIds) {
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0 || !isWarrior(u.kind)) continue;
      u.order = { type: "patrol", ax: u.x, az: u.z, bx: x, bz: z, leg: 1 };
      u.path = undefined;
      u.waypoints = undefined;
    }
    return null;
  }

  if (cmd.type === "rally") {
    const b = byId(state, cmd.buildingId);
    if (!b || b.team !== team || b.hp <= 0) return "Not your building";
    if (!b.train && b.kind !== "town_hall" && b.kind !== "barracks" && b.kind !== "archery_range" && b.kind !== "stable" && b.kind !== "dock" && b.kind !== "siege_workshop") {
      return "That building does not train units";
    }
    b.rallyX = clampMap(cmd.x);
    b.rallyZ = clampMap(cmd.z);
    return null;
  }

  if (cmd.type === "tribute") {
    if (!isTeam(cmd.to) || cmd.to === team) return "Pick another town";
    if (!teamsInPlay(state).includes(cmd.to)) return "That town is gone";
    const good = cmd.good;
    if (good !== "wood" && good !== "food" && good !== "gold" && good !== "stone") return "Unknown good";
    const gift = Math.max(0, Math.min(500, Math.floor(cmd.amount)));
    if (gift < 1) return "Nothing to send";
    const fee = Math.ceil(gift * 0.25);
    const cost = gift + fee;
    if ((state.eco[team][good] || 0) < cost) return `Need ${cost} ${good} (gift plus a quarter fee)`;
    state.eco[team][good] -= cost;
    state.eco[cmd.to][good] = (state.eco[cmd.to][good] || 0) + gift;
    pushEvent(state, {
      kind: "info",
      team,
      text: `Sent ${gift} ${good} as tribute. The road took ${fee}.`,
    });
    return null;
  }

  if (cmd.type === "delete") {
    const t = byId(state, cmd.targetId);
    if (!t || t.team !== team || t.hp <= 0) return "Not yours";
    if (t.kind === "town_hall") return "The town hall stays";
    t.hp = 0;
    return null;
  }

  if (cmd.type === "diplomacy") {
    if (!isTeam(cmd.other) || cmd.other === team) return "Pick another town";
    if (!state.diplomacy) state.diplomacy = {};
    if (!state.diplomacy[team]) state.diplomacy[team] = {};
    state.diplomacy[team]![cmd.other] = cmd.stance;
    pushEvent(state, {
      kind: "info",
      team,
      text: `${TEAM_LABELS[cmd.other] || cmd.other} is now ${cmd.stance}.`,
    });
    return null;
  }

  if (cmd.type === "garrison") {
    const b = byId(state, cmd.buildingId);
    if (!b || b.team !== team || b.hp <= 0) return "Not your building";
    const cap = b.kind === "town_hall" ? 10 : b.kind === "watch_tower" || b.kind === "keep" ? 5 : 0;
    if (!cap) return "Only the town hall and towers take people inside";
    let held = state.entities.filter((e) => e.insideId === b.id && e.hp > 0).length;
    let any = false;
    for (const id of cmd.unitIds) {
      if (held >= cap) break;
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0 || !isLandUnit(u.kind)) continue;
      if (u.kind === "explorer") continue;
      u.insideId = b.id;
      u.order = { type: "idle" };
      u.path = undefined;
      u.x = b.x;
      u.z = b.z;
      held += 1;
      any = true;
    }
    return any ? null : "Nobody can go inside";
  }

  if (cmd.type === "ungarrison") {
    const b = byId(state, cmd.buildingId);
    if (!b || b.team !== team) return "Not your building";
    let n = 0;
    for (const u of state.entities) {
      if (u.insideId !== b.id || u.hp <= 0) continue;
      u.insideId = undefined;
      u.x = clampMap(b.x + Math.cos(n) * 2.4);
      u.z = clampMap(b.z + Math.sin(n) * 2.4);
      u.order = { type: "idle" };
      n += 1.2;
    }
    return n ? null : "Nobody is inside";
  }

  if (cmd.type === "attack_ground") {
    if (!attacksAllowed(state)) return "Peace holds. No attacks for the first 25 minutes.";
    const x = clampMap(cmd.x);
    const z = clampMap(cmd.z);
    let any = false;
    for (const id of cmd.unitIds) {
      const u = byId(state, id);
      if (!u || u.team !== team || u.hp <= 0) continue;
      if (u.kind !== "mangonel" && u.kind !== "scorpion" && u.kind !== "trebuchet" && u.kind !== "ram") continue;
      u.order = { type: "attack_ground", tx: x, tz: z };
      u.path = undefined;
      any = true;
    }
    return any ? null : "Need a siege engine";
  }

  if (cmd.type === "research") {
    const b = byId(state, cmd.buildingId);
    if (!b || b.team !== team) return "Not your building";
    if (b.constructing) return "Still building";
    if (b.train || b.research) return "Busy";
    if (!TECH_SET.has(cmd.tech)) return "Unknown tech";
    const homes = researchBuildingFor(cmd.tech);
    if (!homes.includes(b.kind)) return "Wrong building";
    const meta = state.meta[team];
    if (cmd.tech === "age_feudal") {
      if (meta.age !== "dark") return "Already past Dark Age";
    } else if (cmd.tech === "age_castle") {
      if (meta.age !== "feudal") return "Need Feudal Age first";
    } else if (cmd.tech === "age_imperial") {
      if (meta.age !== "castle") return "Need Castle Age first";
    } else if (cmd.tech === "bs_attack") {
      if (!ageOk(state, team, "feudal")) return "Need Feudal Age";
      if (meta.atkBonus >= 2) return "Max attack upgrades";
    } else if (cmd.tech === "bs_armor") {
      if (!ageOk(state, team, "feudal")) return "Need Feudal Age";
      if (meta.armorBonus >= 2) return "Max armor upgrades";
    } else if (cmd.tech === "uni_masonry") {
      if (!ageOk(state, team, "castle")) return "Need Castle Age";
      if (meta.masonry) return "Already researched";
    } else if (cmd.tech === "uni_ballistics") {
      if (!ageOk(state, team, "castle")) return "Need Castle Age";
      if (meta.ballistics) return "Already researched";
    }
    const cost = costOf(cmd.tech);
    if (!canAfford(state.eco[team], cost)) return "Not enough resources";
    pay(state.eco[team], cost);
    b.research = { tech: cmd.tech, left: RESEARCH_TICKS[cmd.tech] };
    return null;
  }

  if (cmd.type === "train") {
    const b = byId(state, cmd.buildingId);
    if (!b || b.team !== team) return "Not your building";
    if (b.constructing) return "Still building";
    if (b.train || b.research) return "Busy";
    if (!TRAIN_SET.has(cmd.unit)) return "Unknown unit";
    const homes = trainBuildingFor(cmd.unit);
    if (!homes.includes(b.kind)) return "Cannot train here";
    if (!ageOk(state, team, UNIT_AGE[cmd.unit])) return `Need ${AGE_LABELS[UNIT_AGE[cmd.unit]]}`;
    recountPop(state);
    if (state.meta[team].pop >= state.meta[team].popCap) return "Population cap — build another house";
    const cost = { ...costOf(cmd.unit) };
    if (cmd.unit === "worker") {
      const cheaper = teamCiv(state, team).villagerCost;
      if (cheaper) cost.food = cheaper;
    }
    if (!canAfford(state.eco[team], cost)) return "Not enough resources";
    pay(state.eco[team], cost);
    b.train = { unit: cmd.unit, left: TRAIN_TICKS[cmd.unit] };
    return null;
  }

  if (cmd.type === "build") {
    if (!BUILD_KINDS_SET.has(cmd.building)) return "Unknown building";
    const building = cmd.building as BuildKind;
    if (!ageOk(state, team, BUILD_AGE[building])) {
      return `Need ${AGE_LABELS[BUILD_AGE[building]]}`;
    }
    const tx = clampMap(cmd.x);
    const tz = clampMap(cmd.z);
    const asked = [...(cmd.unitIds || []), cmd.unitId].filter(Boolean);

    // Fortifications: snap to grid, drag line (H/V/45°/L or multi-corner path)
    if (isFortKind(building)) {
      const pathPts = Array.isArray(cmd.path)
        ? cmd.path
            .filter(
              (p) =>
                p &&
                typeof p.x === "number" &&
                Number.isFinite(p.x) &&
                typeof p.z === "number" &&
                Number.isFinite(p.z)
            )
            .slice(0, 24)
            .map((p) => ({ x: Math.round(p.x), z: Math.round(p.z) }))
        : [];
      const hasEnd =
        typeof cmd.x1 === "number" &&
        Number.isFinite(cmd.x1) &&
        typeof cmd.z1 === "number" &&
        Number.isFinite(cmd.z1);
      let cells: FortPoint[];
      if (pathPts.length >= 2) {
        cells = fortCellsAlongCorners(pathPts, FORT_LINE_MAX);
      } else if (hasEnd) {
        cells = fortCellsAlong(tx, tz, cmd.x1!, cmd.z1!, FORT_LINE_MAX);
      } else {
        cells = [{ x: Math.round(tx), z: Math.round(tz) }];
      }
      const gateKeys = new Set(
        (Array.isArray(cmd.gates) ? cmd.gates : [])
          .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.z))
          .map((p) => `${Math.round(p.x)},${Math.round(p.z)}`)
      );
      // Gate: single gatehouse (midpoint of a drag line / gap)
      const placeCells =
        building === "gate"
          ? [
              cells.length > 1
                ? cells[Math.floor(cells.length / 2)]
                : cells[0] || { x: Math.round(tx), z: Math.round(tz) },
            ]
          : cells;
      const crew = buildersFor(state, team, asked, { x: tx, z: tz });
      if (!crew.length) return "Need a villager";
      let placed = 0;
      let first: { x: number; z: number } | null = null;
      for (const cell of placeCells) {
        const px = clampMap(cell.x);
        const pz = clampMap(cell.z);
        const gx = Math.round(px);
        const gz = Math.round(pz);
        if (isWaterAt(state, gx, gz)) continue;
        if (fortCellTaken(state, gx, gz)) continue;
        const placeKind: FortBuildKind =
          building !== "gate" && gateKeys.has(`${gx},${gz}`)
            ? "gate"
            : (building as FortBuildKind);
        if (placeKind === "gate" && gateSpotBlocked(state, gx, gz)) continue;
        if (!ageOk(state, team, BUILD_AGE[placeKind])) continue;
        const cost = costOf(placeKind);
        if (!canAfford(state.eco[team], cost)) {
          if (placed === 0) return "Not enough resources";
          break;
        }
        pay(state.eco[team], cost);
        spawnFortSite(state, team, placeKind, gx, gz);
        if (!first) first = { x: gx, z: gz };
        placed += 1;
      }
      if (!placed || !first) return "Cannot place fortification here";
      const placedKeys = new Set(
        placeCells.map((c) => `${Math.round(c.x)},${Math.round(c.z)}`)
      );
      const sites = state.entities.filter(
        (e) =>
          e.team === team &&
          e.constructing &&
          e.hp > 0 &&
          isFortKind(e.kind) &&
          placedKeys.has(`${Math.round(e.x)},${Math.round(e.z)}`)
      );
      crew.forEach((u, i) => {
        let best = sites[0];
        let bestD = Infinity;
        const step = Math.max(1, Math.floor(sites.length / crew.length));
        const prefer = sites[Math.min(sites.length - 1, i * step)] || null;
        for (const s of sites) {
          const d = dist(u, s) + (prefer && s.id === prefer.id ? -0.4 : 0);
          if (d < bestD) {
            bestD = d;
            best = s;
          }
        }
        if (!best) return;
        u.order = {
          type: "build",
          building: best.kind as BuildKind,
          tx: best.x,
          tz: best.z,
        };
        u.path = undefined;
      });
      return null;
    }

    const spot = snapBuildPoint(state, building, tx, tz);
    if (!spot) {
      return building === "dock" ? "Dock needs shoreline" : "Cannot build here";
    }
    const existing = state.entities.find(
      (b) =>
        b.team === team &&
        b.kind === building &&
        b.constructing &&
        b.hp > 0 &&
        dist(b, spot) < 1.15
    );
    const crew = buildersFor(state, team, asked, spot);
    if (!crew.length) return "Need a villager";
    if (!existing) {
      const cost = costOf(building);
      if (!canAfford(state.eco[team], cost)) return "Not enough resources";
      pay(state.eco[team], cost);
      placeFoundation(state, team, building, spot.x, spot.z);
    }
    for (const u of crew) {
      u.order = { type: "build", building, tx: spot.x, tz: spot.z };
      u.path = undefined;
    }
    return null;
  }

  const ids = "unitIds" in cmd ? cmd.unitIds : [];
  const units = ids
    .map((id) => byId(state, id))
    .filter((e): e is Entity => !!e && e.team === team && isControllableUnit(e.kind));

  // Divide Work: empty selection = all team villagers (supports 2…12+).
  // Optional people cap takes the first N of the selection (stable by id).
  if (cmd.type === "divide_work") {
    const tasks = [...new Set(cmd.tasks.filter((t) => DIVIDE_TASK_SET.has(t)))];
    if (!tasks.length) return "Pick at least one task";
    let workers = (
      units.length
        ? units.filter((u) => u.kind === "worker" && u.hp > 0)
        : state.entities.filter(
            (e) => e.team === team && e.kind === "worker" && e.hp > 0
          )
    ).sort((a, b) => (a.id < b.id ? -1 : 1));
    const people =
      typeof cmd.people === "number" && Number.isFinite(cmd.people)
        ? Math.max(1, Math.min(80, Math.floor(cmd.people)))
        : 0;
    if (people > 0) workers = workers.slice(0, people);
    if (!workers.length) return "Need villagers";
    discoverVisibleResources(state);
    // Round-robin primary task so 12 villagers across 4 tasks → 3 each
    let assigned = 0;
    for (let i = 0; i < workers.length; i++) {
      const w = workers[i];
      let placed = false;
      // Prefer this villager's slot task, then rotate through the rest
      for (let attempt = 0; attempt < tasks.length; attempt++) {
        const task = tasks[(i + attempt) % tasks.length];
        const node = pickOwnSource(state, team, task, w, {
          allowEmptyFarm: task === "food",
        });
        if (node) {
          w.order = { type: "gather", resourceId: node.id };
          w.path = undefined;
          assigned += 1;
          placed = true;
          break;
        }
      }
      // Last resort: any of the chosen tasks anywhere on the map (known/visible)
      if (!placed) {
        for (const task of tasks) {
          const node = pickOwnSource(state, team, task, w, {
            allowEmptyFarm: task === "food",
          });
          if (node) {
            w.order = { type: "gather", resourceId: node.id };
          w.path = undefined;
            assigned += 1;
            placed = true;
            break;
          }
        }
      }
      if (!placed) w.order = { type: "idle" };
    }
    if (!assigned) return "No remembered sources for those tasks — scout first";
    return null;
  }

  if (units.length === 0) return "No valid units";

  if (cmd.type === "move" || cmd.type === "attack_move") {
    const tx = clampMap(cmd.x);
    const tz = clampMap(cmd.z);
    // Soft formation fan — units still pass through each other on the way
    const sorted = [...units].sort((a, b) => (a.id < b.id ? -1 : 1));
    sorted.forEach((u, i) => {
      const naval = isNavalUnit(u.kind);
      const col = i % 4;
      const row = Math.floor(i / 4);
      const ox = (col - 1.5) * 0.85;
      const oz = row * 0.85;
      let ax = clampMap(tx + ox);
      let az = clampMap(tz + oz);
      const spot = nearestSurface(state, ax, az, naval) || nearestSurface(state, tx, tz, naval);
      if (!spot) return;
      ax = spot.x;
      az = spot.z;
      const fight = cmd.type === "attack_move" && attacksAllowed(state) && isWarrior(u.kind);
      u.order = fight
        ? { type: "attack_move", tx: ax, tz: az }
        : { type: "move", tx: ax, tz: az, ...(u.kind === "explorer" ? { manual: true } : {}) };
      u.path = undefined;
      u.waypoints = undefined;
    });
    return null;
  }

  if (cmd.type === "gather") {
    const res = byId(state, cmd.resourceId);
    if (!res) return "Invalid resource";
    const rk = res.kind;
    // Oil / gas seeps: send villagers or geologists to drill a well
    if (rk === "oil_seep" || rk === "gas_seep") {
      let any = false;
      for (const u of units) {
        if (u.kind !== "worker" && u.kind !== "geologist") continue;
        u.order = { type: "move", tx: res.x, tz: res.z };
        any = true;
      }
      return any ? null : "Need a villager or geologist";
    }
    if (
      rk !== "wood" &&
      rk !== "food" &&
      rk !== "gold" &&
      rk !== "stone" &&
      rk !== "fish" &&
      rk !== "farm" &&
      !isFoodAnimal(rk)
    ) {
      return "Invalid resource";
    }
    if (isPredator(rk)) return "Cannot gather predators";
    const rtype = resTypeOfNode(rk);
    if (!rtype) return "Invalid resource";
    rememberResource(state, team, res.id);
    for (const u of units) {
      if (rk === "fish") {
        if (u.kind !== "fishing_boat") continue;
        const mine = pickOwnSource(state, team, "food", u, {
          preferId: res.id,
          allowEmptyFarm: false,
        });
        u.order = { type: "gather", resourceId: (mine || res).id };
        u.path = undefined;
      } else if (u.kind === "worker") {
        // Each villager takes the closest remembered node of that type
        const mine = pickOwnSource(state, team, rtype, u, {
          preferId: res.id,
          allowEmptyFarm: rtype === "food",
        });
        u.order = { type: "gather", resourceId: (mine || res).id };
        u.path = undefined;
      }
    }
    return null;
  }

  if (cmd.type === "attack") {
    const t = byId(state, cmd.targetId);
    if (!t || t.hp <= 0) return "Invalid target";
    if (t.kind === "explorer") return "The explorer cannot be attacked";
    if (t.team && !isHostile(state, team, t.team)) return "That is an ally";
    if (!attacksAllowed(state)) return "Peace holds. No attacks for the first 25 minutes.";
    let any = false;
    for (const u of units) {
      if (!isWarrior(u.kind)) continue;
      u.order = { type: "attack", targetId: t.id };
      any = true;
    }
    return any ? null : "Only warriors attack. Villagers and the explorer do not fight.";
  }

  if (cmd.type === "heal") {
    const t = byId(state, cmd.targetId);
    if (!t || t.hp <= 0 || !t.team) return "Nothing to heal";
    if (!areAllied(state, team, t.team) || !isControllableUnit(t.kind)) return "Can only heal your people";
    let any = false;
    for (const u of units) {
      if (u.kind !== "healer") continue;
      u.order = { type: "heal", targetId: t.id };
      u.path = undefined;
      any = true;
    }
    return any ? null : "Need a healer from the Church";
  }

  if (cmd.type === "capture") {
    const relic = byId(state, cmd.relicId);
    if (!relic || relic.kind !== "chrono") return "No Chrono Vault there";
    for (const u of units) {
      if (!isLandUnit(u.kind)) continue;
      u.order = { type: "capture", relicId: relic.id };
    }
    return null;
  }

  return "Unknown command";
}

function isCavKind(k: string): boolean {
  return k === "scout_cav" || k === "knight" || k === "camel_rider";
}
function isArcherKind(k: string): boolean {
  return k === "archer" || k === "skirmisher" || k === "crossbowman" || k === "hand_cannon";
}
function isInfKind(k: string): boolean {
  return k === "militia" || k === "spearman" || k === "man_at_arms" || k === "worker";
}

function unitDmg(state: GameState, e: Entity, target: Entity): number {
  const st = UNIT_STATS[e.kind as TrainUnit];
  if (!st) return 3;
  let dmg = st.dmg;
  if (e.team) dmg += state.meta[e.team].atkBonus;
  // Rock-paper-scissors counter matrix
  if (st.vsCav && isCavKind(target.kind)) dmg += st.vsCav;
  if (st.vsArcher && isArcherKind(target.kind)) dmg += st.vsArcher;
  if (st.vsInf && isInfKind(target.kind)) dmg += st.vsInf;
  if (e.kind === "knight" && isArcherKind(target.kind)) dmg += 6;
  if (e.kind === "scout_cav" && target.kind === "worker") dmg += 4;
  if (st.siege && isBuildingKind(target.kind)) {
    dmg += e.kind === "trebuchet" ? 35 : e.kind === "ram" ? 22 : 18;
  }
  if (target.kind === "wall" || target.kind === "gate" || target.kind === "keep") {
    if (st.siege) dmg += e.kind === "trebuchet" ? 25 : 10;
    else dmg *= 0.35;
  }
  if (isCavKind(e.kind) && (e.charge || 0) > 0.55) {
    dmg = Math.floor(dmg * 1.4);
    e.charge = 0;
  }
  if (
    terrainHeight(e.x, e.z, state.mapSeed, state.mapBiome) -
      terrainHeight(target.x, target.z, state.mapSeed, state.mapBiome) >
    0.85
  ) {
    dmg = Math.floor(dmg * 1.18);
  }
  const armor =
    (UNIT_STATS[target.kind as TrainUnit]?.armor || 0) +
    (target.team ? state.meta[target.team].armorBonus : 0);
  return Math.max(1, Math.floor(dmg - armor * 0.5));
}

function unitRange(state: GameState, e: Entity): number {
  const st = UNIT_STATS[e.kind as TrainUnit];
  if (!st) return ATTACK_RANGE_MELEE;
  let r = st.range || ATTACK_RANGE_MELEE;
  if (e.team && (e.kind === "archer" || e.kind === "skirmisher" || e.kind === "crossbowman" || e.kind === "hand_cannon" || e.kind === "war_galley" || e.kind === "mangonel" || e.kind === "scorpion" || e.kind === "trebuchet")) {
    r += state.meta[e.team].rangeBonus;
  }
  return r;
}

/**
 * Base unit speed. Villagers scale by trip length:
 * short hops = stroll, long runs to wood/mines/hall = hustle.
 */
function unitSpeed(state: GameState, e: Entity, tx?: number, tz?: number): number {
  if (e.kind === "explorer") return 4.2;
  let base = UNIT_STATS[e.kind as TrainUnit]?.speed || 2.4;
  if (e.kind === "worker" && e.team) base *= teamCiv(state, e.team).walk || 1;
  if (e.kind === "war_elephant" && e.team && teamCiv(state, e.team).id === "persia") base *= 1.2;
  if (e.kind !== "worker" || tx == null || tz == null) return base;
  const d = dist(e, { x: tx, z: tz });
  const t = Math.max(0, Math.min(1, (d - 2.5) / 15.5));
  return base * (1 + t * 0.35);
}

function captureRelic(state: GameState, team: Team, relic: Entity) {
  const tier = (relic.chronoTier || "D") as ChronoTier;
  const def = CHRONO_DEFS[tier];
  relic.hp = 0;
  state.chronoCharges[team] = {
    tier,
    name: def.name,
    rewindSec: def.rewindSec,
    relicId: relic.id,
    capturedAt: Date.now(),
  };
  pushEvent(state, {
    kind: "chrono_found",
    team,
    text: `${TEAM_LABELS[team] || team} recovered ${def.name} (${Math.round(def.rewindSec / 60)} min rewind)`,
    rewindSec: def.rewindSec,
    vaultName: def.name,
  });
}

function finishResearch(state: GameState, team: Team, tech: TechId) {
  const meta = state.meta[team];
  if (tech === "age_feudal") {
    meta.age = "feudal";
    upgradeTeamFarms(state, team);
    pushEvent(state, {
      kind: "age_up",
      team,
      text: `${TEAM_LABELS[team] || team} advanced to Feudal Age`,
    });
  } else if (tech === "age_castle") {
    meta.age = "castle";
    upgradeTeamFarms(state, team);
    pushEvent(state, {
      kind: "age_up",
      team,
      text: `${TEAM_LABELS[team] || team} advanced to Castle Age`,
    });
  } else if (tech === "age_imperial") {
    meta.age = "imperial";
    upgradeTeamFarms(state, team);
    pushEvent(state, {
      kind: "age_up",
      team,
      text: `${TEAM_LABELS[team] || team} advanced to Imperial Age`,
    });
  } else if (tech === "bs_attack") {
    meta.atkBonus = Math.min(2, meta.atkBonus + 1);
  } else if (tech === "bs_armor") {
    meta.armorBonus = Math.min(2, meta.armorBonus + 1);
  } else if (tech === "uni_masonry") {
    meta.masonry = true;
    meta.wallHpBonus = 100;
    for (const e of state.entities) {
      if (e.team === team && (e.kind === "wall" || e.kind === "gate" || e.kind === "watch_tower")) {
        e.maxHp += 100;
        e.hp += 100;
      }
    }
  } else if (tech === "uni_ballistics") {
    meta.ballistics = true;
    meta.rangeBonus = 1.5;
  }
}

function findEnemyInRange(
  state: GameState,
  e: Entity,
  range: number
): Entity | null {
  if (!attacksAllowed(state) || e.kind === "explorer" || e.kind === "worker") return null;
  let best: Entity | null = null;
  let bestD = range + 0.01;
  for (const t of state.entities) {
    if (t.hp <= 0 || t.kind === "chrono" || t.kind === "explorer") continue;
    // Enemy teams, or wild predators (team null) that threaten villagers
    if (t.team) {
      if (!hostileTarget(state, e.team, t)) continue;
    } else if (!isPredator(t.kind)) {
      continue;
    }
    const d = dist(e, t);
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}

function nearestWounded(state: GameState, e: Entity, reach: number): Entity | null {
  if (!e.team) return null;
  let best: Entity | null = null;
  let bestD = reach;
  for (const t of state.entities) {
    if (t.id === e.id || t.hp <= 0 || !t.team) continue;
    if (!isControllableUnit(t.kind)) continue;
    if (t.hp >= t.maxHp - 0.4) continue;
    if (!areAllied(state, e.team, t.team)) continue;
    const d = dist(e, t);
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}

function doAttack(state: GameState, e: Entity, t: Entity, dt: number) {
  if (e.kind === "healer" || e.kind === "explorer" || e.kind === "worker") return;
  if (t.kind === "explorer") return;
  if (!attacksAllowed(state)) return;
  let range = unitRange(state, e);
  if (
    range > 1.8 &&
    terrainHeight(e.x, e.z, state.mapSeed, state.mapBiome) -
      terrainHeight(t.x, t.z, state.mapSeed, state.mapBiome) >
      0.85
  ) {
    range += 0.8;
  }
  e.attackCd = (e.attackCd || 0) - dt;
  if (dist(e, t) > range) {
    moveToward(state, e, t.x, t.z, unitSpeed(state, e), dt);
    return;
  }
  e.facing = Math.atan2(t.x - e.x, t.z - e.z);
  if ((e.attackCd || 0) > 0) return;
  const st = UNIT_STATS[e.kind as TrainUnit];
  e.attackCd = e.kind === "trebuchet" ? 1.4 : e.kind === "mangonel" || e.kind === "scorpion" ? 0.9 : 0.55;
  if ((e.kind === "archer" || e.kind === "skirmisher" || e.kind === "crossbowman") && e.team) {
    e.attackCd *= teamCiv(state, e.team).archerCd || 1;
  }
  let primary = unitDmg(state, e, t);
  const higher =
    terrainHeight(e.x, e.z, state.mapSeed, state.mapBiome) -
    terrainHeight(t.x, t.z, state.mapSeed, state.mapBiome);
  if (higher > 0.9 && Math.random() < 0.25) primary *= 3;
  t.hp -= primary;
  if (t.hp < 0) t.hp = 0;

  // Mangonel splash on clustered foes
  if (st?.splash) {
    for (const o of state.entities) {
      if (o.id === t.id || o.hp <= 0 || o.kind === "explorer") continue;
      if (!o.team || !isHostile(state, e.team, o.team)) continue;
      if (dist(o, t) <= st.splash) {
        o.hp -= Math.max(1, Math.floor(primary * 0.45));
        if (o.hp < 0) o.hp = 0;
      }
    }
  }
  // Scorpion pierce bolt through a line
  if (st?.pierce) {
    const dx = Math.sin(e.facing || 0);
    const dz = Math.cos(e.facing || 0);
    for (const o of state.entities) {
      if (o.id === t.id || o.hp <= 0 || o.kind === "explorer") continue;
      if (!o.team || o.team === e.team) continue;
      const vx = o.x - e.x;
      const vz = o.z - e.z;
      const along = vx * dx + vz * dz;
      if (along < 0.5 || along > range + 1) continue;
      const px = e.x + dx * along;
      const pz = e.z + dz * along;
      if (Math.hypot(o.x - px, o.z - pz) < 0.85) {
        o.hp -= Math.max(1, Math.floor(primary * 0.7));
        if (o.hp < 0) o.hp = 0;
      }
    }
  }

  if (t.hp <= 0) {
    if (e.order?.type === "attack") e.order = { type: "idle" };
  }
}


function updateTradeRoutes(state: GameState, dt: number) {
  const markets = state.entities.filter(
    (e) => e.kind === "market" && e.hp > 0 && !e.constructing && e.team
  );
  // Ensure one cart per team that has a market + allied market
  for (const team of teamsInPlay(state)) {
    const mine = markets.filter((m) => m.team === team);
    if (!mine.length) continue;
    const allyMarkets = markets.filter(
      (m) => m.team && m.team !== team && areAllied(state, team, m.team)
    );
    // Also allow trade to own second market
    const targets = allyMarkets.length
      ? allyMarkets
      : mine.length > 1
        ? mine.slice(1)
        : [];
    if (!targets.length) continue;
    let cart = state.entities.find(
      (e) => e.kind === "trade_cart" && e.team === team && e.hp > 0
    );
    const home = mine[0];
    const dest = targets[0];
    if (!cart) {
      cart = {
        id: nid("trd"),
        kind: "trade_cart",
        team,
        x: home.x,
        z: home.z,
        hp: 60,
        maxHp: 60,
        order: { type: "move", tx: dest.x, tz: dest.z },
        tradeTargetId: dest.id,
        facing: 0,
      };
      state.entities.push(cart);
    }
  }
  for (const cart of state.entities) {
    if (cart.kind !== "trade_cart" || cart.hp <= 0 || !cart.team) continue;
    const dest = cart.tradeTargetId
      ? state.entities.find((e) => e.id === cart.tradeTargetId)
      : null;
    if (!dest || dest.hp <= 0) {
      // Retarget or despawn
      const markets = state.entities.filter(
        (e) =>
          e.kind === "market" &&
          e.hp > 0 &&
          e.team &&
          (e.team === cart.team || areAllied(state, cart.team, e.team))
      );
      const home = markets.find((m) => m.team === cart.team);
      const other = markets.find((m) => m.id !== home?.id);
      if (!home || !other) continue;
      cart.tradeTargetId = other.id;
      cart.order = { type: "move", tx: other.x, tz: other.z };
      continue;
    }
    if (moveToward(state, cart, dest.x, dest.z, 2.8, dt)) {
      state.eco[cart.team].gold = (state.eco[cart.team].gold || 0) + TRADE_GOLD_PER_TRIP;
      state.eco[cart.team].food = (state.eco[cart.team].food || 0) + 5;
      state.eco[cart.team].wood = (state.eco[cart.team].wood || 0) + 5;
      // Bounce back to a home market
      const home =
        state.entities.find(
          (e) =>
            e.kind === "market" &&
            e.team === cart.team &&
            e.hp > 0 &&
            e.id !== dest.id
        ) ||
        state.entities.find(
          (e) => e.kind === "market" && e.team === cart.team && e.hp > 0
        );
      if (home) {
        cart.tradeTargetId = home.id;
        cart.order = { type: "move", tx: home.x, tz: home.z };
      }
    }
  }
}

function ensureExplorers(state: GameState) {
  for (const team of teamsInPlay(state)) {
    if (state.entities.some((e) => e.kind === "explorer" && e.team === team && e.hp > 0)) continue;
    const th = state.entities.find((e) => e.kind === "town_hall" && e.team === team && e.hp > 0);
    if (!th) continue;
    const face = Math.atan2(-th.z, -th.x);
    state.entities.push({
      id: nid("ex"),
      kind: "explorer",
      team,
      x: clampMap(th.x + Math.cos(face) * 8 + Math.cos(face + Math.PI / 2) * 3.2),
      z: clampMap(th.z + Math.sin(face) * 8 + Math.sin(face + Math.PI / 2) * 3.2),
      hp: 80,
      maxHp: 80,
      order: { type: "idle" },
      facing: 0,
    });
  }
}

/** A player's explorer must not stand on the Town Hall. That spot is for clicking the hall. */
function clearExplorerOffHall(state: GameState) {
  for (const e of state.entities) {
    if (e.kind !== "explorer" || e.hp <= 0 || !e.team) continue;
    if (state.computerTeams?.[e.team]) continue;
    if (e.order?.type === "move" && e.order.manual) continue;
    const th = state.entities.find((t) => t.kind === "town_hall" && t.team === e.team && t.hp > 0);
    if (!th || dist(e, th) > 5.6) continue;
    const face = Math.atan2(-th.z, -th.x);
    e.x = clampMap(th.x + Math.cos(face) * 8 + Math.cos(face + Math.PI / 2) * 3.2);
    e.z = clampMap(th.z + Math.sin(face) * 8 + Math.sin(face + Math.PI / 2) * 3.2);
    e.order = { type: "idle" };
    e.path = undefined;
    e.waypoints = undefined;
  }
}

/** Computer explorers walk the land and open the fog. A player's explorer stays until they right-click. */
function steerExplorers(state: GameState) {
  const teams = teamsInPlay(state);
  for (const e of state.entities) {
    if (e.kind !== "explorer" || e.hp <= 0 || !e.team) continue;
    if (!state.computerTeams?.[e.team]) {
      if (e.order?.type === "move" && !e.order.manual) {
        e.order = { type: "idle" };
        e.path = undefined;
        e.waypoints = undefined;
      }
      continue;
    }
    if (e.order && e.order.type !== "idle") continue;
    const th = state.entities.find((t) => t.kind === "town_hall" && t.team === e.team && t.hp > 0);
    const ox = th?.x ?? e.x;
    const oz = th?.z ?? e.z;
    const towardCenter = Math.atan2(-oz, -ox);
    const sweep = ((state.tick + teams.indexOf(e.team) * 90) % 720) * (Math.PI / 360);
    for (let step = 0; step < 10; step++) {
      const ang = towardCenter + sweep + step * 0.55;
      const rad = 30 + step * 16;
      const x = clampMap(ox + Math.cos(ang) * rad);
      const z = clampMap(oz + Math.sin(ang) * rad);
      if (Math.hypot(x - ox, z - oz) < 20) continue;
      if (isWaterAt(state, x, z)) continue;
      if (step < 7 && isExplored(state, e.team, x, z)) continue;
      e.order = { type: "move", tx: x, tz: z };
      e.path = undefined;
      break;
    }
    if (!e.order || e.order.type === "idle") {
      const ang = towardCenter + sweep;
      e.order = {
        type: "move",
        tx: clampMap(ox + Math.cos(ang) * 48),
        tz: clampMap(oz + Math.sin(ang) * 48),
      };
      e.path = undefined;
    }
  }
}

function notePeace(state: GameState) {
  if (!attacksAllowed(state) || state.peaceEnded) return;
  state.peaceEnded = true;
  pushEvent(state, { kind: "info", text: "The peace is over. Warriors may attack." });
}

export function advanceTick(state: GameState): GameState {
  ensureMeta(state);
  if (state.winner || !state.started) return state;
  parkResources(state);
  state.tick += 1;
  pathBudget = 3;
  ensureExplorers(state);
  clearExplorerOffHall(state);
  steerExplorers(state);
  notePeace(state);
  const dt = TICK_MS / 1000;
  if (!state.chronoCharges) state.chronoCharges = Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, null])) as Record<Team, ChronoCharge | null>;
  if (!state.events) state.events = [];
  if (!state.explored) state.explored = Object.fromEntries(EMPIRE_TEAMS.map((t) => [t, {}])) as Record<Team, Record<string, 1>>;

  // Dynamic gates: friendlies/allies approaching open; auto-close; locked stays shut
  for (const g of state.entities) {
    if (g.kind !== "gate" || g.hp <= 0 || g.constructing) continue;
    if ((g.gateState || "closed") === "locked") continue;
    let friendNear = false;
    for (const u of state.entities) {
      if (u.hp <= 0 || !u.team || u.aboardShipId) continue;
      if (!isControllableUnit(u.kind) || isNavalUnit(u.kind)) continue;
      if (!areAllied(state, g.team, u.team)) continue;
      if (dist(g, u) < 2.6) {
        friendNear = true;
        break;
      }
    }
    if (friendNear) {
      g.gateState = "open";
      g.gateCloseIn = 2.2;
    } else if (g.gateState === "open") {
      g.gateCloseIn = (g.gateCloseIn || 0) - dt;
      if ((g.gateCloseIn || 0) <= 0) g.gateState = "closed";
    }
  }

  // Cargo rides with ship; ships with cargo fire light cannons at hostiles
  for (const ship of state.entities) {
    if (ship.kind !== "transport" || ship.hp <= 0) continue;
    if (!ship.cargo) ship.cargo = [];
    ship.cargo = ship.cargo.filter((id) => {
      const u = state.entities.find((e) => e.id === id);
      return !!u && u.hp > 0;
    });
    for (const id of ship.cargo) {
      const u = byId(state, id);
      if (!u) continue;
      u.aboardShipId = ship.id;
      u.x = ship.x;
      u.z = ship.z;
      u.order = { type: "idle" };
      u.path = undefined;
    }
  }

  // Trade carts between allied markets
  updateTradeRoutes(state, dt);

  // Farms regenerate forever; capacity upgrades with age
  for (const e of state.entities) {
    if (e.kind !== "farm" || e.hp <= 0 || e.constructing || !e.team) continue;
    const cap = farmStockCap(state, e.team);
    if ((e.stock ?? 0) > cap) e.stock = cap;
    if (state.tick % FARM_REGEN_EVERY === 0) {
      e.stock = Math.min(cap, (e.stock ?? 0) + FARM_REGEN);
    }
  }

  // Train / research
  for (const e of state.entities) {
    if (e.hp <= 0 || e.constructing || !e.team) continue;
    if (e.research) {
      e.research.left -= 1;
      if (e.research.left <= 0) {
        const tech = e.research.tech;
        e.research = null;
        finishResearch(state, e.team, tech);
      }
    }
    if (e.train) {
      e.train.left -= 1;
      if (e.train.left <= 0) {
        const unit = e.train.unit;
        e.train = null;
        recountPop(state);
        if (state.meta[e.team].pop >= state.meta[e.team].popCap) {
          refund(state.eco[e.team], costOf(unit));
          continue;
        }
        const naval = !!UNIT_STATS[unit]?.naval;
        let sx = e.x;
        let sz = e.z;
        if (naval) {
          const spot = waterSpawnNearDock(state, e);
          if (!spot) {
            refund(state.eco[e.team], costOf(unit));
            continue;
          }
          sx = spot.x;
          sz = spot.z;
        } else {
          // Claim a free N/E/S/W (then diagonal) pad around the building
          let placed = false;
          for (let ring = 0; ring < 3 && !placed; ring++) {
            for (let s = 0; s < STAND_DIRS.length; s++) {
              const r = 2.2 + ring * 0.55;
              const [dx, dz] = STAND_DIRS[s];
              const px = clampMap(e.x + dx * r);
              const pz = clampMap(e.z + dz * r);
              if (isWaterAt(state, px, pz)) continue;
              if (wallBlocks(state, px, pz, e.team, false)) continue;
              const busy = state.entities.some(
                (o) =>
                  o.hp > 0 &&
                  isControllableUnit(o.kind) &&
                  !isNavalUnit(o.kind) &&
                  dist(o, { x: px, z: pz }) < 0.55
              );
              if (busy) continue;
              sx = px;
              sz = pz;
              placed = true;
              break;
            }
          }
          if (!placed) {
            sx = clampMap(e.x + 2.5);
            sz = clampMap(e.z + 2.5);
          }
        }
        const st = UNIT_STATS[unit];
        state.entities.push({
          id: nid(unit.slice(0, 2)),
          kind: unit,
          team: e.team,
          x: sx,
          z: sz,
          hp: st.hp + (unit === "worker" ? teamCiv(state, e.team).villagerHp || 0 : 0),
          maxHp: st.hp + (unit === "worker" ? teamCiv(state, e.team).villagerHp || 0 : 0),
          carry:
            unit === "worker" || unit === "fishing_boat"
              ? { type: "food", amount: 0 }
              : undefined,
          order:
            e.rallyX != null && e.rallyZ != null
              ? { type: "move", tx: e.rallyX, tz: e.rallyZ }
              : { type: "idle" },
          facing: Math.random() * Math.PI * 2,
        });
      }
    }
  }

  // Towers / Keep auto-attack
  for (const e of state.entities) {
    if (e.hp <= 0 || e.constructing || !e.team) continue;
    if (e.kind !== "watch_tower" && e.kind !== "keep") continue;
    const range = e.kind === "keep" ? 9 : 7.5;
    const bonus = state.meta[e.team].rangeBonus;
    const target = findEnemyInRange(state, e, range + bonus);
    if (!target) continue;
    e.attackCd = (e.attackCd || 0) - dt;
    if ((e.attackCd || 0) > 0) continue;
    e.attackCd = 0.7;
    const dmg = (e.kind === "keep" ? 16 : 10) + state.meta[e.team].atkBonus;
    target.hp -= dmg;
    if (target.hp < 0) target.hp = 0;
  }


  // Recruit neutral map geologists who join the nearest exploring team
  for (const g of state.entities) {
    if (g.kind !== "geologist" || g.team || g.hp <= 0) continue;
    for (const u of state.entities) {
      if (u.hp <= 0 || !u.team) continue;
      if (u.kind !== "worker" && u.kind !== "geologist" && u.kind !== "militia") continue;
      if (dist(u, g) > 2.6) continue;
      g.team = u.team;
      g.order = { type: "idle" };
      pushEvent(state, {
        kind: "info",
        team: u.team,
        text: `${TEAM_LABELS[u.team]} hired a field geologist`,
      });
      break;
    }
  }

  // Geologists / workers drill oil & gas seeps into wells
  for (const seep of state.entities) {
    if ((seep.kind !== "oil_seep" && seep.kind !== "gas_seep") || seep.hp <= 0) continue;
    const fuel = seep.kind === "gas_seep" ? "gas" : "oil";
    for (const u of state.entities) {
      if (u.hp <= 0 || !u.team) continue;
      if (u.kind !== "worker" && u.kind !== "geologist") continue;
      if (dist(u, seep) > 2.2) continue;
      // Convert seep → well owned by team
      seep.kind = "oil_well";
      seep.team = u.team;
      seep.maxHp = 220;
      seep.hp = 220;
      seep.stock = seep.stock || (fuel === "gas" ? 600 : 800);
      pushEvent(state, {
        kind: "info",
        team: u.team,
        text: `${TEAM_LABELS[u.team]} struck ${fuel} — well online`,
      });
      break;
    }
  }
  // Producing wells drip gold
  if (state.tick % 8 === 0) {
    for (const well of state.entities) {
      if (well.kind !== "oil_well" || !well.team || well.hp <= 0) continue;
      if ((well.stock || 0) <= 0) continue;
      well.stock = (well.stock || 0) - 1;
      state.eco[well.team].gold = (state.eco[well.team].gold || 0) + 3;
    }
  }

  // Unit AI
  for (const e of state.entities) {
    if (e.hp <= 0) continue;
    if (e.insideId) {
      const home = byId(state, e.insideId);
      if (!home || home.hp <= 0) {
        e.insideId = undefined;
      } else {
        e.x = home.x;
        e.z = home.z;
        if (e.hp < e.maxHp && state.tick % 5 === 0) e.hp = Math.min(e.maxHp, e.hp + 3);
        continue;
      }
    }
    if (!isControllableUnit(e.kind)) continue;
    if (isLandUnit(e.kind) && !e.aboardShipId) escapeIfEmbedded(state, e);
    const order = e.order;
    if (
      (e.kind === "worker" || e.kind === "explorer") &&
      order &&
      (order.type === "attack" || order.type === "attack_move")
    ) {
      e.order = { type: "idle" };
      e.path = undefined;
      continue;
    }
    if (!attacksAllowed(state) && order && (order.type === "attack" || order.type === "attack_move")) {
      e.order =
        order.type === "attack_move" ? { type: "move", tx: order.tx, tz: order.tz } : { type: "idle" };
      e.path = undefined;
      continue;
    }
    if (!order || order.type === "idle") {
      if (e.kind === "healer" && e.team) {
        const hurt = nearestWounded(state, e, 16);
        if (hurt) {
          e.order = { type: "heal", targetId: hurt.id };
          e.path = undefined;
          continue;
        }
      }
      // A full load is a drop-off, not a stroll. Humans stand until ordered.
      // Only computer seats wander, and only after they have actually been idle.
      if (e.team && (e.carry?.amount || 0) > 0) {
        e.order = { type: "return", resourceId: "" };
        e.path = undefined;
        continue;
      }
      if (
        attacksAllowed(state) &&
        isWarrior(e.kind) &&
        e.stance === "stand"
      ) {
        const foe = findEnemyInRange(state, e, unitRange(state, e));
        if (foe) doAttack(state, e, foe, dt);
        continue;
      }
      if (
        attacksAllowed(state) &&
        isWarrior(e.kind) &&
        e.kind !== "scout_cav" &&
        e.stance !== "stand"
      ) {
        const foe = findEnemyInRange(state, e, visionRadius(e));
        if (foe) {
          e.order = { type: "attack", targetId: foe.id };
          e.path = undefined;
          continue;
        }
      }
      const cpu = !!(e.team && state.computerTeams?.[e.team]);
      if (
        cpu &&
        e.kind !== "worker" &&
        e.kind !== "explorer" &&
        isLandUnit(e.kind) &&
        !e.aboardShipId &&
        (e.browseCd || 0) <= 0
      ) {
        const th = state.entities.find(
          (b) => b.kind === "town_hall" && b.team === e.team && b.hp > 0
        );
        const cx = th ? th.x : e.x;
        const cz = th ? th.z : e.z;
        const ang = Math.random() * Math.PI * 2;
        const rad = 3 + Math.random() * 7;
        const bx = clampMap(cx + Math.cos(ang) * rad);
        const bz = clampMap(cz + Math.sin(ang) * rad);
        if (!isWaterAt(state, bx, bz) && !wallBlocks(state, bx, bz, e.team, false)) {
          e.order = { type: "browse", tx: bx, tz: bz };
          e.browseCd = 4 + Math.random() * 6;
          e.path = undefined;
        } else {
          e.browseCd = 2;
        }
      } else {
        e.browseCd = Math.max(0, (e.browseCd || 0) - dt);
      }
      continue;
    }

    if (order.type === "browse") {
      e.browseCd = Math.max(0, (e.browseCd || 0) - dt);
      if (moveToward(state, e, order.tx, order.tz, unitSpeed(state, e, order.tx, order.tz) * 0.7, dt)) {
        e.order = { type: "idle" };
        e.browseCd = 3 + Math.random() * 4;
      }
      continue;
    }

    if (order.type === "move") {
      if (moveToward(state, e, order.tx, order.tz, unitSpeed(state, e, order.tx, order.tz), dt)) {
        const next = e.waypoints?.shift();
        if (next) {
          e.order = { type: "move", tx: next.x, tz: next.z, manual: order.manual };
          e.path = undefined;
        } else {
          e.order = { type: "idle" };
          e.waypoints = undefined;
          e.browseCd = 8;
        }
      }
      continue;
    }

    if (order.type === "repair" && e.kind === "worker" && e.team) {
      const site = byId(state, order.targetId);
      if (!site || site.hp <= 0 || site.hp >= site.maxHp - 0.4 || site.team !== e.team) {
        e.order = { type: "idle" };
        continue;
      }
      if (dist(e, site) > 1.7) {
        moveToward(state, e, site.x, site.z, unitSpeed(state, e, site.x, site.z), dt);
      } else if (state.tick % 5 === 0) {
        const wood = state.eco[e.team].wood || 0;
        if (wood >= 1) {
          state.eco[e.team].wood = wood - 1;
          site.hp = Math.min(site.maxHp, site.hp + 14);
        } else {
          site.hp = Math.min(site.maxHp, site.hp + 4);
        }
      }
      continue;
    }

    if (order.type === "convert" && e.kind === "healer" && e.team) {
      if (!attacksAllowed(state)) {
        e.order = { type: "idle" };
        continue;
      }
      if ((e.convertCd || 0) > 0) {
        e.convertCd = (e.convertCd || 0) - dt;
        continue;
      }
      const mark = byId(state, order.targetId);
      if (!mark || mark.hp <= 0 || mark.kind === "town_hall" || mark.kind === "explorer") {
        e.order = { type: "idle" };
        continue;
      }
      if (dist(e, mark) > 6) {
        moveToward(state, e, mark.x, mark.z, unitSpeed(state, e, mark.x, mark.z), dt);
      } else {
        e.facing = Math.atan2(mark.x - e.x, mark.z - e.z);
        e.convertProg = (e.convertProg || 0) + dt;
        if ((e.convertProg || 0) >= 4) {
          mark.team = e.team;
          mark.order = { type: "idle" };
          mark.path = undefined;
          e.convertProg = 0;
          e.convertCd = 8;
          e.order = { type: "idle" };
          pushEvent(state, { kind: "info", team: e.team, text: "A priest converted a unit." });
        }
      }
      continue;
    }

    if (order.type === "attack_ground") {
      if (!attacksAllowed(state)) {
        e.order = { type: "idle" };
        continue;
      }
      const range = unitRange(state, e) || 6;
      if (dist(e, { x: order.tx, z: order.tz }) > range) {
        moveToward(state, e, order.tx, order.tz, unitSpeed(state, e, order.tx, order.tz), dt);
      } else if ((e.attackCd || 0) <= 0) {
        e.attackCd = 1.1;
        e.facing = Math.atan2(order.tx - e.x, order.tz - e.z);
        const blast = UNIT_STATS[e.kind as TrainUnit]?.splash || 2.2;
        const dmg = unitDmg(state, e, e);
        for (const o of state.entities) {
          if (o.id === e.id || o.hp <= 0 || o.kind === "explorer") continue;
          if (dist(o, { x: order.tx, z: order.tz }) > blast) continue;
          if (o.team && o.team === e.team) continue;
          if (o.team && !hostileTarget(state, e.team, o) && o.team) continue;
          o.hp -= Math.max(4, Math.floor(dmg * 0.7));
          if (o.hp < 0) o.hp = 0;
        }
      } else {
        e.attackCd = (e.attackCd || 0) - dt;
      }
      continue;
    }

    if (order.type === "patrol") {
      const dest = order.leg === 1 ? { x: order.bx, z: order.bz } : { x: order.ax, z: order.az };
      if (
        attacksAllowed(state) &&
        e.stance !== "stand" &&
        isWarrior(e.kind)
      ) {
        const foe = findEnemyInRange(state, e, unitRange(state, e) + 1.2);
        if (foe) {
          doAttack(state, e, foe, dt);
          continue;
        }
      }
      if (moveToward(state, e, dest.x, dest.z, unitSpeed(state, e, dest.x, dest.z), dt)) {
        e.order = { ...order, leg: order.leg === 1 ? 0 : 1 };
        e.path = undefined;
      }
      continue;
    }

    if (order.type === "attack_move") {
      const foe = findEnemyInRange(state, e, unitRange(state, e) + 2);
      if (foe && e.kind !== "fishing_boat" && e.kind !== "transport") {
        doAttack(state, e, foe, dt);
      } else if (moveToward(state, e, order.tx, order.tz, unitSpeed(state, e, order.tx, order.tz), dt)) {
        e.order = { type: "idle" };
      }
      continue;
    }

    if (order.type === "build" && e.kind === "worker") {
      const pad = standAround(state, e, order.tx, order.tz, "build");
      const buildReach = (e.stuck || 0) >= 8 ? BUILD_RANGE * 1.5 : BUILD_RANGE;
      const nearSite = dist(e, { x: order.tx, z: order.tz }) <= buildReach;
      if (!nearSite) {
        moveToward(state, e, pad.x, pad.z, unitSpeed(state, e, pad.x, pad.z), dt);
      } else {
        e.stuck = 0;
        e.path = undefined;
      }
      if (nearSite) {
        // The paid foundation is created by the build command. Finishing it
        // must not drop another free copy on the same tile.
        const site = state.entities.find(
          (b) =>
            b.team === e.team &&
            b.kind === order.building &&
            b.hp > 0 &&
            dist(b, { x: order.tx, z: order.tz }) < 1.6
        );
        if (!site || !site.constructing) {
          if (site && site.kind === "farm" && (site.stock ?? 0) > 0) {
            e.order = { type: "gather", resourceId: site.id };
            e.path = undefined;
          } else if (e.team && isFortKind(order.building)) {
            const next = nextUnfinishedFort(state, e.team, site || e);
            e.order = next
              ? { type: "build", building: next.kind as BuildKind, tx: next.x, tz: next.z }
              : { type: "idle" };
            e.browseCd = 8;
            e.path = undefined;
          } else {
            e.order = { type: "idle" };
            e.browseCd = 8;
          }
          continue;
        }
        const rate = isFortKind(order.building) ? 20 : 12;
        site.hp = Math.min(site.maxHp, site.hp + rate);
        if (site.hp >= site.maxHp) {
          site.constructing = false;
          if (site.kind === "wonder" && site.team) {
            if (!state.wonderSince) state.wonderSince = {};
            state.wonderSince[site.team] = Date.now();
            pushEvent(state, { kind: "info", team: site.team, text: "The wonder stands. Hold it." });
          }
          recountPop(state);
          if (site.kind === "farm") {
            e.order = { type: "gather", resourceId: site.id };
            e.path = undefined;
          } else if (e.team && isFortKind(order.building)) {
            const next = nextUnfinishedFort(state, e.team, site);
            if (next) {
              e.order = {
                type: "build",
                building: next.kind as BuildKind,
                tx: next.x,
                tz: next.z,
              };
            } else {
              e.order = { type: "idle" };
              e.browseCd = 8;
            }
          } else {
            e.order = { type: "idle" };
            e.browseCd = 8;
          }
        }
      }
      continue;
    }

    if (order.type === "capture") {
      const relic = byId(state, order.relicId);
      if (!relic || relic.kind !== "chrono") {
        e.order = { type: "idle" };
        continue;
      }
      if (dist(e, relic) > CAPTURE_RANGE) {
        moveToward(state, e, relic.x, relic.z, unitSpeed(state, e, relic.x, relic.z), dt);
      } else if (e.team) {
        captureRelic(state, e.team, relic);
        e.order = { type: "idle" };
      }
      continue;
    }

    if (order.type === "gather") {
      let node = byId(state, order.resourceId);
      const wantType =
        (node && resTypeOfNode(node.kind)) ||
        e.carry?.type ||
        null;

      // Depleted / gone → roll to next remembered source of the same type
      if (
        !node ||
        node.hp <= 0 ||
        ((node.stock ?? 0) <= 0 && node.kind !== "farm")
      ) {
        if (e.team && wantType) {
          const next = pickOwnSource(state, e.team, wantType, e, {
            allowEmptyFarm: wantType === "food",
          });
          if (next) {
            e.order = { type: "gather", resourceId: next.id };
            node = next;
          } else {
            e.order = { type: "idle" };
            continue;
          }
        } else {
          e.order = { type: "idle" };
          continue;
        }
      }

      // Empty farm: wait on pad for regen (farms never die from harvest)
      if (node.kind === "farm" && (node.stock ?? 0) <= 0) {
        const pad = standAround(state, e, node.x, node.z, "gather");
        if (dist(e, node) > GATHER_RANGE) {
          moveToward(state, e, pad.x, pad.z, unitSpeed(state, e, pad.x, pad.z), dt);
        } else {
          e.stuck = 0;
          e.path = undefined;
        }
        continue;
      }

      const canGather =
        (e.kind === "worker" &&
          (node.kind === "wood" ||
            node.kind === "food" ||
            node.kind === "gold" ||
            node.kind === "stone" ||
            node.kind === "farm" ||
            isFoodAnimal(node.kind))) ||
        (e.kind === "fishing_boat" && node.kind === "fish");
      if (!canGather) {
        e.order = { type: "idle" };
        continue;
      }
      if (e.team) rememberResource(state, e.team, node.id);

      const carryAmt = e.carry?.amount || 0;
      if (carryAmt >= CARRY_MAX) {
        e.order = { type: "return", resourceId: node.id };
        continue;
      }
      const pad = standAround(state, e, node.x, node.z, "gather");
      const gatherReach = (e.stuck || 0) >= 8 ? GATHER_RANGE * 1.55 : GATHER_RANGE;
      const inRange = dist(e, node) <= gatherReach;
      if (!inRange) {
        moveToward(state, e, pad.x, pad.z, unitSpeed(state, e, pad.x, pad.z), dt);
        if ((e.stuck || 0) >= 6 && !e.path && e.team && wantType) {
          const next = pickOwnSource(state, e.team, wantType, e);
          if (next && next.id !== node.id && dist(e, next) + 1.5 < dist(e, node)) {
            e.order = { type: "gather", resourceId: next.id };
            e.path = undefined;
            e.stuck = 0;
          }
        }
      } else {
        e.stuck = 0;
        e.path = undefined;
      }
      if (inRange) {
        let take = Math.min(2, node.stock || 0, CARRY_MAX - carryAmt);
        if (e.team && take > 0) {
          const civ = teamCiv(state, e.team);
          let mul = civ.work || 1;
          if (node.kind === "wood") mul *= civ.wood || 1;
          if (node.kind === "gold") mul *= civ.gold || 1;
          if (node.kind === "stone") mul *= civ.stone || 1;
          if (isFoodAnimal(node.kind)) mul *= civ.hunt || 1;
          if (state.difficulty === "easy" && state.computerTeams?.[e.team]) mul *= 0.65;
          if (state.difficulty === "hard" && state.computerTeams?.[e.team]) mul *= 1.3;
          take = Math.min(node.stock || 0, CARRY_MAX - carryAmt, Math.max(1, Math.round(take * mul)));
        }
        node.stock = (node.stock || 0) - take;
        const rtype = resTypeOfNode(node.kind) || "food";
        e.carry = { type: rtype, amount: carryAmt + take };
        if ((node.stock || 0) <= 0 && node.kind !== "farm") {
          node.hp = 0;
          // Immediately queue next remembered tree/mine of same type
          if (e.team) {
            const next = pickOwnSource(state, e.team, rtype, e);
            if (next && (e.carry.amount || 0) < CARRY_MAX) {
              e.order = { type: "gather", resourceId: next.id };
            } else if ((e.carry.amount || 0) > 0) {
              e.order = { type: "return", resourceId: next?.id || node.id };
            } else {
              e.order = { type: "idle" };
            }
          }
        } else if ((e.carry.amount || 0) >= CARRY_MAX) {
          e.order = { type: "return", resourceId: node.id };
        }
      }
      continue;
    }

    if (order.type === "return" && e.team) {
      const c = e.carry;
      const rtype = c?.type || "food";
      const drop = nearestDrop(state, e.team, rtype, e);
      if (!drop) {
        // No Town Center or camp left. Bank the load in place so the wood
        // is not stuck in their hands, then go back to the same resource.
        if (c && c.amount > 0 && (c.type === "wood" || c.type === "food" || c.type === "gold" || c.type === "stone")) {
          state.eco[e.team][c.type] = (state.eco[e.team][c.type] || 0) + c.amount;
          e.carry = { type: c.type, amount: 0 };
        }
        resumeGather(state, e, order.resourceId, rtype);
        continue;
      }
      const pad = standAround(state, e, drop.x, drop.z, "return");
      const dropReach = (e.stuck || 0) >= 8 ? RETURN_RANGE * 1.45 : RETURN_RANGE;
      let inRange = dist(e, drop) <= dropReach;
      if (!inRange && (e.stuck || 0) >= 6 && c && c.amount > 0) {
        state.eco[e.team][c.type] = (state.eco[e.team][c.type] || 0) + c.amount;
        e.carry = { type: c.type, amount: 0 };
        e.stuck = 0;
        e.path = undefined;
        resumeGather(state, e, order.resourceId, rtype);
        continue;
      }
      if (!inRange) {
        moveToward(state, e, pad.x, pad.z, unitSpeed(state, e, pad.x, pad.z), dt);
        inRange = dist(e, drop) <= dropReach;
      } else {
        e.stuck = 0;
        e.path = undefined;
      }
      if (inRange) {
        if (c && c.amount > 0) {
          state.eco[e.team][c.type] += c.amount;
          e.carry = { type: c.type, amount: 0 };
        }
        resumeGather(state, e, order.resourceId, rtype);
      }
      continue;
    }

    if (order.type === "attack") {
      const t = byId(state, order.targetId);
      if (!t || t.hp <= 0 || t.team === e.team) {
        e.order = { type: "idle" };
        continue;
      }
      if (e.kind === "fishing_boat" || e.kind === "transport") {
        e.order = { type: "idle" };
        continue;
      }
      doAttack(state, e, t, dt);
      continue;
    }

    if (order.type === "heal" && e.kind === "healer" && e.team) {
      const t = byId(state, order.targetId);
      if (!t || t.hp <= 0 || !t.team || !areAllied(state, e.team, t.team)) {
        const hurt = nearestWounded(state, e, 16);
        e.order = hurt ? { type: "heal", targetId: hurt.id } : { type: "idle" };
        e.path = undefined;
        continue;
      }
      const reach = 1.7;
      if (dist(e, t) > reach) {
        moveToward(state, e, t.x, t.z, unitSpeed(state, e, t.x, t.z), dt);
        continue;
      }
      e.facing = Math.atan2(t.x - e.x, t.z - e.z);
      e.stuck = 0;
      e.path = undefined;
      if (t.hp < t.maxHp) {
        t.hp = Math.min(t.maxHp, t.hp + 14 * dt);
      } else {
        const hurt = nearestWounded(state, e, 16);
        e.order = hurt ? { type: "heal", targetId: hurt.id } : { type: "idle" };
        e.path = undefined;
      }
    }
  }


  // Passive animals wander; predators hunt villagers only in the wilderness
  for (const a of state.entities) {
    if (a.hp <= 0 || a.team !== null) continue;
    if (isFoodAnimal(a.kind)) {
      if (state.tick % 12 === a.id.length % 12) {
        const ang = (a.facing || 0) + (Math.random() - 0.5) * 1.2;
        let stepped = false;
        for (let k = 0; k < 6 && !stepped; k++) {
          const a2 = ang + k * 0.9;
          const nx = clampMap(a.x + Math.sin(a2) * 0.35);
          const nz = clampMap(a.z + Math.cos(a2) * 0.35);
          if (!isWaterAt(state, nx, nz) && !wallBlocks(state, nx, nz, null, false)) {
            a.x = nx;
            a.z = nz;
            a.facing = a2;
            stepped = true;
          }
        }
      }
      continue;
    }
    if (!isPredator(a.kind)) continue;
    a.attackCd = (a.attackCd || 0) - dt;

    // Flee Town Hall plazas — never chew villagers on the wood/food line
    if (nearLivingTownHall(state, a.x, a.z)) {
      let bestTh: Entity | null = null;
      let bestD = 1e9;
      for (const th of state.entities) {
        if (th.kind !== "town_hall" || th.hp <= 0 || !th.team) continue;
        const d = dist(a, th);
        if (d < bestD) {
          bestD = d;
          bestTh = th;
        }
      }
      if (bestTh) {
        const dx = a.x - bestTh.x;
        const dz = a.z - bestTh.z;
        const len = Math.hypot(dx, dz) || 1;
        const fleeSpeed = a.kind === "gorilla" ? 2.2 : a.kind === "lion" ? 2.7 : 2.9;
        moveToward(
          state,
          a,
          a.x + (dx / len) * 4,
          a.z + (dz / len) * 4,
          fleeSpeed,
          dt
        );
      }
      continue;
    }

    let prey: Entity | null = null;
    let best = PREDATOR_AGGRO;
    if (!attacksAllowed(state)) {
      /* Animals stay wild, but they do not hunt until the peace ends. */
    } else for (const u of state.entities) {
      if (u.hp <= 0 || !u.team) continue;
      if (u.kind === "explorer") continue;
      if (u.kind !== "worker" && u.kind !== "militia" && u.kind !== "spearman") continue;
      // Eco around the TC is off-limits
      if (nearLivingTownHall(state, u.x, u.z)) continue;
      const d = dist(a, u);
      if (d < best) {
        best = d;
        prey = u;
      }
    }
    if (prey) {
      if (best > PREDATOR_RANGE) {
        moveToward(state, a, prey.x, prey.z, a.kind === "gorilla" ? 2.1 : a.kind === "lion" ? 2.6 : 2.8, dt);
      } else if ((a.attackCd || 0) <= 0) {
        a.attackCd = a.kind === "gorilla" ? 0.85 : a.kind === "lion" ? 0.7 : 0.55;
        a.facing = Math.atan2(prey.x - a.x, prey.z - a.z);
        prey.hp -= PREDATOR_DMG[a.kind] || 6;
        if (prey.hp < 0) prey.hp = 0;
      }
    } else if (state.tick % 10 === 0) {
      const ang = Math.random() * Math.PI * 2;
      const nx = clampMap(a.x + Math.sin(ang) * 0.5);
      const nz = clampMap(a.z + Math.cos(ang) * 0.5);
      if (
        !isWaterAt(state, nx, nz) &&
        !wallBlocks(state, nx, nz, null, false) &&
        !nearLivingTownHall(state, nx, nz)
      ) {
        a.x = nx;
        a.z = nz;
        a.facing = ang;
      }
    }
  }

  // Keep villagers on their own pads — no stacking on farms / mines / halls
  separateWorkers(state);
  yieldInfantryForCavalry(state);

  // Auto-capture chrono
  for (const relic of state.entities) {
    if (relic.kind !== "chrono" || relic.hp <= 0) continue;
    for (const u of state.entities) {
      if (u.hp <= 0 || !u.team || !isLandUnit(u.kind)) continue;
      if (dist(u, relic) <= CAPTURE_RANGE) {
        captureRelic(state, u.team, relic);
        break;
      }
    }
  }

  state.entities = state.entities.filter((e) => {
    if (e.hp > 0) return true;
    if (e.kind === "town_hall") return true;
    return false;
  });

  runComputerTeams(state);

  recountPop(state);
  // Vision is the expensive part of a tick. Once a second is enough for fog and scouting.
  if (state.tick % 5 === 0) refreshFog(state);

  for (const art of state.entities) {
    if (art.kind !== "artifact" || art.hp <= 0) continue;
    let holder: Team | null = art.team;
    let best = 3.2;
    for (const u of state.entities) {
      if (u.hp <= 0 || !u.team || u.insideId || !isLandUnit(u.kind)) continue;
      const d = dist(u, art);
      if (d < best) {
        best = d;
        holder = u.team;
      }
    }
    if (best < 3.2 && holder) art.team = holder;
  }
  checkVictory(state);

  const aliveTeams = teamsInPlay(state).filter((t) =>
    state.entities.some((e) => e.kind === "town_hall" && e.team === t && e.hp > 0)
  );
  if (aliveTeams.length === 0) state.winner = "draw";
  else {
    // Alliance win: all surviving teams share one alliance id
    const groups = new Set(
      aliveTeams.map((t) => state.alliances?.[t] ?? -1 - EMPIRE_TEAMS.indexOf(t))
    );
    if (groups.size === 1) state.winner = aliveTeams[0];
  }

  return state;
}

function checkVictory(state: GameState) {
  if (state.winner) return;
  const now = Date.now();
  if (state.mission === "first-hearth") {
    const houses = state.entities.filter(
      (e) => e.team === "amber" && e.kind === "house" && e.hp > 0 && !e.constructing
    ).length;
    if (houses >= 2 && (state.eco.amber.food || 0) >= 400) {
      state.winner = "amber";
      pushEvent(state, { kind: "info", team: "amber", text: "First Hearth is secure." });
    }
    return;
  }
  if (state.mission === "the-ford") {
    const ruin = state.entities.find((e) => e.kind === "ruin" && e.hp > 0);
    if (ruin) {
      let best = 6;
      let holder: Team | null = null;
      for (const u of state.entities) {
        if (u.hp <= 0 || !u.team || !isLandUnit(u.kind)) continue;
        const d = dist(u, ruin);
        if (d < best) {
          best = d;
          holder = u.team;
        }
      }
      if (holder === "amber") {
        state.winner = "amber";
        pushEvent(state, { kind: "info", team: "amber", text: "The ford ruin is yours." });
      }
    }
    return;
  }
  for (const team of teamsInPlay(state)) {
    const since = state.wonderSince?.[team];
    const wonder = state.entities.find(
      (e) => e.kind === "wonder" && e.team === team && e.hp > 0 && !e.constructing
    );
    if (wonder && since && now - since >= 3 * 60 * 1000) {
      state.winner = team;
      pushEvent(state, { kind: "info", team, text: "The wonder has stood. The age is yours." });
      return;
    }
    if (!wonder && state.wonderSince) delete state.wonderSince[team];
  }
  const arts = state.entities.filter((e) => e.kind === "artifact" && e.hp > 0);
  if (arts.length && arts.every((a) => a.team && a.team === arts[0].team)) {
    state.winner = arts[0].team as Team;
    pushEvent(state, { kind: "info", team: state.winner, text: "Every artifact is held." });
    return;
  }
  const ruins = state.entities.filter((e) => e.kind === "ruin" && e.hp > 0);
  if (ruins.length) {
    const owned = ruins.map((r) => {
      let team: Team | null = null;
      let best = 6;
      for (const u of state.entities) {
        if (u.hp <= 0 || !u.team || !isLandUnit(u.kind)) continue;
        const d = dist(u, r);
        if (d < best) {
          best = d;
          team = u.team;
        }
      }
      return team;
    });
    const first = owned[0];
    if (first && owned.every((t) => t === first)) {
      if (!state.ruinSince || state.ruinSince.team !== first) state.ruinSince = { team: first, at: now };
      else if (now - state.ruinSince.at >= 20 * 1000) {
        state.winner = first;
        pushEvent(state, { kind: "info", team: first, text: "All ruins are held." });
        return;
      }
    } else state.ruinSince = null;
  }
  if (state.winRule === "score") {
    let best: Team | null = null;
    let bestScore = 0;
    for (const team of teamsInPlay(state)) {
      const eco = state.eco[team];
      const stock = (eco.food || 0) + (eco.wood || 0) + (eco.gold || 0) + (eco.stone || 0);
      const buildings = state.entities.filter((e) => e.team === team && isBuildingKind(e.kind) && e.hp > 0).length;
      const score = Math.floor(stock / 10) + buildings * 25 + (state.meta[team]?.pop || 0) * 8;
      if (score > bestScore) {
        bestScore = score;
        best = team;
      }
      if (score >= (state.scoreTarget || 8000) && best) {
        state.winner = best;
        pushEvent(state, { kind: "info", team: best, text: "The score is decided." });
        return;
      }
    }
  }
}

/** Lightweight computer seat brain — gather, build, train, raid. */
function runComputerTeams(state: GameState) {
  if (!state.computerTeams) return;
  const stride = state.difficulty === "easy" ? 12 : state.difficulty === "hard" ? 4 : 5;
  if (state.tick % stride !== 0) return;
  for (const team of teamsInPlay(state)) {
    if (!state.computerTeams[team]) continue;
    if (!state.entities.some((e) => e.kind === "town_hall" && e.team === team && e.hp > 0))
      continue;
    const eco = state.eco[team];
    const meta = state.meta[team];
    const workers = state.entities.filter(
      (e) => e.team === team && e.kind === "worker" && e.hp > 0
    );
    const idle = workers.filter((e) => !e.order || e.order.type === "idle");
    // Assign idle workers to gather
    for (const w of idle) {
      const prefer: Array<ResType> = ["food", "wood", "gold", "stone"];
      let assigned = false;
      for (const r of prefer) {
        const node = pickOwnSource(state, team, r, w, { allowEmptyFarm: r === "food" });
        if (node) {
          applyCommand(state, team, {
            type: "gather",
            unitIds: [w.id],
            resourceId: node.id,
          });
          assigned = true;
          break;
        }
      }
      if (!assigned) w.order = { type: "idle" };
    }
    // Train workers if under pop
    const th = state.entities.find(
      (e) => e.kind === "town_hall" && e.team === team && e.hp > 0 && !e.constructing
    );
    if (th && !th.train && workers.length < 10 && (eco.food || 0) >= 50 && meta.pop < meta.popCap) {
      applyCommand(state, team, { type: "train", buildingId: th.id, unit: "worker" });
    }
    // Build house if near pop cap
    if (meta.pop >= meta.popCap - 1 && (eco.wood || 0) >= 25) {
      const builder = idle[0] || workers[0];
      if (builder && th) {
        const ang = Math.random() * Math.PI * 2;
        applyCommand(state, team, {
          type: "build",
          unitId: builder.id,
          building: "house",
          x: clampMap(th.x + Math.cos(ang) * 5),
          z: clampMap(th.z + Math.sin(ang) * 5),
        });
      }
    }
    // No army until the peace ends. Villagers stay home and gather.
    if (!attacksAllowed(state)) continue;
    // Barracks + militia when wealthy
    const hasBarracks = state.entities.some(
      (e) => e.kind === "barracks" && e.team === team && e.hp > 0 && !e.constructing
    );
    if (!hasBarracks && (eco.wood || 0) >= 150 && workers.length >= 4) {
      const builder = workers.find((w) => w.order?.type === "gather") || workers[0];
      if (builder && th) {
        const ang = Math.random() * Math.PI * 2;
        applyCommand(state, team, {
          type: "build",
          unitId: builder.id,
          building: "barracks",
          x: clampMap(th.x + Math.cos(ang) * 7),
          z: clampMap(th.z + Math.sin(ang) * 7),
        });
      }
    }
    const barracks = state.entities.find(
      (e) => e.kind === "barracks" && e.team === team && e.hp > 0 && !e.constructing && !e.train
    );
    if (barracks && (eco.food || 0) >= 60 && meta.pop < meta.popCap) {
      applyCommand(state, team, {
        type: "train",
        buildingId: barracks.id,
        unit: "militia",
      });
    }
    // Raid with idle warriors toward the nearest enemy town, only after the peace.
    if (attacksAllowed(state) && state.tick % 80 === 0) {
      const army = state.entities.filter(
        (e) =>
          e.team === team &&
          e.hp > 0 &&
          (e.kind === "militia" ||
            e.kind === "spearman" ||
            e.kind === "archer" ||
            e.kind === "scout_cav") &&
          (!e.order || e.order.type === "idle")
      );
      const need = state.difficulty === "easy" ? 8 : state.difficulty === "hard" ? 2 : 4;
      if (army.length >= need) {
        let foe: Entity | null = null;
        let best = 1e9;
        for (const e of state.entities) {
          if (e.kind !== "town_hall" || !e.team || e.team === team || e.hp <= 0) continue;
          if (areAllied(state, team, e.team)) continue;
          const d = dist(th || army[0], e);
          if (d < best) {
            best = d;
            foe = e;
          }
        }
        if (foe) {
          applyCommand(state, team, {
            type: "attack_move",
            unitIds: army.map((a) => a.id),
            x: foe.x,
            z: foe.z,
          });
        }
      }
    }
  }
}

export function claimComputerSeat(state: GameState, team: Team): boolean {
  if (!isTeam(team)) return false;
  if (!state.computerTeams?.[team]) return false;
  if (!teamsInPlay(state).includes(team)) return false;
  const next = { ...(state.computerTeams || {}) };
  delete next[team];
  state.computerTeams = next;
  pushEvent(state, {
    kind: "info",
    team,
    text: `${TEAM_LABELS[team]} city claimed by a human commander`,
  });
  return true;
}

export function markComputerSeat(state: GameState, team: Team): void {
  if (!state.computerTeams) state.computerTeams = {};
  state.computerTeams[team] = true;
}


export function advanceToNow(
  state: GameState,
  lastTickAt: number,
  now = Date.now()
): { state: GameState; lastTickAt: number } {
  ensureMeta(state);
  if (!state.started || state.winner) return { state, lastTickAt };
  // One step whenever the last save is even slightly old. Waiting for a full
  // 200ms meant a 160ms poll often saved the same positions and nobody moved.
  if (now - lastTickAt < 40) return { state, lastTickAt };
  advanceTick(state);
  return { state, lastTickAt: now };
}

export type TeamSnapshot = {
  at: number;
  tick: number;
  eco: PlayerEco;
  meta: PlayerMeta;
  entities: Entity[];
};

export function snapshotTeam(state: GameState, team: Team): TeamSnapshot {
  ensureMeta(state);
  return {
    at: Date.now(),
    tick: state.tick,
    eco: { ...state.eco[team] },
    meta: { ...state.meta[team] },
    entities: state.entities
      .filter((e) => e.team === team)
      .map((e) => JSON.parse(JSON.stringify(e)) as Entity),
  };
}

export function applyTeamRewind(
  state: GameState,
  victim: Team,
  snap: TeamSnapshot,
  attacker: Team,
  vaultName: string,
  rewindSec: number
): void {
  ensureMeta(state);
  state.eco[victim] = {
    wood: snap.eco.wood ?? 0,
    food: snap.eco.food ?? 0,
    gold: snap.eco.gold ?? 0,
    stone: snap.eco.stone ?? 0,
  };
  if (snap.meta) state.meta[victim] = { ...defaultMeta(), ...snap.meta };
  const keep = state.entities.filter((e) => e.team !== victim);
  const restored = snap.entities.map((e) => JSON.parse(JSON.stringify(e)) as Entity);
  state.entities = [...keep, ...restored];
  state.chronoCharges[attacker] = null;
  pushEvent(state, {
    kind: "chrono_used",
    team: attacker,
    text: `${attacker === "amber" ? "Amber" : "Teal"} fired ${vaultName} — opponent chronoshifted ${Math.round(rewindSec / 60)} min`,
    rewindSec,
    vaultName,
  });
  pushEvent(state, {
    kind: "chrono_victim",
    team: victim,
    text: "You were chronoshifted!",
    rewindSec,
    vaultName,
  });
  recountPop(state);
  refreshFog(state);
}

export function parseCommand(raw: unknown): Command | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const type = String(o.type || "");
  if (type === "move") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 80)
      : [];
    return { type: "move", unitIds, x: Number(o.x), z: Number(o.z) };
  }
  if (type === "attack_move") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 80)
      : [];
    return { type: "attack_move", unitIds, x: Number(o.x), z: Number(o.z) };
  }
  if (type === "gather") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 80)
      : [];
    return { type: "gather", unitIds, resourceId: String(o.resourceId || "") };
  }
  if (type === "divide_work") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 120)
      : [];
    const tasks = (Array.isArray(o.tasks) ? o.tasks : [])
      .map((t) => String(t) as ResType)
      .filter((t) => DIVIDE_TASK_SET.has(t))
      .slice(0, 4);
    const peopleRaw = Number(o.people);
    const people =
      Number.isFinite(peopleRaw) && peopleRaw > 0
        ? Math.min(120, Math.floor(peopleRaw))
        : undefined;
    return people
      ? { type: "divide_work", unitIds, tasks, people }
      : { type: "divide_work", unitIds, tasks };
  }
  if (type === "attack") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 80)
      : [];
    return { type: "attack", unitIds, targetId: String(o.targetId || "") };
  }
  if (type === "heal") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 80)
      : [];
    return { type: "heal", unitIds, targetId: String(o.targetId || "") };
  }
  if (type === "build") {
    const building = String(o.building || "");
    if (!BUILD_KINDS_SET.has(building)) return null;
    const x1 = Number(o.x1);
    const z1 = Number(o.z1);
    const line =
      Number.isFinite(x1) && Number.isFinite(z1)
        ? { x1, z1 }
        : {};
    const rawPath = Array.isArray(o.path) ? o.path : null;
    const path =
      rawPath && rawPath.length >= 2
        ? rawPath
            .slice(0, 24)
            .map((p: unknown) => {
              const pt = p as { x?: unknown; z?: unknown };
              return { x: Number(pt?.x), z: Number(pt?.z) };
            })
            .filter(
              (p: FortPoint) => Number.isFinite(p.x) && Number.isFinite(p.z)
            )
        : undefined;
    const rawGates = Array.isArray(o.gates) ? o.gates : null;
    const gates =
      rawGates && rawGates.length
        ? rawGates
            .slice(0, 32)
            .map((p: unknown) => {
              const pt = p as { x?: unknown; z?: unknown };
              return { x: Number(pt?.x), z: Number(pt?.z) };
            })
            .filter(
              (p: FortPoint) => Number.isFinite(p.x) && Number.isFinite(p.z)
            )
        : undefined;
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).filter(Boolean).slice(0, 40)
      : [];
    return {
      type: "build",
      unitId: String(o.unitId || unitIds[0] || ""),
      ...(unitIds.length ? { unitIds } : {}),
      building: building as BuildKind,
      x: Number(o.x),
      z: Number(o.z),
      ...line,
      ...(path && path.length >= 2 ? { path } : {}),
      ...(gates && gates.length ? { gates } : {}),
    };
  }
  if (type === "train") {
    const unit = String(o.unit || "");
    if (!TRAIN_SET.has(unit)) return null;
    return { type: "train", buildingId: String(o.buildingId || ""), unit: unit as TrainUnit };
  }
  if (type === "research") {
    const tech = String(o.tech || "");
    if (!TECH_SET.has(tech)) return null;
    return {
      type: "research",
      buildingId: String(o.buildingId || ""),
      tech: tech as TechId,
    };
  }
  if (type === "stop") {
    const unitIds = Array.isArray(o.unitIds) ? o.unitIds.map(String).slice(0, 80) : [];
    return { type: "stop", unitIds };
  }
  if (type === "waypoint" || type === "patrol") {
    const unitIds = Array.isArray(o.unitIds) ? o.unitIds.map(String).slice(0, 80) : [];
    return { type, unitIds, x: Number(o.x), z: Number(o.z) };
  }
  if (type === "stance") {
    const stance = String(o.stance || "");
    if (stance !== "aggressive" && stance !== "stand") return null;
    const unitIds = Array.isArray(o.unitIds) ? o.unitIds.map(String).slice(0, 80) : [];
    return { type: "stance", unitIds, stance };
  }
  if (type === "repair" || type === "convert") {
    const unitIds = Array.isArray(o.unitIds) ? o.unitIds.map(String).slice(0, 80) : [];
    return { type, unitIds, targetId: String(o.targetId || "") };
  }
  if (type === "rally") {
    return { type: "rally", buildingId: String(o.buildingId || ""), x: Number(o.x), z: Number(o.z) };
  }
  if (type === "tribute") {
    const to = String(o.to || "");
    const good = String(o.good || "") as ResType;
    if (!isTeam(to)) return null;
    return { type: "tribute", to, good, amount: Number(o.amount) };
  }
  if (type === "delete") {
    return { type: "delete", targetId: String(o.targetId || "") };
  }
  if (type === "diplomacy") {
    const other = String(o.other || "");
    const stance = String(o.stance || "");
    if (!isTeam(other)) return null;
    if (stance !== "ally" && stance !== "neutral" && stance !== "enemy") return null;
    return { type: "diplomacy", other, stance };
  }
  if (type === "garrison") {
    const unitIds = Array.isArray(o.unitIds) ? o.unitIds.map(String).slice(0, 40) : [];
    return { type: "garrison", unitIds, buildingId: String(o.buildingId || "") };
  }
  if (type === "ungarrison") {
    return { type: "ungarrison", buildingId: String(o.buildingId || "") };
  }
  if (type === "attack_ground") {
    const unitIds = Array.isArray(o.unitIds) ? o.unitIds.map(String).slice(0, 40) : [];
    return { type: "attack_ground", unitIds, x: Number(o.x), z: Number(o.z) };
  }
  if (type === "trade_route") return { type: "trade_route" };
  if (type === "market_swap") {
    const give = String(o.give || "").toLowerCase();
    const take = String(o.take || "").toLowerCase();
    const stocks = new Set(["wood", "food", "gold", "stone"]);
    if (!stocks.has(give) || !stocks.has(take)) return null;
    return {
      type: "market_swap",
      give: give as "wood" | "food" | "gold" | "stone",
      take: take as "wood" | "food" | "gold" | "stone",
    };
  }
  if (type === "market_buy" || type === "market_sell") {
    // Legacy: { foodAmount } → buy food
    let good = String(o.good || "").toLowerCase();
    if (!MARKET_GOOD_SET.has(good) && o.foodAmount != null) good = "food";
    if (!MARKET_GOOD_SET.has(good)) return null;
    return {
      type,
      good: good as MarketGood,
      amount: Number(o.amount) || MARKET_LOT,
    };
  }
  if (type === "capture") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 80)
      : [];
    return { type: "capture", unitIds, relicId: String(o.relicId || "") };
  }
  if (type === "rewind") return { type: "rewind" };
  if (type === "board") {
    const unitIds = Array.isArray(o.unitIds)
      ? o.unitIds.map(String).slice(0, 20)
      : [];
    return { type: "board", unitIds, shipId: String(o.shipId || "") };
  }
  if (type === "unload") {
    return {
      type: "unload",
      shipId: String(o.shipId || ""),
      x: Number(o.x),
      z: Number(o.z),
    };
  }
  if (type === "toggle_gate") {
    const mode = String(o.mode || "");
    return {
      type: "toggle_gate",
      gateId: String(o.gateId || ""),
      mode:
        mode === "open" || mode === "closed" || mode === "locked"
          ? (mode as GateState)
          : undefined,
    };
  }
  if (type === "destroy_fort") {
    return {
      type: "destroy_fort",
      targetId: String(o.targetId || ""),
      nearby: o.nearby === true,
    };
  }
  return null;
}

function villagerJob(state: GameState, e: Entity): string | undefined {
  if (e.kind !== "worker" || e.hp <= 0) return undefined;
  const o = e.order;
  if (!o || o.type === "idle" || o.type === "browse" || o.type === "move") return "idle";
  if (o.type === "gather") {
    const node = byId(state, o.resourceId);
    return (node && resTypeOfNode(node.kind)) || e.carry?.type || "food";
  }
  if (o.type === "return") return e.carry?.type || "food";
  if (o.type === "build") return "build";
  return "idle";
}

/** 0–1 job progress for villager head bar (build site HP or carry fill). */
function workerWorkProgress(state: GameState, e: Entity): number | undefined {
  if (e.kind !== "worker" || !e.order || e.hp <= 0) return undefined;
  if (e.order.type === "build") {
    const ord = e.order;
    const site = state.entities.find(
      (b) =>
        b.hp > 0 &&
        b.team === e.team &&
        b.kind === ord.building &&
        b.constructing &&
        dist(b, { x: ord.tx, z: ord.tz }) < 0.9
    );
    if (!site) return undefined;
    if (dist(e, site) > BUILD_RANGE + 0.35) return undefined;
    return Math.min(1, Math.max(0.05, site.hp / Math.max(1, site.maxHp)));
  }
  if (e.order.type === "gather" || e.order.type === "return") {
    const amt = e.carry?.amount || 0;
    if (amt <= 0) return undefined;
    return Math.min(1, amt / CARRY_MAX);
  }
  return undefined;
}

export function formatCost(cost: Cost): string {
  const parts: string[] = [];
  if (cost.wood) parts.push(`${cost.wood}W`);
  if (cost.food) parts.push(`${cost.food}F`);
  if (cost.gold) parts.push(`${cost.gold}G`);
  if (cost.stone) parts.push(`${cost.stone}S`);
  return parts.join(" ");
}

function visionEyes(state: GameState, team: Team) {
  const eyes: Array<{ x: number; z: number; r2: number }> = [];
  for (const e of state.entities) {
    if (e.hp <= 0 || e.team !== team) continue;
    const r = visionRadius(e);
    eyes.push({ x: e.x, z: e.z, r2: r * r });
  }
  return eyes;
}

function seenByEyes(
  eyes: Array<{ x: number; z: number; r2: number }>,
  x: number,
  z: number
): boolean {
  for (const eye of eyes) {
    const dx = eye.x - x;
    const dz = eye.z - z;
    if (dx * dx + dz * dz <= eye.r2) return true;
  }
  return false;
}

function orderAim(
  _state: GameState,
  e: Entity,
  byId: Map<string, Entity>
): { ox?: number; oz?: number } {
  const o = e.order;
  if (!o || o.type === "idle") return {};
  let x: number | null = null;
  let z: number | null = null;
  if (
    o.type === "move" ||
    o.type === "browse" ||
    o.type === "build" ||
    o.type === "attack_move" ||
    o.type === "attack_ground"
  ) {
    x = o.tx;
    z = o.tz;
  } else if (o.type === "patrol") {
    x = o.leg === 1 ? o.bx : o.ax;
    z = o.leg === 1 ? o.bz : o.az;
  } else {
    const id =
      o.type === "gather" || o.type === "return"
        ? o.resourceId
        : o.type === "capture"
          ? o.relicId
          : o.targetId;
    const t = byId.get(id);
    if (t) {
      x = t.x;
      z = t.z;
    }
  }
  if (x == null || z == null) return {};
  return { ox: Math.round(x * 10) / 10, oz: Math.round(z * 10) / 10 };
}

const SLIM_KINDS = new Set(["wood", "food", "gold", "stone", "fish", "beach_kid"]);

export function publicGame(
  state: GameState,
  viewerTeam: Team | null = null,
  opts?: { slim?: boolean }
) {
  ensureMeta(state);
  const eyes = viewerTeam ? visionEyes(state, viewerTeam) : [];
  const slim = !!opts?.slim;
  const source = !slim && state.props?.length ? state.entities.concat(state.props) : state.entities;
  const byIdMap = new Map(source.map((e) => [e.id, e]));
  const entities = source
    .filter((e) => (slim ? !SLIM_KINDS.has(e.kind) : true))
    .filter((e) => e.hp > 0 || e.kind === "town_hall" || e.kind === "wood")
    .filter((e) => {
      if (!viewerTeam) return true;
      if (e.kind === "wood") return true;
      if (e.team === viewerTeam) return true;
      const seen = seenByEyes(eyes, e.x, e.z);
      if (e.kind === "chrono") return seen;
      if (
        e.team &&
        e.team !== viewerTeam &&
        !isBuildingKind(e.kind) &&
        inStealthThicket(e.x, e.z, state.mapSeed, state.mapBiome)
      ) {
        const inside = eyes.some((eye) =>
          inStealthThicket(eye.x, eye.z, state.mapSeed, state.mapBiome)
        );
        if (!inside) return false;
      }
      if (e.team && e.team !== viewerTeam) return seen;
      return seen || isExplored(state, viewerTeam, e.x, e.z);
    })
    .map((e) => ({
      id: e.id,
      kind: e.kind,
      team: e.team,
      x: Math.round(e.x * 100) / 100,
      z: Math.round(e.z * 100) / 100,
      hp: Math.max(0, Math.round(e.hp)),
      maxHp: e.maxHp,
      carry: e.carry?.amount ? e.carry : undefined,
      train: e.train ? { unit: e.train.unit, left: e.train.left } : undefined,
      research: e.research
        ? { tech: e.research.tech, left: e.research.left }
        : undefined,
      stock: e.stock,
      order: e.order?.type || "idle",
      ...orderAim(state, e, byIdMap),
      stance: e.stance || undefined,
      insideId: e.insideId || undefined,
      job: villagerJob(state, e),
      facing: e.facing,
      constructing: e.constructing || undefined,
      workProgress: workerWorkProgress(state, e),
      gateState: e.kind === "gate" ? e.gateState || "closed" : undefined,
      cargo: e.kind === "transport" && e.cargo?.length ? e.cargo.length : undefined,
      aboardShipId: e.aboardShipId || undefined,
      chronoTier: e.kind === "chrono" ? e.chronoTier : undefined,
      chronoName:
        e.kind === "chrono" && e.chronoTier
          ? CHRONO_DEFS[e.chronoTier].name
          : undefined,
    }));

  const fogCells: string[] = [];
  const visionCells: string[] = [];
  if (!slim && viewerTeam && state.explored?.[viewerTeam]) {
    for (const k of Object.keys(state.explored[viewerTeam])) fogCells.push(k);
    const seen = new Set<string>();
    const step = FOG_CELL;
    for (const eye of eyes) {
      const r = Math.sqrt(eye.r2);
      for (let ox = -r; ox <= r; ox += step) {
        for (let oz = -r; oz <= r; oz += step) {
          if (ox * ox + oz * oz > eye.r2) continue;
          const k = cellKey(eye.x + ox, eye.z + oz);
          if (seen.has(k)) continue;
          seen.add(k);
          visionCells.push(k);
        }
      }
    }
  }

  const waterCells = slim ? [] : Object.keys(state.water || {});

  ensureMarketPrices(state);

  return {
    tick: state.tick,
    started: state.started,
    practice: !!state.practice,
    attacksAt: (state.startedAt || Date.now()) + PEACE_MS,
    winner: state.winner || null,
    eco: state.eco,
    meta: state.meta,
    marketPrices: state.marketPrices,
    entities,
    chronoCharge: viewerTeam ? state.chronoCharges?.[viewerTeam] || null : null,
    events: (state.events || []).slice(-20),
    mapHalf: MAP_HALF,
    water: {
      cell: WATER_CELL,
      mapHalf: MAP_HALF,
      cells: waterCells,
    },
    beaches: Object.keys(state.beaches || {}),
    activeTeams: teamsInPlay(state),
    computerTeams: state.computerTeams || {},
    alliances: state.alliances || {},
    mapId: state.mapId || null,
    mapName: state.mapName || null,
    mapBiome: state.mapBiome || null,
    mapSeed: state.mapSeed ?? null,
    difficulty: state.difficulty || "normal",
    civ: viewerTeam ? state.civs?.[viewerTeam] || state.meta?.[viewerTeam]?.civ || "assyria" : null,
    mission: state.mission || "skirmish",
    diplomacy: viewerTeam ? state.diplomacy?.[viewerTeam] || {} : {},
    islands: state.islands || [],
    fog:
      !slim && viewerTeam
        ? {
            cell: FOG_CELL,
            mapHalf: MAP_HALF,
            explored: fogCells,
            visible: visionCells,
          }
        : null,
  };
}
